// Code generated by spellbook: DO NOT EDIT.
import {
    queryOptions as createQueryOptions,
    useMutation,
    useQueryClient,
    type QueryClient,
    type QueryFunctionContext,
    type QueryKey,
    type DefaultError,
    type UseMutationOptions,
    type UseMutationResult,
    type UseQueryOptions,
    isServer,
} from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import { z } from "zod";

import {
    createCall,
    createIdempotencyKey,
    RequestError,
    ResponseError,
    ScopeContextChangedError,
    ScopeUnavailableError,
    checkScopeEligibility,
    notifySpellbookError,
    resolveScopeBinding,
    scopeEligibilityFromBinding,
    withScopeErrorNotification,
    spellbook,
    type RequestContext,
    type ScopeContextBinding,
    type ScopeEligibility,
    type ScopeOperation,
    type RequestMeta,
    type SpellbookError,
    type SpellbookErrorContext,
} from "./spellbook";

// Keep the query factory available to generated consumers even when an API
// exposes only mutations.
export { createQueryOptions };

/** TanStack query options accepted by a generated scope-aware query factory. */
export type ScopeQueryOptions<
    TQueryFnData,
    TError = DefaultError,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
> = Omit<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    "queryKey" | "queryFn" | "enabled"
> & {
    /** A boolean or a factory-time eligibility check. */
    enabled?: boolean | (() => boolean);
};

type NonUndefined<T> = T extends undefined ? never : T;

const managedOperationMetaKey: unique symbol = Symbol("spellbook-managed-operation");
const connectedQueryMetaKey: unique symbol = Symbol("spellbook-connected-query");

type ManagedOperation = {
    source: "query" | "mutation";
    operation: ScopeOperation;
};

type InternalMeta = Record<PropertyKey, unknown> & {
    [managedOperationMetaKey]?: ManagedOperation;
    [connectedQueryMetaKey]?: object;
};

function withManagedOperationMeta(
    meta: unknown,
    managedOperation?: ManagedOperation,
): InternalMeta {
    const userMeta = meta && typeof meta === "object" ? meta : {};
    return {
        ...userMeta,
        ...(managedOperation ? { [managedOperationMetaKey]: managedOperation } : {}),
    } as InternalMeta;
}

function managedOperationFromMeta(meta: unknown): ManagedOperation | undefined {
    if (!meta || typeof meta !== "object") {
        return undefined;
    }
    return (meta as InternalMeta)[managedOperationMetaKey];
}

function withConnectedQueryMeta(meta: unknown, token: object): InternalMeta {
    const userMeta = meta && typeof meta === "object" ? meta : {};
    return { ...userMeta, [connectedQueryMetaKey]: token } as InternalMeta;
}

function isSpellbookError(error: unknown): error is SpellbookError {
    return (
        error instanceof RequestError ||
        error instanceof ResponseError ||
        error instanceof ScopeUnavailableError ||
        error instanceof ScopeContextChangedError
    );
}

function notifyUnconnectedScopeGuard(
    error: unknown,
    context: SpellbookErrorContext,
    connected: boolean,
): void {
    if (
        !connected &&
        (error instanceof ScopeUnavailableError || error instanceof ScopeContextChangedError)
    ) {
        notifySpellbookError(error, context);
    }
}

const connectedQueryTokens = new WeakSet<object>();
const queryClientAttachments = new WeakMap<QueryClient, QueryClientAttachment>();

type QueryLike = {
    options: {
        meta?: unknown;
        queryFn?: (context: QueryFunctionContext) => unknown;
        retry?: UseQueryOptions["retry"];
        [key: string]: unknown;
    };
    state: { error: unknown; fetchStatus: string };
};

type TrackedQuery = {
    managedOperation: ManagedOperation;
    originalQueryFn: (context: QueryFunctionContext) => unknown;
    connectedQueryFn: (context: QueryFunctionContext) => unknown;
    originalRetry: QueryLike["options"]["retry"];
    connectedRetry: NonNullable<UseQueryOptions["retry"]>;
    managedExecution: boolean;
};

type MutationLike = {
    options: { meta?: unknown };
    state: { error: unknown; status: string; variables?: unknown };
};

type MutationNotificationState = { ownerToken?: object };

// Per-variables ownership keeps attachment changes from transferring an
// already-running mutation between direct and cache-based error reporting.
const mutationNotificationStates = new WeakMap<object, MutationNotificationState>();

function startMutationNotificationState(variables: unknown, ownerToken?: object): void {
    if (variables && typeof variables === "object") {
        mutationNotificationStates.set(variables, { ownerToken });
    }
}

function directMutationNotificationState(
    variables: unknown,
): MutationNotificationState | undefined {
    return variables && typeof variables === "object"
        ? mutationNotificationStates.get(variables)
        : undefined;
}

function currentMutationOwnerToken(queryClient: QueryClient): object | undefined {
    return queryClientAttachments.get(queryClient)?.token;
}

function ownsCurrentQueryClientAttachment(
    ownerToken: object | undefined,
    queryClient: QueryClient,
): boolean {
    return ownerToken !== undefined && currentMutationOwnerToken(queryClient) === ownerToken;
}

type QueryClientAttachment = {
    references: number;
    token: object;
    stopQuerySubscription: () => void;
    stopMutationSubscription: () => void;
    restoreQuery: (query: object) => void;
    trackedQueries: Map<object, TrackedQuery>;
    unmanagedInFlightQueries: WeakSet<object>;
};

/** Returns whether a query's generated metadata belongs to a connected client. */
export function isSpellbookQueryMetaConnected(meta: unknown): boolean {
    if (!meta || typeof meta !== "object") {
        return false;
    }
    const token = (meta as InternalMeta)[connectedQueryMetaKey];
    return token !== undefined && connectedQueryTokens.has(token);
}

/**
 * Connects the public TanStack caches so managed Spellbook failures are
 * reported once, after retries and lifecycle guards have finished.
 * Repeated attachment to one client shares a subscription and cleanup is safe
 * to call more than once.
 */
export function connectSpellbookToQueryClient(queryClient: QueryClient): () => void {
    let attachment = queryClientAttachments.get(queryClient);
    if (!attachment) {
        const trackedQueries = new Map<object, TrackedQuery>();
        const unmanagedInFlightQueries = new WeakSet<object>();
        const token = {};

        const trackQuery = (query: QueryLike) => {
            const queryObject = query as object;
            const managedOperation = managedOperationFromMeta(query.options.meta);
            const existing = trackedQueries.get(queryObject);
            if (
                !managedOperation ||
                managedOperation.source !== "query" ||
                !query.options.queryFn
            ) {
                if (existing) {
                    const restoreQueryFn = query.options.queryFn === existing.connectedQueryFn;
                    const restoreRetry = query.options.retry === existing.connectedRetry;
                    if (restoreQueryFn) {
                        query.options.queryFn = existing.originalQueryFn;
                    }
                    if (restoreRetry) {
                        query.options.retry = existing.originalRetry;
                    }
                }
                trackedQueries.delete(queryObject);
                return;
            }

            if (
                existing?.managedOperation === managedOperation &&
                query.options.queryFn === existing.connectedQueryFn &&
                query.options.retry === existing.connectedRetry
            ) {
                return;
            }

            const originalQueryFn =
                existing && query.options.queryFn === existing.connectedQueryFn
                    ? existing.originalQueryFn
                    : (query.options.queryFn as (context: QueryFunctionContext) => unknown);
            const originalRetry =
                existing && query.options.retry === existing.connectedRetry
                    ? existing.originalRetry
                    : query.options.retry;
            const connectedQueryFn = (context: QueryFunctionContext) => {
                const managedExecution =
                    connectedQueryTokens.has(token) && !unmanagedInFlightQueries.has(queryObject);
                const tracked = trackedQueries.get(queryObject);
                if (tracked) {
                    tracked.managedExecution = managedExecution;
                }
                if (managedExecution) {
                    context.meta = withConnectedQueryMeta(
                        context.meta,
                        token,
                    ) as typeof context.meta;
                }
                return originalQueryFn(context);
            };
            const connectedRetry: NonNullable<UseQueryOptions["retry"]> = (failureCount, error) => {
                const retryNormally = () => {
                    if (typeof originalRetry === "function") {
                        return originalRetry(failureCount, error as never);
                    }
                    if (typeof originalRetry === "number") {
                        return failureCount < originalRetry;
                    }
                    if (originalRetry !== undefined) {
                        return originalRetry;
                    }
                    // Match Query's own default when no client/key retry was set.
                    return failureCount < (isServer ? 0 : 3);
                };
                // A fetch active at attachment keeps its original retry contract
                // for the whole cycle, including any later retry attempts.
                if (unmanagedInFlightQueries.has(queryObject)) {
                    return retryNormally();
                }
                if (
                    error instanceof ScopeUnavailableError ||
                    error instanceof ScopeContextChangedError
                ) {
                    return false;
                }
                return retryNormally();
            };

            // Query.fetch captures this per-Query options object for its retryer
            // before dispatching the public "fetch" cache event. Update only its
            // fields in place so the retryer sees the scope-aware retry wrapper;
            // Query's defaulting step already cloned the shared factory options.
            query.options.queryFn = connectedQueryFn;
            query.options.retry = connectedRetry;
            trackedQueries.set(queryObject, {
                managedOperation,
                originalQueryFn,
                connectedQueryFn,
                originalRetry,
                connectedRetry,
                managedExecution: existing?.managedExecution ?? false,
            });
        };

        const restoreQuery = (query: QueryLike) => {
            const queryObject = query as object;
            const existing = trackedQueries.get(queryObject);
            if (existing) {
                const restoreQueryFn = query.options.queryFn === existing.connectedQueryFn;
                const restoreRetry = query.options.retry === existing.connectedRetry;
                if (restoreQueryFn) {
                    query.options.queryFn = existing.originalQueryFn;
                }
                if (restoreRetry) {
                    query.options.retry = existing.originalRetry;
                }
            }
            trackedQueries.delete(queryObject);
        };

        const queryCache = queryClient.getQueryCache();
        const mutationCache = queryClient.getMutationCache();

        attachment = {
            references: 0,
            token,
            stopQuerySubscription: () => undefined,
            stopMutationSubscription: () => undefined,
            restoreQuery: (query) => restoreQuery(query as QueryLike),
            trackedQueries,
            unmanagedInFlightQueries,
        };
        queryClientAttachments.set(queryClient, attachment);
        connectedQueryTokens.add(token);

        for (const query of queryCache.getAll()) {
            if ((query as unknown as QueryLike).state.fetchStatus !== "idle") {
                unmanagedInFlightQueries.add(query as object);
            }
            trackQuery(query as unknown as QueryLike);
        }

        attachment.stopQuerySubscription = queryCache.subscribe((event) => {
            if (!("query" in event)) {
                return;
            }

            const query = event.query as unknown as QueryLike;
            if (event.type === "removed") {
                restoreQuery(query);
                return;
            }

            if (event.type === "updated" && event.action.type === "fetch") {
                const tracked = trackedQueries.get(query as object);
                if (tracked && !unmanagedInFlightQueries.has(query as object)) {
                    tracked.managedExecution = false;
                }
            }
            trackQuery(query);
            if (
                event.type === "updated" &&
                query.state.fetchStatus === "idle" &&
                unmanagedInFlightQueries.delete(query as object)
            ) {
                return;
            }
            if (
                event.type !== "updated" ||
                event.action.type !== "error" ||
                query.state.fetchStatus !== "idle"
            ) {
                return;
            }

            const managedOperation = managedOperationFromMeta(query.options.meta);
            const error = query.state.error;
            if (
                !managedOperation ||
                managedOperation.source !== "query" ||
                !trackedQueries.get(query as object)?.managedExecution ||
                !isSpellbookError(error)
            ) {
                return;
            }
            const context: SpellbookErrorContext = {
                source: "query",
                operation: managedOperation.operation,
            };
            notifySpellbookError(error, context);
        });

        attachment.stopMutationSubscription = mutationCache.subscribe((event) => {
            if (event.type !== "updated" || event.action.type !== "error") {
                return;
            }

            const mutation = event.mutation as unknown as MutationLike;
            const managedOperation = managedOperationFromMeta(mutation.options.meta);
            const error = mutation.state.error;
            if (
                mutation.state.status !== "error" ||
                !managedOperation ||
                managedOperation.source !== "mutation" ||
                !isSpellbookError(error)
            ) {
                return;
            }

            const ownerToken = directMutationNotificationState(
                mutation.state.variables,
            )?.ownerToken;
            if (!ownsCurrentQueryClientAttachment(ownerToken, queryClient)) {
                return;
            }
            const context: SpellbookErrorContext = {
                source: "mutation",
                operation: managedOperation.operation,
            };
            notifySpellbookError(error, context);
        });
    }

    attachment.references += 1;
    let cleanedUp = false;
    return () => {
        if (cleanedUp) {
            return;
        }
        cleanedUp = true;
        attachment!.references -= 1;
        if (attachment!.references > 0) {
            return;
        }

        attachment!.stopQuerySubscription();
        attachment!.stopMutationSubscription();
        for (const query of attachment!.trackedQueries.keys()) {
            attachment!.restoreQuery(query);
        }
        attachment!.trackedQueries.clear();
        connectedQueryTokens.delete(attachment!.token);
        queryClientAttachments.delete(queryClient);
    };
}

function sameScopeBinding(left: ScopeContextBinding, right: ScopeContextBinding): boolean {
    return (
        left.usesResolver === right.usesResolver &&
        left.status === right.status &&
        JSON.stringify(left.cacheIdentity) === JSON.stringify(right.cacheIdentity)
    );
}

function assertCurrentScope(
    operation: ScopeOperation,
    requestContext: RequestContext | undefined,
    expected: ScopeContextBinding,
): void {
    const { binding } = resolveScopeBinding(operation, requestContext);
    if (binding.status === "blocked") {
        throw new ScopeUnavailableError(
            operation,
            scopeEligibilityFromBinding(binding) as Extract<ScopeEligibility, { allowed: false }>,
        );
    }
    if (!sameScopeBinding(expected, binding)) {
        throw new ScopeContextChangedError();
    }
}

/** Creates query options with a scope-specific key and execution guard. */
export function createScopeQueryOptions<
    TQueryFnData,
    TError = DefaultError,
    TData = TQueryFnData,
    TQueryKey extends QueryKey = QueryKey,
>(input: {
    operation: ScopeOperation;
    requestContext?: RequestContext;
    queryKey: TQueryKey;
    queryFn: (
        context: QueryFunctionContext<TQueryKey>,
        expectedScope: ScopeContextBinding,
    ) => TQueryFnData | Promise<TQueryFnData>;
    options?: ScopeQueryOptions<TQueryFnData, TError, TData, TQueryKey>;
}) {
    const initial = resolveScopeBinding(input.operation, input.requestContext).binding;
    const meta = withManagedOperationMeta(input.options?.meta, {
        source: "query",
        operation: input.operation,
    });
    const queryKey = (
        initial.usesResolver
            ? [...input.queryKey, "scope", initial.status, ...initial.cacheIdentity]
            : input.queryKey
    ) as TQueryKey;

    const queryOptions = {
        ...input.options,
        meta,
        queryKey,
        queryFn: async (context: QueryFunctionContext<TQueryKey>) => {
            try {
                assertCurrentScope(input.operation, input.requestContext, initial);
            } catch (error) {
                notifyUnconnectedScopeGuard(
                    error,
                    { source: "query", operation: input.operation },
                    isSpellbookQueryMetaConnected(context.meta),
                );
                throw error;
            }
            const notifyOnError = () => !isSpellbookQueryMetaConnected(context.meta);
            const managedScope = withScopeErrorNotification(initial, {
                errorContext: { source: "query", operation: input.operation },
                notifyOnError,
            });
            return input.queryFn(context, managedScope);
        },
        enabled:
            initial.status === "ready" &&
            (typeof input.options?.enabled === "function"
                ? input.options.enabled()
                : (input.options?.enabled ?? true)),
    };

    if (input.options?.initialData === undefined) {
        return createQueryOptions({ ...queryOptions, initialData: undefined });
    }
    return createQueryOptions({
        ...queryOptions,
        initialData: input.options.initialData as
            | NonUndefined<TQueryFnData>
            | (() => NonUndefined<TQueryFnData>),
    });
}

/**
 * Variables accepted by an idempotent mutation. The key is transport
 * metadata and is never included in the JSON request body.
 */
export type MutationVariables<TVariables extends object> = TVariables & {
    idempotencyKey?: string;
};

/** Options accepted by a generated idempotent mutation hook. */
export type IdempotentMutationOptions<
    TData,
    TVariables extends object,
    TError = DefaultError,
    TContext = unknown,
> = Omit<UseMutationOptions<TData, TError, MutationVariables<TVariables>, TContext>, "mutationFn">;

/** Result returned by a generated idempotent mutation hook. */
export type IdempotentMutationResult<
    TData,
    TVariables extends object,
    TError = DefaultError,
    TContext = unknown,
> = UseMutationResult<TData, TError, MutationVariables<TVariables>, TContext> & {
    /** Returns current eligibility for the supplied operation variables. */
    getEligibility: (variables: MutationVariables<TVariables>) => ScopeEligibility;
    /** Retry the most recent logical operation with its original key. */
    retry: () => void;
    /** Start a new logical operation with a fresh key, even with the same input. */
    restart: (variables: MutationVariables<TVariables>) => void;
};

type IdempotencyOperation = {
    key: string;
};

type IdempotencyOperationManager = {
    prepare<TVariables extends object>(
        variables: MutationVariables<TVariables>,
        scopeFingerprint?: string,
    ): MutationVariables<TVariables>;
    clearIfSuccessful<TVariables extends object>(
        variables: MutationVariables<TVariables>,
        scopeFingerprint?: string,
    ): void;
    reset(): void;
};

const opaqueObjectIDs = new WeakMap<object, number>();
let nextOpaqueObjectID = 0;

function opaqueObjectID(value: object): number {
    const existingID = opaqueObjectIDs.get(value);
    if (existingID !== undefined) {
        return existingID;
    }

    nextOpaqueObjectID += 1;
    opaqueObjectIDs.set(value, nextOpaqueObjectID);
    return nextOpaqueObjectID;
}

function stableSerialize(value: unknown, seen = new WeakSet<object>()): string {
    if (value === null) {
        return "null";
    }
    if (typeof value === "string") {
        return `string:${JSON.stringify(value)}`;
    }
    if (typeof value === "boolean") {
        return `boolean:${value}`;
    }
    if (typeof value === "number") {
        return `number:${String(value)}`;
    }
    if (typeof value === "bigint") {
        return `bigint:${value.toString()}`;
    }
    if (typeof value === "undefined") {
        return "undefined";
    }
    if (typeof value === "symbol") {
        return `symbol:${String(value)}`;
    }
    if (typeof value === "function") {
        return `function:${String(value)}`;
    }

    if (value instanceof Date) {
        return `date:${value.getTime()}`;
    }
    if (typeof URLSearchParams !== "undefined" && value instanceof URLSearchParams) {
        return `url-search-params:${value.toString()}`;
    }
    if (typeof FormData !== "undefined" && value instanceof FormData) {
        return `form-data:${opaqueObjectID(value)}`;
    }
    if (typeof Blob !== "undefined" && value instanceof Blob) {
        return `blob:${opaqueObjectID(value)}`;
    }

    if (seen.has(value)) {
        return "[Circular]";
    }
    seen.add(value);

    let serialized: string;
    if (Array.isArray(value)) {
        serialized = `[${value.map((item) => stableSerialize(item, seen)).join(",")}]`;
    } else {
        const record = value as Record<string, unknown>;
        serialized = `{${Object.keys(record)
            .sort()
            .map((key) => `${JSON.stringify(key)}:${stableSerialize(record[key], seen)}`)
            .join(",")}}`;
    }

    seen.delete(value);
    return serialized;
}

function requestFingerprint(variables: object, scopeFingerprint?: string): string {
    const requestVariables = Object.fromEntries(
        Object.entries(variables).filter(
            ([key]) =>
                key !== "idempotencyKey" &&
                key !== "signal" &&
                key !== "requestContext" &&
                key !== "headers",
        ),
    );
    return stableSerialize([requestVariables, scopeFingerprint]);
}

/** @internal */
export function createIdempotencyOperationManager(
    keyFactory: () => string = createIdempotencyKey,
): IdempotencyOperationManager {
    const operations = new Map<string, IdempotencyOperation>();

    return {
        prepare<TVariables extends object>(
            variables: MutationVariables<TVariables>,
            scopeFingerprint?: string,
        ) {
            const fingerprint = requestFingerprint(variables, scopeFingerprint);
            if (variables.idempotencyKey) {
                operations.set(fingerprint, { key: variables.idempotencyKey });
                return variables;
            }

            const operation = operations.get(fingerprint);
            if (operation) {
                return { ...variables, idempotencyKey: operation.key };
            }

            const key = keyFactory();
            operations.set(fingerprint, { key });
            return { ...variables, idempotencyKey: key };
        },
        clearIfSuccessful<TVariables extends object>(
            variables: MutationVariables<TVariables>,
            scopeFingerprint?: string,
        ) {
            const fingerprint = requestFingerprint(variables, scopeFingerprint);
            if (operations.get(fingerprint)?.key === variables.idempotencyKey) {
                operations.delete(fingerprint);
            }
        },
        reset() {
            operations.clear();
        },
    };
}

/**
 * Returns whether an idempotent mutation should receive one automatic retry.
 * The same idempotency key is reused by TanStack Query for that retry.
 */
export function defaultIdempotencyMutationRetry(failureCount: number, error: unknown): boolean {
    if (failureCount >= 1) {
        return false;
    }
    if (error instanceof RequestError) {
        return error.reason === "Transport";
    }
    if (error instanceof ResponseError) {
        const status = error.response.status;
        return status === 408 || status === 425 || status === 429 || status >= 500;
    }
    return false;
}

type ScopeBindingMap = WeakMap<object, ScopeContextBinding>;

function scopeBindingFor(
    variables: object,
    operation: ScopeOperation | undefined,
    bindings: ScopeBindingMap,
): ScopeContextBinding {
    const existing = bindings.get(variables);
    if (existing) {
        return existing;
    }
    const binding = operation
        ? resolveScopeBinding(
              operation,
              (variables as { requestContext?: RequestContext }).requestContext,
          ).binding
        : { usesResolver: false, status: "ready" as const, cacheIdentity: [], missingScopes: [] };
    bindings.set(variables, binding);
    return binding;
}

function bindMutationVariables<TVariables extends object>(
    variables: MutationVariables<TVariables>,
    operation: ScopeOperation | undefined,
    bindings: ScopeBindingMap,
): MutationVariables<TVariables> {
    const captured = { ...variables };
    const binding = scopeBindingFor(captured, operation, bindings);
    bindings.set(captured, binding);
    return captured;
}

function assertMutationScope<TVariables extends object>(
    variables: MutationVariables<TVariables>,
    operation: ScopeOperation | undefined,
    bindings: ScopeBindingMap,
): ScopeContextBinding {
    const binding = scopeBindingFor(variables, operation, bindings);
    if (operation) {
        assertCurrentScope(
            operation,
            (variables as { requestContext?: RequestContext }).requestContext,
            binding,
        );
    }
    return binding;
}

function scopeFingerprint(binding: ScopeContextBinding): string | undefined {
    return binding.usesResolver
        ? stableSerialize([binding.status, binding.cacheIdentity])
        : undefined;
}

function scopeEligibility(
    operation: ScopeOperation | undefined,
    variables: object,
): ScopeEligibility {
    if (!operation) {
        return {
            usesResolver: false,
            status: "ready",
            cacheIdentity: [],
            allowed: true,
            missingScopes: [],
        };
    }
    return checkScopeEligibility(operation, {
        requestContext: (variables as { requestContext?: RequestContext }).requestContext,
    });
}

function retryWithScopeGuard<TError>(
    retry: boolean | number | ((failureCount: number, error: TError) => boolean) | undefined,
    failureCount: number,
    error: TError,
    defaultRetry: (failureCount: number, error: unknown) => boolean,
): boolean {
    if (error instanceof ScopeUnavailableError || error instanceof ScopeContextChangedError) {
        return false;
    }
    if (typeof retry === "function") {
        return retry(failureCount, error);
    }
    if (typeof retry === "number") {
        return failureCount < retry;
    }
    if (retry !== undefined) {
        return retry;
    }
    return defaultRetry(failureCount, error);
}

function effectiveMutationRetry<TData, TError, TVariables extends object, TContext>(
    queryClient: QueryClient,
    options: UseMutationOptions<TData, TError, MutationVariables<TVariables>, TContext> | undefined,
    fallback: boolean | number | ((failureCount: number, error: TError) => boolean),
): boolean | number | ((failureCount: number, error: TError) => boolean) {
    const mutationKeyRetry = options?.mutationKey
        ? queryClient.getMutationDefaults(options.mutationKey)?.retry
        : undefined;
    return (options?.retry ??
        mutationKeyRetry ??
        queryClient.getDefaultOptions().mutations?.retry ??
        fallback) as boolean | number | ((failureCount: number, error: TError) => boolean);
}

/** Options accepted by a scope-guarded generated mutation hook. */
export type ScopeAwareMutationOptions<
    TData,
    TVariables extends object,
    TError = DefaultError,
    TContext = unknown,
> = Omit<UseMutationOptions<TData, TError, MutationVariables<TVariables>, TContext>, "mutationFn">;

/** Result returned by a scope-guarded generated mutation hook. */
export type ScopeAwareMutationResult<
    TData,
    TVariables extends object,
    TError = DefaultError,
    TContext = unknown,
> = UseMutationResult<TData, TError, MutationVariables<TVariables>, TContext> & {
    /** Returns current eligibility for the supplied operation variables. */
    getEligibility: (variables: MutationVariables<TVariables>) => ScopeEligibility;
};

/** Creates a TanStack mutation that guards scope before lifecycle callbacks and requests. */
export function useScopeAwareMutation<
    TData,
    TVariables extends object,
    TError = DefaultError,
    TContext = unknown,
>(
    execute: (variables: TVariables, expectedScope: ScopeContextBinding) => Promise<TData>,
    options: ScopeAwareMutationOptions<TData, TVariables, TError, TContext> | undefined,
    operation: ScopeOperation,
): ScopeAwareMutationResult<TData, TVariables, TError, TContext> {
    const queryClient = useQueryClient();
    const inheritedRetry = effectiveMutationRetry(queryClient, options, false);
    const bindings = useRef<ScopeBindingMap>(new WeakMap());
    const onMutate = options?.onMutate;
    const guardedOnMutate: NonNullable<
        UseMutationOptions<TData, TError, MutationVariables<TVariables>, TContext>["onMutate"]
    > = (...args) => {
        if (!directMutationNotificationState(args[0])) {
            startMutationNotificationState(args[0], currentMutationOwnerToken(queryClient));
        }
        try {
            assertMutationScope(args[0], operation, bindings.current);
        } catch (error) {
            notifyUnconnectedScopeGuard(
                error,
                { source: "mutation", operation },
                ownsCurrentQueryClientAttachment(
                    directMutationNotificationState(args[0])?.ownerToken,
                    queryClient,
                ),
            );
            throw error;
        }
        return onMutate ? onMutate(...args) : (undefined as TContext);
    };

    const mutation = useMutation<TData, TError, MutationVariables<TVariables>, TContext>({
        ...options,
        meta: withManagedOperationMeta(options?.meta, { source: "mutation", operation }),
        retry: (failureCount, error) =>
            retryWithScopeGuard(
                inheritedRetry as
                    | boolean
                    | number
                    | ((failureCount: number, error: TError) => boolean)
                    | undefined,
                failureCount,
                error,
                () => false,
            ),
        onMutate: guardedOnMutate,
        mutationFn: (variables) => {
            if (!directMutationNotificationState(variables)) {
                startMutationNotificationState(variables, currentMutationOwnerToken(queryClient));
            }
            const ownerToken = directMutationNotificationState(variables)?.ownerToken;
            let binding: ScopeContextBinding;
            try {
                binding = assertMutationScope(variables, operation, bindings.current);
            } catch (error) {
                notifyUnconnectedScopeGuard(
                    error,
                    { source: "mutation", operation },
                    ownsCurrentQueryClientAttachment(ownerToken, queryClient),
                );
                throw error;
            }
            const notifyOnError = () => !ownsCurrentQueryClientAttachment(ownerToken, queryClient);
            const managedBinding = withScopeErrorNotification(binding, {
                errorContext: { source: "mutation", operation },
                notifyOnError,
            });
            return execute(variables as TVariables, managedBinding);
        },
    });

    const mutationRef = useRef(mutation);
    mutationRef.current = mutation;

    const mutate: typeof mutation.mutate = useCallback(
        (...args) => {
            const [variables, mutateOptions] = args;
            if (variables === undefined) {
                mutationRef.current.mutate(...args);
                return;
            }
            const boundVariables = bindMutationVariables(variables, operation, bindings.current);
            startMutationNotificationState(boundVariables, currentMutationOwnerToken(queryClient));
            mutationRef.current.mutate(boundVariables, mutateOptions);
        },
        [operation, queryClient],
    );
    const mutateAsync: typeof mutation.mutateAsync = useCallback(
        (...args) => {
            const [variables, mutateOptions] = args;
            if (variables === undefined) {
                return mutationRef.current.mutateAsync(...args);
            }
            const boundVariables = bindMutationVariables(variables, operation, bindings.current);
            startMutationNotificationState(boundVariables, currentMutationOwnerToken(queryClient));
            return mutationRef.current.mutateAsync(boundVariables, mutateOptions);
        },
        [operation, queryClient],
    );
    const getEligibility = useCallback(
        (variables: MutationVariables<TVariables>) => scopeEligibility(operation, variables),
        [operation],
    );

    return { ...mutation, mutate, mutateAsync, getEligibility };
}

/**
 * Creates a TanStack mutation that automatically reuses an idempotency key
 * when an equivalent request is attempted again after a failure.
 *
 * Calling reset forgets the current operation. Calling restart explicitly
 * starts a new operation even when its input is unchanged.
 */
export function useIdempotentMutation<
    TData,
    TVariables extends object,
    TError = DefaultError,
    TContext = unknown,
>(
    execute: (
        variables: TVariables,
        idempotencyKey: string,
        expectedScope: ScopeContextBinding,
    ) => Promise<TData>,
    options?: IdempotentMutationOptions<TData, TVariables, TError, TContext>,
    operation?: ScopeOperation,
): IdempotentMutationResult<TData, TVariables, TError, TContext> {
    const queryClient = useQueryClient();
    const operations = useRef(createIdempotencyOperationManager());
    const bindings = useRef<ScopeBindingMap>(new WeakMap());
    const onMutate = options?.onMutate;
    const guardedOnMutate: NonNullable<
        UseMutationOptions<TData, TError, MutationVariables<TVariables>, TContext>["onMutate"]
    > = (...args) => {
        if (!directMutationNotificationState(args[0])) {
            startMutationNotificationState(args[0], currentMutationOwnerToken(queryClient));
        }
        try {
            assertMutationScope(args[0], operation, bindings.current);
        } catch (error) {
            if (operation) {
                notifyUnconnectedScopeGuard(
                    error,
                    { source: "mutation", operation },
                    ownsCurrentQueryClientAttachment(
                        directMutationNotificationState(args[0])?.ownerToken,
                        queryClient,
                    ),
                );
            }
            throw error;
        }
        return onMutate ? onMutate(...args) : (undefined as TContext);
    };
    const onSuccess = options?.onSuccess;

    const mutation = useMutation<TData, TError, MutationVariables<TVariables>, TContext>({
        ...options,
        meta: withManagedOperationMeta(
            options?.meta,
            operation ? { source: "mutation", operation } : undefined,
        ),
        retry: (failureCount, error) =>
            retryWithScopeGuard(
                effectiveMutationRetry(queryClient, options, defaultIdempotencyMutationRetry),
                failureCount,
                error,
                defaultIdempotencyMutationRetry,
            ),
        onMutate: guardedOnMutate,
        onSuccess: (...args) => {
            const variables = args[1];
            const binding = scopeBindingFor(variables, operation, bindings.current);
            operations.current.clearIfSuccessful(variables, scopeFingerprint(binding));
            return onSuccess?.(...args);
        },
        mutationFn: (variables) => {
            if (!directMutationNotificationState(variables)) {
                startMutationNotificationState(variables, currentMutationOwnerToken(queryClient));
            }
            const ownerToken = directMutationNotificationState(variables)?.ownerToken;
            let binding: ScopeContextBinding;
            try {
                binding = assertMutationScope(variables, operation, bindings.current);
            } catch (error) {
                if (operation) {
                    notifyUnconnectedScopeGuard(
                        error,
                        { source: "mutation", operation },
                        ownsCurrentQueryClientAttachment(ownerToken, queryClient),
                    );
                }
                throw error;
            }
            const notifyOnError = () => !ownsCurrentQueryClientAttachment(ownerToken, queryClient);
            const managedBinding = operation
                ? withScopeErrorNotification(binding, {
                      errorContext: { source: "mutation", operation },
                      notifyOnError,
                  })
                : binding;
            const { idempotencyKey, ...requestVariables } = variables;
            if (!idempotencyKey) {
                throw new Error("An idempotency key is required for this mutation");
            }
            return execute(requestVariables as TVariables, idempotencyKey, managedBinding);
        },
    });

    const mutationRef = useRef(mutation);
    mutationRef.current = mutation;

    const prepareVariables = (variables: MutationVariables<TVariables>) => {
        const pinned = bindMutationVariables(variables, operation, bindings.current);
        const binding = scopeBindingFor(pinned, operation, bindings.current);
        if (binding.status === "blocked") {
            return pinned;
        }
        const prepared = operations.current.prepare(pinned, scopeFingerprint(binding));
        bindings.current.set(prepared, binding);
        return prepared;
    };

    const mutate: typeof mutation.mutate = useCallback(
        (...args) => {
            const [variables, mutateOptions] = args;
            if (variables === undefined) {
                mutationRef.current.mutate(...args);
                return;
            }
            const prepared = prepareVariables(variables);
            startMutationNotificationState(prepared, currentMutationOwnerToken(queryClient));
            mutationRef.current.mutate(prepared, mutateOptions);
        },
        [operation, queryClient],
    );

    const mutateAsync: typeof mutation.mutateAsync = useCallback(
        (...args) => {
            const [variables, mutateOptions] = args;
            if (variables === undefined) {
                return mutationRef.current.mutateAsync(...args);
            }
            const prepared = prepareVariables(variables);
            startMutationNotificationState(prepared, currentMutationOwnerToken(queryClient));
            return mutationRef.current.mutateAsync(prepared, mutateOptions);
        },
        [operation, queryClient],
    );

    const retry = useCallback(() => {
        const currentMutation = mutationRef.current;
        if (!currentMutation.variables) {
            throw new Error("There is no mutation operation to retry");
        }
        startMutationNotificationState(
            currentMutation.variables,
            currentMutationOwnerToken(queryClient),
        );
        currentMutation.mutate(currentMutation.variables);
    }, [queryClient]);

    const restart = useCallback(
        (variables: MutationVariables<TVariables>) => {
            operations.current.reset();
            const { idempotencyKey: _, ...requestVariables } = variables;
            mutate(requestVariables as MutationVariables<TVariables>);
        },
        [mutate],
    );

    const reset = useCallback(() => {
        operations.current.reset();
        mutationRef.current.reset();
    }, []);

    const getEligibility = useCallback(
        (variables: MutationVariables<TVariables>) => scopeEligibility(operation, variables),
        [operation],
    );

    return { ...mutation, mutate, mutateAsync, retry, restart, reset, getEligibility };
}

const activateUserEndpoint = spellbook("/auth/users/{id}/activate").PUT();

export type ActivateUserRequest<
    TParams = z.infer<typeof activateUserEndpoint.p>,
    TReq = z.infer<typeof activateUserEndpoint.req>,
> = {
    params: TParams;
    req: TReq;
} & RequestMeta;

const activateUserCall = (input: ActivateUserRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(activateUserEndpoint, undefined, { expectedScope });
    return call(input);
};
type ActivateUserResponse = Awaited<ReturnType<typeof activateUserCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type ActivateUserMutationCallOptions<
    TParams = z.infer<typeof activateUserEndpoint.p>,
    TReq = z.infer<typeof activateUserEndpoint.req>,
    TRes = ActivateUserResponse,
> = {
    schemas?: {
        params?: (schema: typeof activateUserEndpoint.p) => z.ZodType<TParams>;
        req?: (schema: typeof activateUserEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof activateUserEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/** Returns a TanStack mutation for PUT /auth/users/{id}/activate without idempotency retry behavior. */
export type ActivateUserMutationOptions<
    TParams = z.infer<typeof activateUserEndpoint.p>,
    TReq = z.infer<typeof activateUserEndpoint.req>,
    TRes = ActivateUserResponse,
    TContext = unknown,
> = Omit<
    UseMutationOptions<TRes, DefaultError, ActivateUserRequest<TParams, TReq>, TContext>,
    "mutationFn"
> & {
    call?: ActivateUserMutationCallOptions<TParams, TReq, TRes>;
};

export function useActivateUserMutation<
    TParams = z.infer<typeof activateUserEndpoint.p>,
    TReq = z.infer<typeof activateUserEndpoint.req>,
    TRes = ActivateUserResponse,
    TContext = unknown,
>(options?: ActivateUserMutationOptions<TParams, TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: ActivateUserRequest<TParams, TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, TReq, TRes>(
            {
                ...activateUserEndpoint,
                p: (callOptions?.schemas?.params?.(activateUserEndpoint.p) ??
                    activateUserEndpoint.p) as z.ZodType<TParams>,
                req: (callOptions?.schemas?.req?.(activateUserEndpoint.req) ??
                    activateUserEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(activateUserEndpoint.res) ??
                    activateUserEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call(input as unknown as Parameters<typeof call>[0]) as Promise<TRes>;
    };

    return useScopeAwareMutation<TRes, ActivateUserRequest<TParams, TReq>, DefaultError, TContext>(
        (input, expectedScope) => execute(input, expectedScope),
        { ...mutationOptions, mutationKey: ["PUT", "/auth/users/{id}/activate"] },
        activateUserEndpoint,
    );
}

const cancelTaskEndpoint = spellbook("/tasks/{id}/cancel").PATCH();

export type CancelTaskRequest<
    TParams = z.infer<typeof cancelTaskEndpoint.p>,
    TReq = z.infer<typeof cancelTaskEndpoint.req>,
> = {
    params: TParams;
    req: TReq;
} & RequestMeta;

const cancelTaskCall = (input: CancelTaskRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(cancelTaskEndpoint, undefined, { expectedScope });
    return call(input);
};
type CancelTaskResponse = Awaited<ReturnType<typeof cancelTaskCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type CancelTaskMutationCallOptions<
    TParams = z.infer<typeof cancelTaskEndpoint.p>,
    TReq = z.infer<typeof cancelTaskEndpoint.req>,
> = {
    schemas?: {
        params?: (schema: typeof cancelTaskEndpoint.p) => z.ZodType<TParams>;
        req?: (schema: typeof cancelTaskEndpoint.req) => z.ZodType<TReq>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for PATCH /tasks/{id}/cancel.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type CancelTaskMutationOptions<
    TParams = z.infer<typeof cancelTaskEndpoint.p>,
    TReq = z.infer<typeof cancelTaskEndpoint.req>,
    TContext = unknown,
> = IdempotentMutationOptions<
    CancelTaskResponse,
    CancelTaskRequest<TParams, TReq>,
    DefaultError,
    TContext
> & {
    call?: CancelTaskMutationCallOptions<TParams, TReq>;
};

export function useCancelTaskMutation<
    TParams = z.infer<typeof cancelTaskEndpoint.p>,
    TReq = z.infer<typeof cancelTaskEndpoint.req>,
    TContext = unknown,
>(options?: CancelTaskMutationOptions<TParams, TReq, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: CancelTaskRequest<TParams, TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, TReq, undefined>(
            {
                ...cancelTaskEndpoint,
                p: (callOptions?.schemas?.params?.(cancelTaskEndpoint.p) ??
                    cancelTaskEndpoint.p) as z.ZodType<TParams>,
                req: (callOptions?.schemas?.req?.(cancelTaskEndpoint.req) ??
                    cancelTaskEndpoint.req) as z.ZodType<TReq>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<CancelTaskResponse>;
    };

    return useIdempotentMutation<
        CancelTaskResponse,
        CancelTaskRequest<TParams, TReq>,
        DefaultError,
        TContext
    >(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as CancelTaskRequest<TParams, TReq>,
                expectedScope,
            ),
        {
            mutationKey: ["PATCH", "/tasks/{id}/cancel"],
            ...mutationOptions,
        },
        cancelTaskEndpoint,
    );
}

const checkGoogleAuthenticationEndpoint = spellbook("/auth/google/verify").POST();

export type CheckGoogleAuthenticationRequest = {} & RequestMeta;

const checkGoogleAuthenticationCall = (
    input: CheckGoogleAuthenticationRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(checkGoogleAuthenticationEndpoint, undefined, { expectedScope });
    return call(input);
};
type CheckGoogleAuthenticationResponse = Awaited<ReturnType<typeof checkGoogleAuthenticationCall>>;
/** Returns a TanStack mutation for POST /auth/google/verify without idempotency retry behavior. */
export type CheckGoogleAuthenticationMutationOptions<TContext = unknown> = Omit<
    UseMutationOptions<
        CheckGoogleAuthenticationResponse,
        DefaultError,
        CheckGoogleAuthenticationRequest,
        TContext
    >,
    "mutationFn"
>;

export function useCheckGoogleAuthenticationMutation<TContext = unknown>(
    options?: CheckGoogleAuthenticationMutationOptions<TContext>,
) {
    return useScopeAwareMutation<
        CheckGoogleAuthenticationResponse,
        CheckGoogleAuthenticationRequest,
        DefaultError,
        TContext
    >(
        (input, expectedScope) => checkGoogleAuthenticationCall(input, expectedScope),
        { ...options, mutationKey: ["POST", "/auth/google/verify"] },
        checkGoogleAuthenticationEndpoint,
    );
}

const childrenSearchEndpoint = spellbook("/organisations/children/search").GET();

export type ChildrenSearchRequest<TQuery = z.infer<typeof childrenSearchEndpoint.q>> = {
    query: TQuery;
} & RequestMeta;

const childrenSearchCall = (input: ChildrenSearchRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(childrenSearchEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /organisations/children/search. */
export function childrenSearchQueryOptions<
    TSelected = Awaited<ReturnType<typeof childrenSearchCall>>,
>(
    input: ChildrenSearchRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof childrenSearchCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: childrenSearchEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/organisations/children/search", input.query] as const,
        queryFn: ({ signal }, expectedScope) =>
            childrenSearchCall({ ...input, signal }, expectedScope),
        options,
    });
}

const cloneEndpoint = spellbook("/tasks/{id}/clone").POST();

export type CloneRequest<
    TParams = z.infer<typeof cloneEndpoint.p>,
    TReq = z.infer<typeof cloneEndpoint.req>,
> = {
    params: TParams;
    req: TReq;
} & RequestMeta;

const cloneCall = (input: CloneRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(cloneEndpoint, undefined, { expectedScope });
    return call(input);
};
type CloneResponse = Awaited<ReturnType<typeof cloneCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type CloneMutationCallOptions<
    TParams = z.infer<typeof cloneEndpoint.p>,
    TReq = z.infer<typeof cloneEndpoint.req>,
    TRes = CloneResponse,
> = {
    schemas?: {
        params?: (schema: typeof cloneEndpoint.p) => z.ZodType<TParams>;
        req?: (schema: typeof cloneEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof cloneEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for POST /tasks/{id}/clone.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type CloneMutationOptions<
    TParams = z.infer<typeof cloneEndpoint.p>,
    TReq = z.infer<typeof cloneEndpoint.req>,
    TRes = CloneResponse,
    TContext = unknown,
> = IdempotentMutationOptions<TRes, CloneRequest<TParams, TReq>, DefaultError, TContext> & {
    call?: CloneMutationCallOptions<TParams, TReq, TRes>;
};

export function useCloneMutation<
    TParams = z.infer<typeof cloneEndpoint.p>,
    TReq = z.infer<typeof cloneEndpoint.req>,
    TRes = CloneResponse,
    TContext = unknown,
>(options?: CloneMutationOptions<TParams, TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (input: CloneRequest<TParams, TReq>, expectedScope: ScopeContextBinding) => {
        const call = createCall<TParams, undefined, TReq, TRes>(
            {
                ...cloneEndpoint,
                p: (callOptions?.schemas?.params?.(cloneEndpoint.p) ??
                    cloneEndpoint.p) as z.ZodType<TParams>,
                req: (callOptions?.schemas?.req?.(cloneEndpoint.req) ??
                    cloneEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(cloneEndpoint.res) ??
                    cloneEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<TRes, CloneRequest<TParams, TReq>, DefaultError, TContext>(
        (input, idempotencyKey, expectedScope) =>
            execute({ ...input, idempotencyKey } as CloneRequest<TParams, TReq>, expectedScope),
        {
            mutationKey: ["POST", "/tasks/{id}/clone"],
            ...mutationOptions,
        },
        cloneEndpoint,
    );
}

const confirmEndpoint = spellbook("/tasks/{id}/confirm").POST();

export type ConfirmRequest<
    TParams = z.infer<typeof confirmEndpoint.p>,
    TReq = z.infer<typeof confirmEndpoint.req>,
> = {
    params: TParams;
    req: TReq;
} & RequestMeta;

const confirmCall = (input: ConfirmRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(confirmEndpoint, undefined, { expectedScope });
    return call(input);
};
type ConfirmResponse = Awaited<ReturnType<typeof confirmCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type ConfirmMutationCallOptions<
    TParams = z.infer<typeof confirmEndpoint.p>,
    TReq = z.infer<typeof confirmEndpoint.req>,
    TRes = ConfirmResponse,
> = {
    schemas?: {
        params?: (schema: typeof confirmEndpoint.p) => z.ZodType<TParams>;
        req?: (schema: typeof confirmEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof confirmEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for POST /tasks/{id}/confirm.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type ConfirmMutationOptions<
    TParams = z.infer<typeof confirmEndpoint.p>,
    TReq = z.infer<typeof confirmEndpoint.req>,
    TRes = ConfirmResponse,
    TContext = unknown,
> = IdempotentMutationOptions<TRes, ConfirmRequest<TParams, TReq>, DefaultError, TContext> & {
    call?: ConfirmMutationCallOptions<TParams, TReq, TRes>;
};

export function useConfirmMutation<
    TParams = z.infer<typeof confirmEndpoint.p>,
    TReq = z.infer<typeof confirmEndpoint.req>,
    TRes = ConfirmResponse,
    TContext = unknown,
>(options?: ConfirmMutationOptions<TParams, TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (input: ConfirmRequest<TParams, TReq>, expectedScope: ScopeContextBinding) => {
        const call = createCall<TParams, undefined, TReq, TRes>(
            {
                ...confirmEndpoint,
                p: (callOptions?.schemas?.params?.(confirmEndpoint.p) ??
                    confirmEndpoint.p) as z.ZodType<TParams>,
                req: (callOptions?.schemas?.req?.(confirmEndpoint.req) ??
                    confirmEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(confirmEndpoint.res) ??
                    confirmEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<TRes, ConfirmRequest<TParams, TReq>, DefaultError, TContext>(
        (input, idempotencyKey, expectedScope) =>
            execute({ ...input, idempotencyKey } as ConfirmRequest<TParams, TReq>, expectedScope),
        {
            mutationKey: ["POST", "/tasks/{id}/confirm"],
            ...mutationOptions,
        },
        confirmEndpoint,
    );
}

const confirmHourTypesEndpoint = spellbook(
    "/organisations/{organisationId}/confirm_hours-types",
).PUT();

export type ConfirmHourTypesRequest<TParams = z.infer<typeof confirmHourTypesEndpoint.p>> = {
    params: TParams;
} & RequestMeta;

const confirmHourTypesCall = (
    input: ConfirmHourTypesRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(confirmHourTypesEndpoint, undefined, { expectedScope });
    return call(input);
};
type ConfirmHourTypesResponse = Awaited<ReturnType<typeof confirmHourTypesCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type ConfirmHourTypesMutationCallOptions<
    TParams = z.infer<typeof confirmHourTypesEndpoint.p>,
> = {
    schemas?: {
        params?: (schema: typeof confirmHourTypesEndpoint.p) => z.ZodType<TParams>;
    };
};
/**
 * Returns an idempotent TanStack mutation for PUT /organisations/{organisationId}/confirm_hours-types.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type ConfirmHourTypesMutationOptions<
    TParams = z.infer<typeof confirmHourTypesEndpoint.p>,
    TContext = unknown,
> = IdempotentMutationOptions<
    ConfirmHourTypesResponse,
    ConfirmHourTypesRequest<TParams>,
    DefaultError,
    TContext
> & {
    call?: ConfirmHourTypesMutationCallOptions<TParams>;
};

export function useConfirmHourTypesMutation<
    TParams = z.infer<typeof confirmHourTypesEndpoint.p>,
    TContext = unknown,
>(options?: ConfirmHourTypesMutationOptions<TParams, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: ConfirmHourTypesRequest<TParams>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, undefined, undefined>(
            {
                ...confirmHourTypesEndpoint,
                p: (callOptions?.schemas?.params?.(confirmHourTypesEndpoint.p) ??
                    confirmHourTypesEndpoint.p) as z.ZodType<TParams>,
            },
            undefined,
            {
                expectedScope,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<ConfirmHourTypesResponse>;
    };

    return useIdempotentMutation<
        ConfirmHourTypesResponse,
        ConfirmHourTypesRequest<TParams>,
        DefaultError,
        TContext
    >(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as ConfirmHourTypesRequest<TParams>,
                expectedScope,
            ),
        {
            mutationKey: ["PUT", "/organisations/{organisationId}/confirm_hours-types"],
            ...mutationOptions,
        },
        confirmHourTypesEndpoint,
    );
}

const confirmIndemnitiesEndpoint = spellbook(
    "/organisations/{organisationId}/confirm_indemnities",
).PUT();

export type ConfirmIndemnitiesRequest<TParams = z.infer<typeof confirmIndemnitiesEndpoint.p>> = {
    params: TParams;
} & RequestMeta;

const confirmIndemnitiesCall = (
    input: ConfirmIndemnitiesRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(confirmIndemnitiesEndpoint, undefined, { expectedScope });
    return call(input);
};
type ConfirmIndemnitiesResponse = Awaited<ReturnType<typeof confirmIndemnitiesCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type ConfirmIndemnitiesMutationCallOptions<
    TParams = z.infer<typeof confirmIndemnitiesEndpoint.p>,
> = {
    schemas?: {
        params?: (schema: typeof confirmIndemnitiesEndpoint.p) => z.ZodType<TParams>;
    };
};
/**
 * Returns an idempotent TanStack mutation for PUT /organisations/{organisationId}/confirm_indemnities.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type ConfirmIndemnitiesMutationOptions<
    TParams = z.infer<typeof confirmIndemnitiesEndpoint.p>,
    TContext = unknown,
> = IdempotentMutationOptions<
    ConfirmIndemnitiesResponse,
    ConfirmIndemnitiesRequest<TParams>,
    DefaultError,
    TContext
> & {
    call?: ConfirmIndemnitiesMutationCallOptions<TParams>;
};

export function useConfirmIndemnitiesMutation<
    TParams = z.infer<typeof confirmIndemnitiesEndpoint.p>,
    TContext = unknown,
>(options?: ConfirmIndemnitiesMutationOptions<TParams, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: ConfirmIndemnitiesRequest<TParams>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, undefined, undefined>(
            {
                ...confirmIndemnitiesEndpoint,
                p: (callOptions?.schemas?.params?.(confirmIndemnitiesEndpoint.p) ??
                    confirmIndemnitiesEndpoint.p) as z.ZodType<TParams>,
            },
            undefined,
            {
                expectedScope,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<ConfirmIndemnitiesResponse>;
    };

    return useIdempotentMutation<
        ConfirmIndemnitiesResponse,
        ConfirmIndemnitiesRequest<TParams>,
        DefaultError,
        TContext
    >(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as ConfirmIndemnitiesRequest<TParams>,
                expectedScope,
            ),
        {
            mutationKey: ["PUT", "/organisations/{organisationId}/confirm_indemnities"],
            ...mutationOptions,
        },
        confirmIndemnitiesEndpoint,
    );
}

const contestDocumentEndpoint = spellbook(
    "/organisations/{organisationId}/document/{documentId}/contest",
).PUT();

export type ContestDocumentRequest<
    TParams = z.infer<typeof contestDocumentEndpoint.p>,
    TReq = z.infer<typeof contestDocumentEndpoint.req>,
> = {
    params: TParams;
    req: TReq;
} & RequestMeta;

const contestDocumentCall = (
    input: ContestDocumentRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(contestDocumentEndpoint, undefined, { expectedScope });
    return call(input);
};
type ContestDocumentResponse = Awaited<ReturnType<typeof contestDocumentCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type ContestDocumentMutationCallOptions<
    TParams = z.infer<typeof contestDocumentEndpoint.p>,
    TReq = z.infer<typeof contestDocumentEndpoint.req>,
    TRes = ContestDocumentResponse,
> = {
    schemas?: {
        params?: (schema: typeof contestDocumentEndpoint.p) => z.ZodType<TParams>;
        req?: (schema: typeof contestDocumentEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof contestDocumentEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for PUT /organisations/{organisationId}/document/{documentId}/contest.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type ContestDocumentMutationOptions<
    TParams = z.infer<typeof contestDocumentEndpoint.p>,
    TReq = z.infer<typeof contestDocumentEndpoint.req>,
    TRes = ContestDocumentResponse,
    TContext = unknown,
> = IdempotentMutationOptions<
    TRes,
    ContestDocumentRequest<TParams, TReq>,
    DefaultError,
    TContext
> & {
    call?: ContestDocumentMutationCallOptions<TParams, TReq, TRes>;
};

export function useContestDocumentMutation<
    TParams = z.infer<typeof contestDocumentEndpoint.p>,
    TReq = z.infer<typeof contestDocumentEndpoint.req>,
    TRes = ContestDocumentResponse,
    TContext = unknown,
>(options?: ContestDocumentMutationOptions<TParams, TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: ContestDocumentRequest<TParams, TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, TReq, TRes>(
            {
                ...contestDocumentEndpoint,
                p: (callOptions?.schemas?.params?.(contestDocumentEndpoint.p) ??
                    contestDocumentEndpoint.p) as z.ZodType<TParams>,
                req: (callOptions?.schemas?.req?.(contestDocumentEndpoint.req) ??
                    contestDocumentEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(contestDocumentEndpoint.res) ??
                    contestDocumentEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<
        TRes,
        ContestDocumentRequest<TParams, TReq>,
        DefaultError,
        TContext
    >(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as ContestDocumentRequest<TParams, TReq>,
                expectedScope,
            ),
        {
            mutationKey: ["PUT", "/organisations/{organisationId}/document/{documentId}/contest"],
            ...mutationOptions,
        },
        contestDocumentEndpoint,
    );
}

const createAccountEndpoint = spellbook("/auth/account").POST();

export type CreateAccountRequest<TReq = z.infer<typeof createAccountEndpoint.req>> = {
    req: TReq;
} & RequestMeta;

const createAccountCall = (input: CreateAccountRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(createAccountEndpoint, undefined, { expectedScope });
    return call(input);
};
type CreateAccountResponse = Awaited<ReturnType<typeof createAccountCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type CreateAccountMutationCallOptions<
    TReq = z.infer<typeof createAccountEndpoint.req>,
    TRes = CreateAccountResponse,
> = {
    schemas?: {
        req?: (schema: typeof createAccountEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof createAccountEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/** Returns a TanStack mutation for POST /auth/account without idempotency retry behavior. */
export type CreateAccountMutationOptions<
    TReq = z.infer<typeof createAccountEndpoint.req>,
    TRes = CreateAccountResponse,
    TContext = unknown,
> = Omit<
    UseMutationOptions<TRes, DefaultError, CreateAccountRequest<TReq>, TContext>,
    "mutationFn"
> & {
    call?: CreateAccountMutationCallOptions<TReq, TRes>;
};

export function useCreateAccountMutation<
    TReq = z.infer<typeof createAccountEndpoint.req>,
    TRes = CreateAccountResponse,
    TContext = unknown,
>(options?: CreateAccountMutationOptions<TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (input: CreateAccountRequest<TReq>, expectedScope: ScopeContextBinding) => {
        const call = createCall<undefined, undefined, TReq, TRes>(
            {
                ...createAccountEndpoint,
                req: (callOptions?.schemas?.req?.(createAccountEndpoint.req) ??
                    createAccountEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(createAccountEndpoint.res) ??
                    createAccountEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call(input as unknown as Parameters<typeof call>[0]) as Promise<TRes>;
    };

    return useScopeAwareMutation<TRes, CreateAccountRequest<TReq>, DefaultError, TContext>(
        (input, expectedScope) => execute(input, expectedScope),
        { ...mutationOptions, mutationKey: ["POST", "/auth/account"] },
        createAccountEndpoint,
    );
}

const delEndpoint = spellbook("/tasks/{id}").DELETE();

export type DelRequest<TParams = z.infer<typeof delEndpoint.p>> = {
    params: TParams;
} & RequestMeta;

const delCall = (input: DelRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(delEndpoint, undefined, { expectedScope });
    return call(input);
};
type DelResponse = Awaited<ReturnType<typeof delCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type DelMutationCallOptions<TParams = z.infer<typeof delEndpoint.p>> = {
    schemas?: {
        params?: (schema: typeof delEndpoint.p) => z.ZodType<TParams>;
    };
};
/**
 * Returns an idempotent TanStack mutation for DELETE /tasks/{id}.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type DelMutationOptions<
    TParams = z.infer<typeof delEndpoint.p>,
    TContext = unknown,
> = IdempotentMutationOptions<DelResponse, DelRequest<TParams>, DefaultError, TContext> & {
    call?: DelMutationCallOptions<TParams>;
};

export function useDelMutation<TParams = z.infer<typeof delEndpoint.p>, TContext = unknown>(
    options?: DelMutationOptions<TParams, TContext>,
) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (input: DelRequest<TParams>, expectedScope: ScopeContextBinding) => {
        const call = createCall<TParams, undefined, undefined, undefined>(
            {
                ...delEndpoint,
                p: (callOptions?.schemas?.params?.(delEndpoint.p) ??
                    delEndpoint.p) as z.ZodType<TParams>,
            },
            undefined,
            {
                expectedScope,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<DelResponse>;
    };

    return useIdempotentMutation<DelResponse, DelRequest<TParams>, DefaultError, TContext>(
        (input, idempotencyKey, expectedScope) =>
            execute({ ...input, idempotencyKey } as DelRequest<TParams>, expectedScope),
        {
            mutationKey: ["DELETE", "/tasks/{id}"],
            ...mutationOptions,
        },
        delEndpoint,
    );
}

const delJobDescriptionEndpoint = spellbook("/job-descriptions/{id}").DELETE();

export type DelJobDescriptionRequest<TParams = z.infer<typeof delJobDescriptionEndpoint.p>> = {
    params: TParams;
} & RequestMeta;

const delJobDescriptionCall = (
    input: DelJobDescriptionRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(delJobDescriptionEndpoint, undefined, { expectedScope });
    return call(input);
};
type DelJobDescriptionResponse = Awaited<ReturnType<typeof delJobDescriptionCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type DelJobDescriptionMutationCallOptions<
    TParams = z.infer<typeof delJobDescriptionEndpoint.p>,
> = {
    schemas?: {
        params?: (schema: typeof delJobDescriptionEndpoint.p) => z.ZodType<TParams>;
    };
};
/**
 * Returns an idempotent TanStack mutation for DELETE /job-descriptions/{id}.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type DelJobDescriptionMutationOptions<
    TParams = z.infer<typeof delJobDescriptionEndpoint.p>,
    TContext = unknown,
> = IdempotentMutationOptions<
    DelJobDescriptionResponse,
    DelJobDescriptionRequest<TParams>,
    DefaultError,
    TContext
> & {
    call?: DelJobDescriptionMutationCallOptions<TParams>;
};

export function useDelJobDescriptionMutation<
    TParams = z.infer<typeof delJobDescriptionEndpoint.p>,
    TContext = unknown,
>(options?: DelJobDescriptionMutationOptions<TParams, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: DelJobDescriptionRequest<TParams>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, undefined, undefined>(
            {
                ...delJobDescriptionEndpoint,
                p: (callOptions?.schemas?.params?.(delJobDescriptionEndpoint.p) ??
                    delJobDescriptionEndpoint.p) as z.ZodType<TParams>,
            },
            undefined,
            {
                expectedScope,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<DelJobDescriptionResponse>;
    };

    return useIdempotentMutation<
        DelJobDescriptionResponse,
        DelJobDescriptionRequest<TParams>,
        DefaultError,
        TContext
    >(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as DelJobDescriptionRequest<TParams>,
                expectedScope,
            ),
        {
            mutationKey: ["DELETE", "/job-descriptions/{id}"],
            ...mutationOptions,
        },
        delJobDescriptionEndpoint,
    );
}

const delMotiveEndpoint = spellbook("/motives/{id}").DELETE();

export type DelMotiveRequest<TParams = z.infer<typeof delMotiveEndpoint.p>> = {
    params: TParams;
} & RequestMeta;

const delMotiveCall = (input: DelMotiveRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(delMotiveEndpoint, undefined, { expectedScope });
    return call(input);
};
type DelMotiveResponse = Awaited<ReturnType<typeof delMotiveCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type DelMotiveMutationCallOptions<TParams = z.infer<typeof delMotiveEndpoint.p>> = {
    schemas?: {
        params?: (schema: typeof delMotiveEndpoint.p) => z.ZodType<TParams>;
    };
};
/**
 * Returns an idempotent TanStack mutation for DELETE /motives/{id}.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type DelMotiveMutationOptions<
    TParams = z.infer<typeof delMotiveEndpoint.p>,
    TContext = unknown,
> = IdempotentMutationOptions<
    DelMotiveResponse,
    DelMotiveRequest<TParams>,
    DefaultError,
    TContext
> & {
    call?: DelMotiveMutationCallOptions<TParams>;
};

export function useDelMotiveMutation<
    TParams = z.infer<typeof delMotiveEndpoint.p>,
    TContext = unknown,
>(options?: DelMotiveMutationOptions<TParams, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (input: DelMotiveRequest<TParams>, expectedScope: ScopeContextBinding) => {
        const call = createCall<TParams, undefined, undefined, undefined>(
            {
                ...delMotiveEndpoint,
                p: (callOptions?.schemas?.params?.(delMotiveEndpoint.p) ??
                    delMotiveEndpoint.p) as z.ZodType<TParams>,
            },
            undefined,
            {
                expectedScope,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<DelMotiveResponse>;
    };

    return useIdempotentMutation<
        DelMotiveResponse,
        DelMotiveRequest<TParams>,
        DefaultError,
        TContext
    >(
        (input, idempotencyKey, expectedScope) =>
            execute({ ...input, idempotencyKey } as DelMotiveRequest<TParams>, expectedScope),
        {
            mutationKey: ["DELETE", "/motives/{id}"],
            ...mutationOptions,
        },
        delMotiveEndpoint,
    );
}

const deleteEndpoint = spellbook("/organisations/{organisationId}/departments/{id}").DELETE();

export type DeleteRequest<TParams = z.infer<typeof deleteEndpoint.p>> = {
    params: TParams;
} & RequestMeta;

const deleteCall = (input: DeleteRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(deleteEndpoint, undefined, { expectedScope });
    return call(input);
};
type DeleteResponse = Awaited<ReturnType<typeof deleteCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type DeleteMutationCallOptions<TParams = z.infer<typeof deleteEndpoint.p>> = {
    schemas?: {
        params?: (schema: typeof deleteEndpoint.p) => z.ZodType<TParams>;
    };
};
/**
 * Returns an idempotent TanStack mutation for DELETE /organisations/{organisationId}/departments/{id}.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type DeleteMutationOptions<
    TParams = z.infer<typeof deleteEndpoint.p>,
    TContext = unknown,
> = IdempotentMutationOptions<DeleteResponse, DeleteRequest<TParams>, DefaultError, TContext> & {
    call?: DeleteMutationCallOptions<TParams>;
};

export function useDeleteMutation<TParams = z.infer<typeof deleteEndpoint.p>, TContext = unknown>(
    options?: DeleteMutationOptions<TParams, TContext>,
) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (input: DeleteRequest<TParams>, expectedScope: ScopeContextBinding) => {
        const call = createCall<TParams, undefined, undefined, undefined>(
            {
                ...deleteEndpoint,
                p: (callOptions?.schemas?.params?.(deleteEndpoint.p) ??
                    deleteEndpoint.p) as z.ZodType<TParams>,
            },
            undefined,
            {
                expectedScope,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<DeleteResponse>;
    };

    return useIdempotentMutation<DeleteResponse, DeleteRequest<TParams>, DefaultError, TContext>(
        (input, idempotencyKey, expectedScope) =>
            execute({ ...input, idempotencyKey } as DeleteRequest<TParams>, expectedScope),
        {
            mutationKey: ["DELETE", "/organisations/{organisationId}/departments/{id}"],
            ...mutationOptions,
        },
        deleteEndpoint,
    );
}

const deleteAttendancesEndpoint = spellbook("/attendances").DELETE();

export type DeleteAttendancesRequest<TReq = z.infer<typeof deleteAttendancesEndpoint.req>> = {
    req: TReq;
} & RequestMeta;

const deleteAttendancesCall = (
    input: DeleteAttendancesRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(deleteAttendancesEndpoint, undefined, { expectedScope });
    return call(input);
};
type DeleteAttendancesResponse = Awaited<ReturnType<typeof deleteAttendancesCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type DeleteAttendancesMutationCallOptions<
    TReq = z.infer<typeof deleteAttendancesEndpoint.req>,
    TRes = DeleteAttendancesResponse,
> = {
    schemas?: {
        req?: (schema: typeof deleteAttendancesEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof deleteAttendancesEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for DELETE /attendances.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type DeleteAttendancesMutationOptions<
    TReq = z.infer<typeof deleteAttendancesEndpoint.req>,
    TRes = DeleteAttendancesResponse,
    TContext = unknown,
> = IdempotentMutationOptions<TRes, DeleteAttendancesRequest<TReq>, DefaultError, TContext> & {
    call?: DeleteAttendancesMutationCallOptions<TReq, TRes>;
};

export function useDeleteAttendancesMutation<
    TReq = z.infer<typeof deleteAttendancesEndpoint.req>,
    TRes = DeleteAttendancesResponse,
    TContext = unknown,
>(options?: DeleteAttendancesMutationOptions<TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (input: DeleteAttendancesRequest<TReq>, expectedScope: ScopeContextBinding) => {
        const call = createCall<undefined, undefined, TReq, TRes>(
            {
                ...deleteAttendancesEndpoint,
                req: (callOptions?.schemas?.req?.(deleteAttendancesEndpoint.req) ??
                    deleteAttendancesEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(deleteAttendancesEndpoint.res) ??
                    deleteAttendancesEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<TRes, DeleteAttendancesRequest<TReq>, DefaultError, TContext>(
        (input, idempotencyKey, expectedScope) =>
            execute({ ...input, idempotencyKey } as DeleteAttendancesRequest<TReq>, expectedScope),
        {
            mutationKey: ["DELETE", "/attendances"],
            ...mutationOptions,
        },
        deleteAttendancesEndpoint,
    );
}

const deleteShiftEndpoint = spellbook("/shifts/{id}").DELETE();

export type DeleteShiftRequest<
    TParams = z.infer<typeof deleteShiftEndpoint.p>,
    TQuery = z.infer<typeof deleteShiftEndpoint.q>,
> = {
    params: TParams;
    query: TQuery;
} & RequestMeta;

const deleteShiftCall = (input: DeleteShiftRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(deleteShiftEndpoint, undefined, { expectedScope });
    return call(input);
};
type DeleteShiftResponse = Awaited<ReturnType<typeof deleteShiftCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type DeleteShiftMutationCallOptions<
    TParams = z.infer<typeof deleteShiftEndpoint.p>,
    TQuery = z.infer<typeof deleteShiftEndpoint.q>,
    TRes = DeleteShiftResponse,
> = {
    schemas?: {
        params?: (schema: typeof deleteShiftEndpoint.p) => z.ZodType<TParams>;
        query?: (schema: typeof deleteShiftEndpoint.q) => z.ZodType<TQuery>;
        res?: (schema: typeof deleteShiftEndpoint.res) => z.ZodType<TRes>;
    };
};
/**
 * Returns an idempotent TanStack mutation for DELETE /shifts/{id}.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type DeleteShiftMutationOptions<
    TParams = z.infer<typeof deleteShiftEndpoint.p>,
    TQuery = z.infer<typeof deleteShiftEndpoint.q>,
    TRes = DeleteShiftResponse,
    TContext = unknown,
> = IdempotentMutationOptions<TRes, DeleteShiftRequest<TParams, TQuery>, DefaultError, TContext> & {
    call?: DeleteShiftMutationCallOptions<TParams, TQuery, TRes>;
};

export function useDeleteShiftMutation<
    TParams = z.infer<typeof deleteShiftEndpoint.p>,
    TQuery = z.infer<typeof deleteShiftEndpoint.q>,
    TRes = DeleteShiftResponse,
    TContext = unknown,
>(options?: DeleteShiftMutationOptions<TParams, TQuery, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: DeleteShiftRequest<TParams, TQuery>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, TQuery, undefined, TRes>(
            {
                ...deleteShiftEndpoint,
                p: (callOptions?.schemas?.params?.(deleteShiftEndpoint.p) ??
                    deleteShiftEndpoint.p) as z.ZodType<TParams>,
                q: (callOptions?.schemas?.query?.(deleteShiftEndpoint.q) ??
                    deleteShiftEndpoint.q) as z.ZodType<TQuery>,
                res: (callOptions?.schemas?.res?.(deleteShiftEndpoint.res) ??
                    deleteShiftEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<TRes, DeleteShiftRequest<TParams, TQuery>, DefaultError, TContext>(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as DeleteShiftRequest<TParams, TQuery>,
                expectedScope,
            ),
        {
            mutationKey: ["DELETE", "/shifts/{id}"],
            ...mutationOptions,
        },
        deleteShiftEndpoint,
    );
}

const deleteShiftsEndpoint = spellbook("/shifts").DELETE();

export type DeleteShiftsRequest<TReq = z.infer<typeof deleteShiftsEndpoint.req>> = {
    req: TReq;
} & RequestMeta;

const deleteShiftsCall = (input: DeleteShiftsRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(deleteShiftsEndpoint, undefined, { expectedScope });
    return call(input);
};
type DeleteShiftsResponse = Awaited<ReturnType<typeof deleteShiftsCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type DeleteShiftsMutationCallOptions<
    TReq = z.infer<typeof deleteShiftsEndpoint.req>,
    TRes = DeleteShiftsResponse,
> = {
    schemas?: {
        req?: (schema: typeof deleteShiftsEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof deleteShiftsEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for DELETE /shifts.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type DeleteShiftsMutationOptions<
    TReq = z.infer<typeof deleteShiftsEndpoint.req>,
    TRes = DeleteShiftsResponse,
    TContext = unknown,
> = IdempotentMutationOptions<TRes, DeleteShiftsRequest<TReq>, DefaultError, TContext> & {
    call?: DeleteShiftsMutationCallOptions<TReq, TRes>;
};

export function useDeleteShiftsMutation<
    TReq = z.infer<typeof deleteShiftsEndpoint.req>,
    TRes = DeleteShiftsResponse,
    TContext = unknown,
>(options?: DeleteShiftsMutationOptions<TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (input: DeleteShiftsRequest<TReq>, expectedScope: ScopeContextBinding) => {
        const call = createCall<undefined, undefined, TReq, TRes>(
            {
                ...deleteShiftsEndpoint,
                req: (callOptions?.schemas?.req?.(deleteShiftsEndpoint.req) ??
                    deleteShiftsEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(deleteShiftsEndpoint.res) ??
                    deleteShiftsEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<TRes, DeleteShiftsRequest<TReq>, DefaultError, TContext>(
        (input, idempotencyKey, expectedScope) =>
            execute({ ...input, idempotencyKey } as DeleteShiftsRequest<TReq>, expectedScope),
        {
            mutationKey: ["DELETE", "/shifts"],
            ...mutationOptions,
        },
        deleteShiftsEndpoint,
    );
}

const deleteUserEndpoint = spellbook("/me/organisations/users/{id}").DELETE();

export type DeleteUserRequest<TParams = z.infer<typeof deleteUserEndpoint.p>> = {
    params: TParams;
} & RequestMeta;

const deleteUserCall = (input: DeleteUserRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(deleteUserEndpoint, undefined, { expectedScope });
    return call(input);
};
type DeleteUserResponse = Awaited<ReturnType<typeof deleteUserCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type DeleteUserMutationCallOptions<
    TParams = z.infer<typeof deleteUserEndpoint.p>,
    TRes = DeleteUserResponse,
> = {
    schemas?: {
        params?: (schema: typeof deleteUserEndpoint.p) => z.ZodType<TParams>;
        res?: (schema: typeof deleteUserEndpoint.res) => z.ZodType<TRes>;
    };
};
/**
 * Returns an idempotent TanStack mutation for DELETE /me/organisations/users/{id}.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type DeleteUserMutationOptions<
    TParams = z.infer<typeof deleteUserEndpoint.p>,
    TRes = DeleteUserResponse,
    TContext = unknown,
> = IdempotentMutationOptions<TRes, DeleteUserRequest<TParams>, DefaultError, TContext> & {
    call?: DeleteUserMutationCallOptions<TParams, TRes>;
};

export function useDeleteUserMutation<
    TParams = z.infer<typeof deleteUserEndpoint.p>,
    TRes = DeleteUserResponse,
    TContext = unknown,
>(options?: DeleteUserMutationOptions<TParams, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (input: DeleteUserRequest<TParams>, expectedScope: ScopeContextBinding) => {
        const call = createCall<TParams, undefined, undefined, TRes>(
            {
                ...deleteUserEndpoint,
                p: (callOptions?.schemas?.params?.(deleteUserEndpoint.p) ??
                    deleteUserEndpoint.p) as z.ZodType<TParams>,
                res: (callOptions?.schemas?.res?.(deleteUserEndpoint.res) ??
                    deleteUserEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<TRes, DeleteUserRequest<TParams>, DefaultError, TContext>(
        (input, idempotencyKey, expectedScope) =>
            execute({ ...input, idempotencyKey } as DeleteUserRequest<TParams>, expectedScope),
        {
            mutationKey: ["DELETE", "/me/organisations/users/{id}"],
            ...mutationOptions,
        },
        deleteUserEndpoint,
    );
}

const departmentGetEndpoint = spellbook("/organisations/{organisationId}/departments/{id}").GET();

export type DepartmentGetRequest<TParams = z.infer<typeof departmentGetEndpoint.p>> = {
    params: TParams;
} & RequestMeta;

const departmentGetCall = (input: DepartmentGetRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(departmentGetEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /organisations/{organisationId}/departments/{id}. */
export function departmentGetQueryOptions<
    TSelected = Awaited<ReturnType<typeof departmentGetCall>>,
>(
    input: DepartmentGetRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof departmentGetCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: departmentGetEndpoint,
        requestContext: input.requestContext,
        queryKey: [
            "GET",
            "/organisations/{organisationId}/departments/{id}",
            input.params,
        ] as const,
        queryFn: ({ signal }, expectedScope) =>
            departmentGetCall({ ...input, signal }, expectedScope),
        options,
    });
}

const departmentListEndpoint = spellbook("/organisations/{organisationId}/departments").GET();

export type DepartmentListRequest<
    TParams = z.infer<typeof departmentListEndpoint.p>,
    TQuery = z.infer<typeof departmentListEndpoint.q>,
> = {
    params: TParams;
    query: TQuery;
} & RequestMeta;

const departmentListCall = (input: DepartmentListRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(departmentListEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /organisations/{organisationId}/departments. */
export function departmentListQueryOptions<
    TSelected = Awaited<ReturnType<typeof departmentListCall>>,
>(
    input: DepartmentListRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof departmentListCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: departmentListEndpoint,
        requestContext: input.requestContext,
        queryKey: [
            "GET",
            "/organisations/{organisationId}/departments",
            input.params,
            input.query,
        ] as const,
        queryFn: ({ signal }, expectedScope) =>
            departmentListCall({ ...input, signal }, expectedScope),
        options,
    });
}

const downloadHandlerEndpoint = spellbook("/download/{type}/{id}").GET();

export type DownloadHandlerRequest<TParams = z.infer<typeof downloadHandlerEndpoint.p>> = {
    params: TParams;
} & RequestMeta;

const downloadHandlerCall = (
    input: DownloadHandlerRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(downloadHandlerEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /download/{type}/{id}. */
export function downloadHandlerQueryOptions<
    TSelected = Awaited<ReturnType<typeof downloadHandlerCall>>,
>(
    input: DownloadHandlerRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof downloadHandlerCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: downloadHandlerEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/download/{type}/{id}", input.params] as const,
        queryFn: ({ signal }, expectedScope) =>
            downloadHandlerCall({ ...input, signal }, expectedScope),
        options,
    });
}

const downloadInvoicesEndpoint = spellbook("/v1/{service}/download").GET();

export type DownloadInvoicesRequest<
    TParams = z.infer<typeof downloadInvoicesEndpoint.p>,
    TQuery = z.infer<typeof downloadInvoicesEndpoint.q>,
> = {
    params: TParams;
    query: TQuery;
} & RequestMeta;

const downloadInvoicesCall = (
    input: DownloadInvoicesRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(downloadInvoicesEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /v1/{service}/download. */
export function downloadInvoicesQueryOptions<
    TSelected = Awaited<ReturnType<typeof downloadInvoicesCall>>,
>(
    input: DownloadInvoicesRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof downloadInvoicesCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: downloadInvoicesEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/v1/{service}/download", input.params, input.query] as const,
        queryFn: ({ signal }, expectedScope) =>
            downloadInvoicesCall({ ...input, signal }, expectedScope),
        options,
    });
}

const getAttendanceDisputeEndpoint = spellbook(
    "/v1/attendances/{attendanceID}/dispute/{disputeID}",
).GET();

export type GetAttendanceDisputeRequest<TParams = z.infer<typeof getAttendanceDisputeEndpoint.p>> =
    {
        params: TParams;
    } & RequestMeta;

const getAttendanceDisputeCall = (
    input: GetAttendanceDisputeRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(getAttendanceDisputeEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /v1/attendances/{attendanceID}/dispute/{disputeID}. */
export function getAttendanceDisputeQueryOptions<
    TSelected = Awaited<ReturnType<typeof getAttendanceDisputeCall>>,
>(
    input: GetAttendanceDisputeRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof getAttendanceDisputeCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: getAttendanceDisputeEndpoint,
        requestContext: input.requestContext,
        queryKey: [
            "GET",
            "/v1/attendances/{attendanceID}/dispute/{disputeID}",
            input.params,
        ] as const,
        queryFn: ({ signal }, expectedScope) =>
            getAttendanceDisputeCall({ ...input, signal }, expectedScope),
        options,
    });
}

const getAttendancesEndpoint = spellbook("/attendances").GET();

export type GetAttendancesRequest<TQuery = z.infer<typeof getAttendancesEndpoint.q>> = {
    query: TQuery;
} & RequestMeta;

const getAttendancesCall = (input: GetAttendancesRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(getAttendancesEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /attendances. */
export function getAttendancesQueryOptions<
    TSelected = Awaited<ReturnType<typeof getAttendancesCall>>,
>(
    input: GetAttendancesRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof getAttendancesCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: getAttendancesEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/attendances", input.query] as const,
        queryFn: ({ signal }, expectedScope) =>
            getAttendancesCall({ ...input, signal }, expectedScope),
        options,
    });
}

const getAttendancesToValidateCountEndpoint = spellbook("/v1/attendances/to_validate/count").GET();

export type GetAttendancesToValidateCountRequest = {} & RequestMeta;

const getAttendancesToValidateCountCall = (
    input: GetAttendancesToValidateCountRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(getAttendancesToValidateCountEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /v1/attendances/to_validate/count. */
export function getAttendancesToValidateCountQueryOptions<
    TSelected = Awaited<ReturnType<typeof getAttendancesToValidateCountCall>>,
>(
    input: GetAttendancesToValidateCountRequest = {} as GetAttendancesToValidateCountRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof getAttendancesToValidateCountCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: getAttendancesToValidateCountEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/v1/attendances/to_validate/count"] as const,
        queryFn: ({ signal }, expectedScope) =>
            getAttendancesToValidateCountCall({ ...input, signal }, expectedScope),
        options,
    });
}

const getChildrenEndpoint = spellbook("/organisations/{organisationId}/children").GET();

export type GetChildrenRequest<TParams = z.infer<typeof getChildrenEndpoint.p>> = {
    params: TParams;
} & RequestMeta;

const getChildrenCall = (input: GetChildrenRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(getChildrenEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /organisations/{organisationId}/children. */
export function getChildrenQueryOptions<TSelected = Awaited<ReturnType<typeof getChildrenCall>>>(
    input: GetChildrenRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof getChildrenCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: getChildrenEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/organisations/{organisationId}/children", input.params] as const,
        queryFn: ({ signal }, expectedScope) =>
            getChildrenCall({ ...input, signal }, expectedScope),
        options,
    });
}

const getCompletionStatusEndpoint = spellbook("/me/organisation/completion").GET();

export type GetCompletionStatusRequest = {} & RequestMeta;

const getCompletionStatusCall = (
    input: GetCompletionStatusRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(getCompletionStatusEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /me/organisation/completion. */
export function getCompletionStatusQueryOptions<
    TSelected = Awaited<ReturnType<typeof getCompletionStatusCall>>,
>(
    input: GetCompletionStatusRequest = {} as GetCompletionStatusRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof getCompletionStatusCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: getCompletionStatusEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/me/organisation/completion"] as const,
        queryFn: ({ signal }, expectedScope) =>
            getCompletionStatusCall({ ...input, signal }, expectedScope),
        options,
    });
}

const getCountUnsignedDocumentsByOrganisationEndpoint = spellbook(
    "/organisations/{organisationId}/documents/unsigned/count",
).GET();

export type GetCountUnsignedDocumentsByOrganisationRequest<
    TParams = z.infer<typeof getCountUnsignedDocumentsByOrganisationEndpoint.p>,
> = {
    params: TParams;
} & RequestMeta;

const getCountUnsignedDocumentsByOrganisationCall = (
    input: GetCountUnsignedDocumentsByOrganisationRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(getCountUnsignedDocumentsByOrganisationEndpoint, undefined, {
        expectedScope,
    });
    return call(input);
};
/** Returns TanStack Query options for GET /organisations/{organisationId}/documents/unsigned/count. */
export function getCountUnsignedDocumentsByOrganisationQueryOptions<
    TSelected = Awaited<ReturnType<typeof getCountUnsignedDocumentsByOrganisationCall>>,
>(
    input: GetCountUnsignedDocumentsByOrganisationRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof getCountUnsignedDocumentsByOrganisationCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: getCountUnsignedDocumentsByOrganisationEndpoint,
        requestContext: input.requestContext,
        queryKey: [
            "GET",
            "/organisations/{organisationId}/documents/unsigned/count",
            input.params,
        ] as const,
        queryFn: ({ signal }, expectedScope) =>
            getCountUnsignedDocumentsByOrganisationCall({ ...input, signal }, expectedScope),
        options,
    });
}

const getFeaturesPolicyEndpoint = spellbook("/me/organisations/features-policy").GET();

export type GetFeaturesPolicyRequest = {} & RequestMeta;

const getFeaturesPolicyCall = (
    input: GetFeaturesPolicyRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(getFeaturesPolicyEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /me/organisations/features-policy. */
export function getFeaturesPolicyQueryOptions<
    TSelected = Awaited<ReturnType<typeof getFeaturesPolicyCall>>,
>(
    input: GetFeaturesPolicyRequest = {} as GetFeaturesPolicyRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof getFeaturesPolicyCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: getFeaturesPolicyEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/me/organisations/features-policy"] as const,
        queryFn: ({ signal }, expectedScope) =>
            getFeaturesPolicyCall({ ...input, signal }, expectedScope),
        options,
    });
}

const getFeedbackEndpoint = spellbook("/v1/feedback/{feedbackId}").GET();

export type GetFeedbackRequest<TParams = z.infer<typeof getFeedbackEndpoint.p>> = {
    params: TParams;
} & RequestMeta;

const getFeedbackCall = (input: GetFeedbackRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(getFeedbackEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /v1/feedback/{feedbackId}. */
export function getFeedbackQueryOptions<TSelected = Awaited<ReturnType<typeof getFeedbackCall>>>(
    input: GetFeedbackRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof getFeedbackCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: getFeedbackEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/v1/feedback/{feedbackId}", input.params] as const,
        queryFn: ({ signal }, expectedScope) =>
            getFeedbackCall({ ...input, signal }, expectedScope),
        options,
    });
}

const getFlexiblePlanningEndpoint = spellbook("/tasks/{id}/flexible-planning").GET();

export type GetFlexiblePlanningRequest<TParams = z.infer<typeof getFlexiblePlanningEndpoint.p>> = {
    params: TParams;
} & RequestMeta;

const getFlexiblePlanningCall = (
    input: GetFlexiblePlanningRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(getFlexiblePlanningEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /tasks/{id}/flexible-planning. */
export function getFlexiblePlanningQueryOptions<
    TSelected = Awaited<ReturnType<typeof getFlexiblePlanningCall>>,
>(
    input: GetFlexiblePlanningRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof getFlexiblePlanningCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: getFlexiblePlanningEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/tasks/{id}/flexible-planning", input.params] as const,
        queryFn: ({ signal }, expectedScope) =>
            getFlexiblePlanningCall({ ...input, signal }, expectedScope),
        options,
    });
}

const getFlexiblePlanningQuotationEndpoint = spellbook(
    "/tasks/{id}/flexible-planning/quotation",
).GET();

export type GetFlexiblePlanningQuotationRequest<
    TParams = z.infer<typeof getFlexiblePlanningQuotationEndpoint.p>,
    TQuery = z.infer<typeof getFlexiblePlanningQuotationEndpoint.q>,
> = {
    params: TParams;
    query: TQuery;
} & RequestMeta;

const getFlexiblePlanningQuotationCall = (
    input: GetFlexiblePlanningQuotationRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(getFlexiblePlanningQuotationEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /tasks/{id}/flexible-planning/quotation. */
export function getFlexiblePlanningQuotationQueryOptions<
    TSelected = Awaited<ReturnType<typeof getFlexiblePlanningQuotationCall>>,
>(
    input: GetFlexiblePlanningQuotationRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof getFlexiblePlanningQuotationCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: getFlexiblePlanningQuotationEndpoint,
        requestContext: input.requestContext,
        queryKey: [
            "GET",
            "/tasks/{id}/flexible-planning/quotation",
            input.params,
            input.query,
        ] as const,
        queryFn: ({ signal }, expectedScope) =>
            getFlexiblePlanningQuotationCall({ ...input, signal }, expectedScope),
        options,
    });
}

const getGuestTaskEndpoint = spellbook("/guest/tasks/{id}").GET();

export type GetGuestTaskRequest<
    TParams = z.infer<typeof getGuestTaskEndpoint.p>,
    TQuery = z.infer<typeof getGuestTaskEndpoint.q>,
> = {
    params: TParams;
    query: TQuery;
} & RequestMeta;

const getGuestTaskCall = (input: GetGuestTaskRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(getGuestTaskEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /guest/tasks/{id}. */
export function getGuestTaskQueryOptions<TSelected = Awaited<ReturnType<typeof getGuestTaskCall>>>(
    input: GetGuestTaskRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof getGuestTaskCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: getGuestTaskEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/guest/tasks/{id}", input.params, input.query] as const,
        queryFn: ({ signal }, expectedScope) =>
            getGuestTaskCall({ ...input, signal }, expectedScope),
        options,
    });
}

const getLegalInformationsEndpoint = spellbook(
    "/organisations/{organisationId}/legal-informations",
).GET();

export type GetLegalInformationsRequest<TParams = z.infer<typeof getLegalInformationsEndpoint.p>> =
    {
        params: TParams;
    } & RequestMeta;

const getLegalInformationsCall = (
    input: GetLegalInformationsRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(getLegalInformationsEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /organisations/{organisationId}/legal-informations. */
export function getLegalInformationsQueryOptions<
    TSelected = Awaited<ReturnType<typeof getLegalInformationsCall>>,
>(
    input: GetLegalInformationsRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof getLegalInformationsCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: getLegalInformationsEndpoint,
        requestContext: input.requestContext,
        queryKey: [
            "GET",
            "/organisations/{organisationId}/legal-informations",
            input.params,
        ] as const,
        queryFn: ({ signal }, expectedScope) =>
            getLegalInformationsCall({ ...input, signal }, expectedScope),
        options,
    });
}

const getMeOrganisationsDocumentsEndpoint = spellbook("/me/organisations/documents").GET();

export type GetMeOrganisationsDocumentsRequest<
    TQuery = z.infer<typeof getMeOrganisationsDocumentsEndpoint.q>,
> = {
    query: TQuery;
} & RequestMeta;

const getMeOrganisationsDocumentsCall = (
    input: GetMeOrganisationsDocumentsRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(getMeOrganisationsDocumentsEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /me/organisations/documents. */
export function getMeOrganisationsDocumentsQueryOptions<
    TSelected = Awaited<ReturnType<typeof getMeOrganisationsDocumentsCall>>,
>(
    input: GetMeOrganisationsDocumentsRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof getMeOrganisationsDocumentsCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: getMeOrganisationsDocumentsEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/me/organisations/documents", input.query] as const,
        queryFn: ({ signal }, expectedScope) =>
            getMeOrganisationsDocumentsCall({ ...input, signal }, expectedScope),
        options,
    });
}

const getOrganisationEndpoint = spellbook("/v1/organisations").GET();

export type GetOrganisationRequest = {} & RequestMeta;

const getOrganisationCall = (
    input: GetOrganisationRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(getOrganisationEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /v1/organisations. */
export function getOrganisationQueryOptions<
    TSelected = Awaited<ReturnType<typeof getOrganisationCall>>,
>(
    input: GetOrganisationRequest = {} as GetOrganisationRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof getOrganisationCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: getOrganisationEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/v1/organisations"] as const,
        queryFn: ({ signal }, expectedScope) =>
            getOrganisationCall({ ...input, signal }, expectedScope),
        options,
    });
}

const getOrganisationProfileEndpoint = spellbook("/me/organisations/profile").GET();

export type GetOrganisationProfileRequest = {} & RequestMeta;

const getOrganisationProfileCall = (
    input: GetOrganisationProfileRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(getOrganisationProfileEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /me/organisations/profile. */
export function getOrganisationProfileQueryOptions<
    TSelected = Awaited<ReturnType<typeof getOrganisationProfileCall>>,
>(
    input: GetOrganisationProfileRequest = {} as GetOrganisationProfileRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof getOrganisationProfileCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: getOrganisationProfileEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/me/organisations/profile"] as const,
        queryFn: ({ signal }, expectedScope) =>
            getOrganisationProfileCall({ ...input, signal }, expectedScope),
        options,
    });
}

const getOrganisationsByOrganisationIdDocumentsEndpoint = spellbook(
    "/organisations/{organisationId}/documents",
).GET();

export type GetOrganisationsByOrganisationIdDocumentsRequest<
    TParams = z.infer<typeof getOrganisationsByOrganisationIdDocumentsEndpoint.p>,
    TQuery = z.infer<typeof getOrganisationsByOrganisationIdDocumentsEndpoint.q>,
> = {
    params: TParams;
    query: TQuery;
} & RequestMeta;

const getOrganisationsByOrganisationIdDocumentsCall = (
    input: GetOrganisationsByOrganisationIdDocumentsRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(getOrganisationsByOrganisationIdDocumentsEndpoint, undefined, {
        expectedScope,
    });
    return call(input);
};
/** Returns TanStack Query options for GET /organisations/{organisationId}/documents. */
export function getOrganisationsByOrganisationIdDocumentsQueryOptions<
    TSelected = Awaited<ReturnType<typeof getOrganisationsByOrganisationIdDocumentsCall>>,
>(
    input: GetOrganisationsByOrganisationIdDocumentsRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof getOrganisationsByOrganisationIdDocumentsCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: getOrganisationsByOrganisationIdDocumentsEndpoint,
        requestContext: input.requestContext,
        queryKey: [
            "GET",
            "/organisations/{organisationId}/documents",
            input.params,
            input.query,
        ] as const,
        queryFn: ({ signal }, expectedScope) =>
            getOrganisationsByOrganisationIdDocumentsCall({ ...input, signal }, expectedScope),
        options,
    });
}

const getPaymentInformationsEndpoint = spellbook(
    "/organisations/{organisationId}/payment-informations",
).GET();

export type GetPaymentInformationsRequest<
    TParams = z.infer<typeof getPaymentInformationsEndpoint.p>,
> = {
    params: TParams;
} & RequestMeta;

const getPaymentInformationsCall = (
    input: GetPaymentInformationsRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(getPaymentInformationsEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /organisations/{organisationId}/payment-informations. */
export function getPaymentInformationsQueryOptions<
    TSelected = Awaited<ReturnType<typeof getPaymentInformationsCall>>,
>(
    input: GetPaymentInformationsRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof getPaymentInformationsCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: getPaymentInformationsEndpoint,
        requestContext: input.requestContext,
        queryKey: [
            "GET",
            "/organisations/{organisationId}/payment-informations",
            input.params,
        ] as const,
        queryFn: ({ signal }, expectedScope) =>
            getPaymentInformationsCall({ ...input, signal }, expectedScope),
        options,
    });
}

const getPlanningEndpoint = spellbook("/v1/planning").GET();

export type GetPlanningRequest<TQuery = z.infer<typeof getPlanningEndpoint.q>> = {
    query: TQuery;
} & RequestMeta;

const getPlanningCall = (input: GetPlanningRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(getPlanningEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /v1/planning. */
export function getPlanningQueryOptions<TSelected = Awaited<ReturnType<typeof getPlanningCall>>>(
    input: GetPlanningRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof getPlanningCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: getPlanningEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/v1/planning", input.query] as const,
        queryFn: ({ signal }, expectedScope) =>
            getPlanningCall({ ...input, signal }, expectedScope),
        options,
    });
}

const getQuotationEndpoint = spellbook("/tasks/{id}/quotation").GET();

export type GetQuotationRequest<TParams = z.infer<typeof getQuotationEndpoint.p>> = {
    params: TParams;
} & RequestMeta;

const getQuotationCall = (input: GetQuotationRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(getQuotationEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /tasks/{id}/quotation. */
export function getQuotationQueryOptions<TSelected = Awaited<ReturnType<typeof getQuotationCall>>>(
    input: GetQuotationRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof getQuotationCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: getQuotationEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/tasks/{id}/quotation", input.params] as const,
        queryFn: ({ signal }, expectedScope) =>
            getQuotationCall({ ...input, signal }, expectedScope),
        options,
    });
}

const getRemunerationPolicyEndpoint = spellbook(
    "/organisations/{organisationId}/remuneration-policy",
).GET();

export type GetRemunerationPolicyRequest<
    TParams = z.infer<typeof getRemunerationPolicyEndpoint.p>,
> = {
    params: TParams;
} & RequestMeta;

const getRemunerationPolicyCall = (
    input: GetRemunerationPolicyRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(getRemunerationPolicyEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /organisations/{organisationId}/remuneration-policy. */
export function getRemunerationPolicyQueryOptions<
    TSelected = Awaited<ReturnType<typeof getRemunerationPolicyCall>>,
>(
    input: GetRemunerationPolicyRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof getRemunerationPolicyCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: getRemunerationPolicyEndpoint,
        requestContext: input.requestContext,
        queryKey: [
            "GET",
            "/organisations/{organisationId}/remuneration-policy",
            input.params,
        ] as const,
        queryFn: ({ signal }, expectedScope) =>
            getRemunerationPolicyCall({ ...input, signal }, expectedScope),
        options,
    });
}

const getRemunerationPolicySettingsEndpoint = spellbook(
    "/organisations/{organisationId}/remuneration-policy-settings",
).GET();

export type GetRemunerationPolicySettingsRequest<
    TParams = z.infer<typeof getRemunerationPolicySettingsEndpoint.p>,
> = {
    params: TParams;
} & RequestMeta;

const getRemunerationPolicySettingsCall = (
    input: GetRemunerationPolicySettingsRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(getRemunerationPolicySettingsEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /organisations/{organisationId}/remuneration-policy-settings. */
export function getRemunerationPolicySettingsQueryOptions<
    TSelected = Awaited<ReturnType<typeof getRemunerationPolicySettingsCall>>,
>(
    input: GetRemunerationPolicySettingsRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof getRemunerationPolicySettingsCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: getRemunerationPolicySettingsEndpoint,
        requestContext: input.requestContext,
        queryKey: [
            "GET",
            "/organisations/{organisationId}/remuneration-policy-settings",
            input.params,
        ] as const,
        queryFn: ({ signal }, expectedScope) =>
            getRemunerationPolicySettingsCall({ ...input, signal }, expectedScope),
        options,
    });
}

const getShiftRecurrenceEndpoint = spellbook("/shift-recurrences/{id}").GET();

export type GetShiftRecurrenceRequest<TParams = z.infer<typeof getShiftRecurrenceEndpoint.p>> = {
    params: TParams;
} & RequestMeta;

const getShiftRecurrenceCall = (
    input: GetShiftRecurrenceRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(getShiftRecurrenceEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /shift-recurrences/{id}. */
export function getShiftRecurrenceQueryOptions<
    TSelected = Awaited<ReturnType<typeof getShiftRecurrenceCall>>,
>(
    input: GetShiftRecurrenceRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof getShiftRecurrenceCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: getShiftRecurrenceEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/shift-recurrences/{id}", input.params] as const,
        queryFn: ({ signal }, expectedScope) =>
            getShiftRecurrenceCall({ ...input, signal }, expectedScope),
        options,
    });
}

const getSiderEndpoint = spellbook("/siders/{siderId}").GET();

export type GetSiderRequest<
    TParams = z.infer<typeof getSiderEndpoint.p>,
    TQuery = z.infer<typeof getSiderEndpoint.q>,
> = {
    params: TParams;
    query: TQuery;
} & RequestMeta;

const getSiderCall = (input: GetSiderRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(getSiderEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /siders/{siderId}. */
export function getSiderQueryOptions<TSelected = Awaited<ReturnType<typeof getSiderCall>>>(
    input: GetSiderRequest,
    options?: ScopeQueryOptions<Awaited<ReturnType<typeof getSiderCall>>, DefaultError, TSelected>,
) {
    return createScopeQueryOptions({
        operation: getSiderEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/siders/{siderId}", input.params, input.query] as const,
        queryFn: ({ signal }, expectedScope) => getSiderCall({ ...input, signal }, expectedScope),
        options,
    });
}

const getVMSConfigurationEndpoint = spellbook(
    "/organisations/{organisationId}/vms-configuration",
).GET();

export type GetVMSConfigurationRequest<TParams = z.infer<typeof getVMSConfigurationEndpoint.p>> = {
    params: TParams;
} & RequestMeta;

const getVMSConfigurationCall = (
    input: GetVMSConfigurationRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(getVMSConfigurationEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /organisations/{organisationId}/vms-configuration. */
export function getVMSConfigurationQueryOptions<
    TSelected = Awaited<ReturnType<typeof getVMSConfigurationCall>>,
>(
    input: GetVMSConfigurationRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof getVMSConfigurationCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: getVMSConfigurationEndpoint,
        requestContext: input.requestContext,
        queryKey: [
            "GET",
            "/organisations/{organisationId}/vms-configuration",
            input.params,
        ] as const,
        queryFn: ({ signal }, expectedScope) =>
            getVMSConfigurationCall({ ...input, signal }, expectedScope),
        options,
    });
}

const googleActivateUserEndpoint = spellbook("/auth/users/{id}/google/activate").PUT();

export type GoogleActivateUserRequest<
    TParams = z.infer<typeof googleActivateUserEndpoint.p>,
    TReq = z.infer<typeof googleActivateUserEndpoint.req>,
> = {
    params: TParams;
    req: TReq;
} & RequestMeta;

const googleActivateUserCall = (
    input: GoogleActivateUserRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(googleActivateUserEndpoint, undefined, { expectedScope });
    return call(input);
};
type GoogleActivateUserResponse = Awaited<ReturnType<typeof googleActivateUserCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type GoogleActivateUserMutationCallOptions<
    TParams = z.infer<typeof googleActivateUserEndpoint.p>,
    TReq = z.infer<typeof googleActivateUserEndpoint.req>,
    TRes = GoogleActivateUserResponse,
> = {
    schemas?: {
        params?: (schema: typeof googleActivateUserEndpoint.p) => z.ZodType<TParams>;
        req?: (schema: typeof googleActivateUserEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof googleActivateUserEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/** Returns a TanStack mutation for PUT /auth/users/{id}/google/activate without idempotency retry behavior. */
export type GoogleActivateUserMutationOptions<
    TParams = z.infer<typeof googleActivateUserEndpoint.p>,
    TReq = z.infer<typeof googleActivateUserEndpoint.req>,
    TRes = GoogleActivateUserResponse,
    TContext = unknown,
> = Omit<
    UseMutationOptions<TRes, DefaultError, GoogleActivateUserRequest<TParams, TReq>, TContext>,
    "mutationFn"
> & {
    call?: GoogleActivateUserMutationCallOptions<TParams, TReq, TRes>;
};

export function useGoogleActivateUserMutation<
    TParams = z.infer<typeof googleActivateUserEndpoint.p>,
    TReq = z.infer<typeof googleActivateUserEndpoint.req>,
    TRes = GoogleActivateUserResponse,
    TContext = unknown,
>(options?: GoogleActivateUserMutationOptions<TParams, TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: GoogleActivateUserRequest<TParams, TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, TReq, TRes>(
            {
                ...googleActivateUserEndpoint,
                p: (callOptions?.schemas?.params?.(googleActivateUserEndpoint.p) ??
                    googleActivateUserEndpoint.p) as z.ZodType<TParams>,
                req: (callOptions?.schemas?.req?.(googleActivateUserEndpoint.req) ??
                    googleActivateUserEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(googleActivateUserEndpoint.res) ??
                    googleActivateUserEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call(input as unknown as Parameters<typeof call>[0]) as Promise<TRes>;
    };

    return useScopeAwareMutation<
        TRes,
        GoogleActivateUserRequest<TParams, TReq>,
        DefaultError,
        TContext
    >(
        (input, expectedScope) => execute(input, expectedScope),
        { ...mutationOptions, mutationKey: ["PUT", "/auth/users/{id}/google/activate"] },
        googleActivateUserEndpoint,
    );
}

const googleLoginEndpoint = spellbook("/auth/google/token").POST();

export type GoogleLoginRequest<TReq = z.infer<typeof googleLoginEndpoint.req>> = {
    req: TReq;
} & RequestMeta;

const googleLoginCall = (input: GoogleLoginRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(googleLoginEndpoint, undefined, { expectedScope });
    return call(input);
};
type GoogleLoginResponse = Awaited<ReturnType<typeof googleLoginCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type GoogleLoginMutationCallOptions<
    TReq = z.infer<typeof googleLoginEndpoint.req>,
    TRes = GoogleLoginResponse,
> = {
    schemas?: {
        req?: (schema: typeof googleLoginEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof googleLoginEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/** Returns a TanStack mutation for POST /auth/google/token without idempotency retry behavior. */
export type GoogleLoginMutationOptions<
    TReq = z.infer<typeof googleLoginEndpoint.req>,
    TRes = GoogleLoginResponse,
    TContext = unknown,
> = Omit<
    UseMutationOptions<TRes, DefaultError, GoogleLoginRequest<TReq>, TContext>,
    "mutationFn"
> & {
    call?: GoogleLoginMutationCallOptions<TReq, TRes>;
};

export function useGoogleLoginMutation<
    TReq = z.infer<typeof googleLoginEndpoint.req>,
    TRes = GoogleLoginResponse,
    TContext = unknown,
>(options?: GoogleLoginMutationOptions<TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (input: GoogleLoginRequest<TReq>, expectedScope: ScopeContextBinding) => {
        const call = createCall<undefined, undefined, TReq, TRes>(
            {
                ...googleLoginEndpoint,
                req: (callOptions?.schemas?.req?.(googleLoginEndpoint.req) ??
                    googleLoginEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(googleLoginEndpoint.res) ??
                    googleLoginEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call(input as unknown as Parameters<typeof call>[0]) as Promise<TRes>;
    };

    return useScopeAwareMutation<TRes, GoogleLoginRequest<TReq>, DefaultError, TContext>(
        (input, expectedScope) => execute(input, expectedScope),
        { ...mutationOptions, mutationKey: ["POST", "/auth/google/token"] },
        googleLoginEndpoint,
    );
}

const googleSignUpEndpoint = spellbook("/auth/google/signup").POST();

export type GoogleSignUpRequest<TReq = z.infer<typeof googleSignUpEndpoint.req>> = {
    req: TReq;
} & RequestMeta;

const googleSignUpCall = (input: GoogleSignUpRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(googleSignUpEndpoint, undefined, { expectedScope });
    return call(input);
};
type GoogleSignUpResponse = Awaited<ReturnType<typeof googleSignUpCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type GoogleSignUpMutationCallOptions<
    TReq = z.infer<typeof googleSignUpEndpoint.req>,
    TRes = GoogleSignUpResponse,
> = {
    schemas?: {
        req?: (schema: typeof googleSignUpEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof googleSignUpEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/** Returns a TanStack mutation for POST /auth/google/signup without idempotency retry behavior. */
export type GoogleSignUpMutationOptions<
    TReq = z.infer<typeof googleSignUpEndpoint.req>,
    TRes = GoogleSignUpResponse,
    TContext = unknown,
> = Omit<
    UseMutationOptions<TRes, DefaultError, GoogleSignUpRequest<TReq>, TContext>,
    "mutationFn"
> & {
    call?: GoogleSignUpMutationCallOptions<TReq, TRes>;
};

export function useGoogleSignUpMutation<
    TReq = z.infer<typeof googleSignUpEndpoint.req>,
    TRes = GoogleSignUpResponse,
    TContext = unknown,
>(options?: GoogleSignUpMutationOptions<TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (input: GoogleSignUpRequest<TReq>, expectedScope: ScopeContextBinding) => {
        const call = createCall<undefined, undefined, TReq, TRes>(
            {
                ...googleSignUpEndpoint,
                req: (callOptions?.schemas?.req?.(googleSignUpEndpoint.req) ??
                    googleSignUpEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(googleSignUpEndpoint.res) ??
                    googleSignUpEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call(input as unknown as Parameters<typeof call>[0]) as Promise<TRes>;
    };

    return useScopeAwareMutation<TRes, GoogleSignUpRequest<TReq>, DefaultError, TContext>(
        (input, expectedScope) => execute(input, expectedScope),
        { ...mutationOptions, mutationKey: ["POST", "/auth/google/signup"] },
        googleSignUpEndpoint,
    );
}

const inviteUserEndpoint = spellbook("/me/organisations/users").POST();

export type InviteUserRequest<TReq = z.infer<typeof inviteUserEndpoint.req>> = {
    req: TReq;
} & RequestMeta;

const inviteUserCall = (input: InviteUserRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(inviteUserEndpoint, undefined, { expectedScope });
    return call(input);
};
type InviteUserResponse = Awaited<ReturnType<typeof inviteUserCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type InviteUserMutationCallOptions<
    TReq = z.infer<typeof inviteUserEndpoint.req>,
    TRes = InviteUserResponse,
> = {
    schemas?: {
        req?: (schema: typeof inviteUserEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof inviteUserEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for POST /me/organisations/users.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type InviteUserMutationOptions<
    TReq = z.infer<typeof inviteUserEndpoint.req>,
    TRes = InviteUserResponse,
    TContext = unknown,
> = IdempotentMutationOptions<TRes, InviteUserRequest<TReq>, DefaultError, TContext> & {
    call?: InviteUserMutationCallOptions<TReq, TRes>;
};

export function useInviteUserMutation<
    TReq = z.infer<typeof inviteUserEndpoint.req>,
    TRes = InviteUserResponse,
    TContext = unknown,
>(options?: InviteUserMutationOptions<TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (input: InviteUserRequest<TReq>, expectedScope: ScopeContextBinding) => {
        const call = createCall<undefined, undefined, TReq, TRes>(
            {
                ...inviteUserEndpoint,
                req: (callOptions?.schemas?.req?.(inviteUserEndpoint.req) ??
                    inviteUserEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(inviteUserEndpoint.res) ??
                    inviteUserEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<TRes, InviteUserRequest<TReq>, DefaultError, TContext>(
        (input, idempotencyKey, expectedScope) =>
            execute({ ...input, idempotencyKey } as InviteUserRequest<TReq>, expectedScope),
        {
            mutationKey: ["POST", "/me/organisations/users"],
            ...mutationOptions,
        },
        inviteUserEndpoint,
    );
}

const invoiceGetOrganisationInvoicesEndpoint = spellbook("/v1/organisations/invoices").GET();

export type InvoiceGetOrganisationInvoicesRequest<
    TQuery = z.infer<typeof invoiceGetOrganisationInvoicesEndpoint.q>,
> = {
    query: TQuery;
} & RequestMeta;

const invoiceGetOrganisationInvoicesCall = (
    input: InvoiceGetOrganisationInvoicesRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(invoiceGetOrganisationInvoicesEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /v1/organisations/invoices. */
export function invoiceGetOrganisationInvoicesQueryOptions<
    TSelected = Awaited<ReturnType<typeof invoiceGetOrganisationInvoicesCall>>,
>(
    input: InvoiceGetOrganisationInvoicesRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof invoiceGetOrganisationInvoicesCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: invoiceGetOrganisationInvoicesEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/v1/organisations/invoices", input.query] as const,
        queryFn: ({ signal }, expectedScope) =>
            invoiceGetOrganisationInvoicesCall({ ...input, signal }, expectedScope),
        options,
    });
}

const jobtypesListEndpoint = spellbook("/organisations/{organisationId}/job_types").GET();

export type JobtypesListRequest<TParams = z.infer<typeof jobtypesListEndpoint.p>> = {
    params: TParams;
} & RequestMeta;

const jobtypesListCall = (input: JobtypesListRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(jobtypesListEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /organisations/{organisationId}/job_types. */
export function jobtypesListQueryOptions<TSelected = Awaited<ReturnType<typeof jobtypesListCall>>>(
    input: JobtypesListRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof jobtypesListCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: jobtypesListEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/organisations/{organisationId}/job_types", input.params] as const,
        queryFn: ({ signal }, expectedScope) =>
            jobtypesListCall({ ...input, signal }, expectedScope),
        options,
    });
}

const listApplicantsEndpoint = spellbook("/tasks/{id}/applicants").GET();

export type ListApplicantsRequest<TParams = z.infer<typeof listApplicantsEndpoint.p>> = {
    params: TParams;
} & RequestMeta;

const listApplicantsCall = (input: ListApplicantsRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(listApplicantsEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /tasks/{id}/applicants. */
export function listApplicantsQueryOptions<
    TSelected = Awaited<ReturnType<typeof listApplicantsCall>>,
>(
    input: ListApplicantsRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof listApplicantsCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: listApplicantsEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/tasks/{id}/applicants", input.params] as const,
        queryFn: ({ signal }, expectedScope) =>
            listApplicantsCall({ ...input, signal }, expectedScope),
        options,
    });
}

const listAttendancesEndpoint = spellbook("/v2/attendances").GET();

export type ListAttendancesRequest<TQuery = z.infer<typeof listAttendancesEndpoint.q>> = {
    query: TQuery;
} & RequestMeta;

const listAttendancesCall = (
    input: ListAttendancesRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(listAttendancesEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /v2/attendances. */
export function listAttendancesQueryOptions<
    TSelected = Awaited<ReturnType<typeof listAttendancesCall>>,
>(
    input: ListAttendancesRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof listAttendancesCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: listAttendancesEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/v2/attendances", input.query] as const,
        queryFn: ({ signal }, expectedScope) =>
            listAttendancesCall({ ...input, signal }, expectedScope),
        options,
    });
}

const listCustomJobTitlesEndpoint = spellbook("/custom-job-titles").GET();

export type ListCustomJobTitlesRequest = {} & RequestMeta;

const listCustomJobTitlesCall = (
    input: ListCustomJobTitlesRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(listCustomJobTitlesEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /custom-job-titles. */
export function listCustomJobTitlesQueryOptions<
    TSelected = Awaited<ReturnType<typeof listCustomJobTitlesCall>>,
>(
    input: ListCustomJobTitlesRequest = {} as ListCustomJobTitlesRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof listCustomJobTitlesCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: listCustomJobTitlesEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/custom-job-titles"] as const,
        queryFn: ({ signal }, expectedScope) =>
            listCustomJobTitlesCall({ ...input, signal }, expectedScope),
        options,
    });
}

const listHolidaysEndpoint = spellbook("/me/organisations/holidays").GET();

export type ListHolidaysRequest<TQuery = z.infer<typeof listHolidaysEndpoint.q>> = {
    query: TQuery;
} & RequestMeta;

const listHolidaysCall = (input: ListHolidaysRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(listHolidaysEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /me/organisations/holidays. */
export function listHolidaysQueryOptions<TSelected = Awaited<ReturnType<typeof listHolidaysCall>>>(
    input: ListHolidaysRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof listHolidaysCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: listHolidaysEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/me/organisations/holidays", input.query] as const,
        queryFn: ({ signal }, expectedScope) =>
            listHolidaysCall({ ...input, signal }, expectedScope),
        options,
    });
}

const listJobDescriptionsEndpoint = spellbook("/job-descriptions").GET();

export type ListJobDescriptionsRequest = {} & RequestMeta;

const listJobDescriptionsCall = (
    input: ListJobDescriptionsRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(listJobDescriptionsEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /job-descriptions. */
export function listJobDescriptionsQueryOptions<
    TSelected = Awaited<ReturnType<typeof listJobDescriptionsCall>>,
>(
    input: ListJobDescriptionsRequest = {} as ListJobDescriptionsRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof listJobDescriptionsCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: listJobDescriptionsEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/job-descriptions"] as const,
        queryFn: ({ signal }, expectedScope) =>
            listJobDescriptionsCall({ ...input, signal }, expectedScope),
        options,
    });
}

const listJobTitleSubtasksEndpoint = spellbook("/job-descriptions/job-titles/{id}/subtasks").GET();

export type ListJobTitleSubtasksRequest<TParams = z.infer<typeof listJobTitleSubtasksEndpoint.p>> =
    {
        params: TParams;
    } & RequestMeta;

const listJobTitleSubtasksCall = (
    input: ListJobTitleSubtasksRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(listJobTitleSubtasksEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /job-descriptions/job-titles/{id}/subtasks. */
export function listJobTitleSubtasksQueryOptions<
    TSelected = Awaited<ReturnType<typeof listJobTitleSubtasksCall>>,
>(
    input: ListJobTitleSubtasksRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof listJobTitleSubtasksCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: listJobTitleSubtasksEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/job-descriptions/job-titles/{id}/subtasks", input.params] as const,
        queryFn: ({ signal }, expectedScope) =>
            listJobTitleSubtasksCall({ ...input, signal }, expectedScope),
        options,
    });
}

const listJobTitlesEndpoint = spellbook("/job-descriptions/job-titles").GET();

export type ListJobTitlesRequest = {} & RequestMeta;

const listJobTitlesCall = (input: ListJobTitlesRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(listJobTitlesEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /job-descriptions/job-titles. */
export function listJobTitlesQueryOptions<
    TSelected = Awaited<ReturnType<typeof listJobTitlesCall>>,
>(
    input: ListJobTitlesRequest = {} as ListJobTitlesRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof listJobTitlesCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: listJobTitlesEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/job-descriptions/job-titles"] as const,
        queryFn: ({ signal }, expectedScope) =>
            listJobTitlesCall({ ...input, signal }, expectedScope),
        options,
    });
}

const listLegalValuesEndpoint = spellbook("/legal-values").GET();

export type ListLegalValuesRequest = {} & RequestMeta;

const listLegalValuesCall = (
    input: ListLegalValuesRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(listLegalValuesEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /legal-values. */
export function listLegalValuesQueryOptions<
    TSelected = Awaited<ReturnType<typeof listLegalValuesCall>>,
>(
    input: ListLegalValuesRequest = {} as ListLegalValuesRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof listLegalValuesCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: listLegalValuesEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/legal-values"] as const,
        queryFn: ({ signal }, expectedScope) =>
            listLegalValuesCall({ ...input, signal }, expectedScope),
        options,
    });
}

const listLocationsEndpoint = spellbook("/organisations/{organisationId}/locations").GET();

export type ListLocationsRequest<TParams = z.infer<typeof listLocationsEndpoint.p>> = {
    params: TParams;
} & RequestMeta;

const listLocationsCall = (input: ListLocationsRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(listLocationsEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /organisations/{organisationId}/locations. */
export function listLocationsQueryOptions<
    TSelected = Awaited<ReturnType<typeof listLocationsCall>>,
>(
    input: ListLocationsRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof listLocationsCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: listLocationsEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/organisations/{organisationId}/locations", input.params] as const,
        queryFn: ({ signal }, expectedScope) =>
            listLocationsCall({ ...input, signal }, expectedScope),
        options,
    });
}

const listMotivesEndpoint = spellbook("/motives").GET();

export type ListMotivesRequest = {} & RequestMeta;

const listMotivesCall = (input: ListMotivesRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(listMotivesEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /motives. */
export function listMotivesQueryOptions<TSelected = Awaited<ReturnType<typeof listMotivesCall>>>(
    input: ListMotivesRequest = {} as ListMotivesRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof listMotivesCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: listMotivesEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/motives"] as const,
        queryFn: ({ signal }, expectedScope) =>
            listMotivesCall({ ...input, signal }, expectedScope),
        options,
    });
}

const listOptionsEndpoint = spellbook("/job-descriptions/options").GET();

export type ListOptionsRequest = {} & RequestMeta;

const listOptionsCall = (input: ListOptionsRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(listOptionsEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /job-descriptions/options. */
export function listOptionsQueryOptions<TSelected = Awaited<ReturnType<typeof listOptionsCall>>>(
    input: ListOptionsRequest = {} as ListOptionsRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof listOptionsCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: listOptionsEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/job-descriptions/options"] as const,
        queryFn: ({ signal }, expectedScope) =>
            listOptionsCall({ ...input, signal }, expectedScope),
        options,
    });
}

const listProspectSidersEndpoint = spellbook("/tasks/{id}/siders/prospects").GET();

export type ListProspectSidersRequest<TParams = z.infer<typeof listProspectSidersEndpoint.p>> = {
    params: TParams;
} & RequestMeta;

const listProspectSidersCall = (
    input: ListProspectSidersRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(listProspectSidersEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /tasks/{id}/siders/prospects. */
export function listProspectSidersQueryOptions<
    TSelected = Awaited<ReturnType<typeof listProspectSidersCall>>,
>(
    input: ListProspectSidersRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof listProspectSidersCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: listProspectSidersEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/tasks/{id}/siders/prospects", input.params] as const,
        queryFn: ({ signal }, expectedScope) =>
            listProspectSidersCall({ ...input, signal }, expectedScope),
        options,
    });
}

const listTasksNamesEndpoint = spellbook("/organisations/{organisationId}/tasks-names").GET();

export type ListTasksNamesRequest<TParams = z.infer<typeof listTasksNamesEndpoint.p>> = {
    params: TParams;
} & RequestMeta;

const listTasksNamesCall = (input: ListTasksNamesRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(listTasksNamesEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /organisations/{organisationId}/tasks-names. */
export function listTasksNamesQueryOptions<
    TSelected = Awaited<ReturnType<typeof listTasksNamesCall>>,
>(
    input: ListTasksNamesRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof listTasksNamesCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: listTasksNamesEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/organisations/{organisationId}/tasks-names", input.params] as const,
        queryFn: ({ signal }, expectedScope) =>
            listTasksNamesCall({ ...input, signal }, expectedScope),
        options,
    });
}

const listTasksV2Endpoint = spellbook("/v2/tasks").GET();

export type ListTasksV2Request<TQuery = z.infer<typeof listTasksV2Endpoint.q>> = {
    query: TQuery;
} & RequestMeta;

const listTasksV2Call = (input: ListTasksV2Request, expectedScope?: ScopeContextBinding) => {
    const call = createCall(listTasksV2Endpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /v2/tasks. */
export function listTasksV2QueryOptions<TSelected = Awaited<ReturnType<typeof listTasksV2Call>>>(
    input: ListTasksV2Request,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof listTasksV2Call>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: listTasksV2Endpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/v2/tasks", input.query] as const,
        queryFn: ({ signal }, expectedScope) =>
            listTasksV2Call({ ...input, signal }, expectedScope),
        options,
    });
}

const listUsersEndpoint = spellbook("/me/organisations/users").GET();

export type ListUsersRequest<TQuery = z.infer<typeof listUsersEndpoint.q>> = {
    query: TQuery;
} & RequestMeta;

const listUsersCall = (input: ListUsersRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(listUsersEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /me/organisations/users. */
export function listUsersQueryOptions<TSelected = Awaited<ReturnType<typeof listUsersCall>>>(
    input: ListUsersRequest,
    options?: ScopeQueryOptions<Awaited<ReturnType<typeof listUsersCall>>, DefaultError, TSelected>,
) {
    return createScopeQueryOptions({
        operation: listUsersEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/me/organisations/users", input.query] as const,
        queryFn: ({ signal }, expectedScope) => listUsersCall({ ...input, signal }, expectedScope),
        options,
    });
}

const logasEndpoint = spellbook("/users/search").GET();

export type LogasRequest<TQuery = z.infer<typeof logasEndpoint.q>> = {
    query: TQuery;
} & RequestMeta;

const logasCall = (input: LogasRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(logasEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /users/search. */
export function logasQueryOptions<TSelected = Awaited<ReturnType<typeof logasCall>>>(
    input: LogasRequest,
    options?: ScopeQueryOptions<Awaited<ReturnType<typeof logasCall>>, DefaultError, TSelected>,
) {
    return createScopeQueryOptions({
        operation: logasEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/users/search", input.query] as const,
        queryFn: ({ signal }, expectedScope) => logasCall({ ...input, signal }, expectedScope),
        options,
    });
}

const loginEndpoint = spellbook("/auth/login").POST();

export type LoginRequest<TReq = z.infer<typeof loginEndpoint.req>> = {
    req: TReq;
} & RequestMeta;

const loginCall = (input: LoginRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(loginEndpoint, undefined, { expectedScope });
    return call(input);
};
type LoginResponse = Awaited<ReturnType<typeof loginCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type LoginMutationCallOptions<
    TReq = z.infer<typeof loginEndpoint.req>,
    TRes = LoginResponse,
> = {
    schemas?: {
        req?: (schema: typeof loginEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof loginEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/** Returns a TanStack mutation for POST /auth/login without idempotency retry behavior. */
export type LoginMutationOptions<
    TReq = z.infer<typeof loginEndpoint.req>,
    TRes = LoginResponse,
    TContext = unknown,
> = Omit<UseMutationOptions<TRes, DefaultError, LoginRequest<TReq>, TContext>, "mutationFn"> & {
    call?: LoginMutationCallOptions<TReq, TRes>;
};

export function useLoginMutation<
    TReq = z.infer<typeof loginEndpoint.req>,
    TRes = LoginResponse,
    TContext = unknown,
>(options?: LoginMutationOptions<TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (input: LoginRequest<TReq>, expectedScope: ScopeContextBinding) => {
        const call = createCall<undefined, undefined, TReq, TRes>(
            {
                ...loginEndpoint,
                req: (callOptions?.schemas?.req?.(loginEndpoint.req) ??
                    loginEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(loginEndpoint.res) ??
                    loginEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call(input as unknown as Parameters<typeof call>[0]) as Promise<TRes>;
    };

    return useScopeAwareMutation<TRes, LoginRequest<TReq>, DefaultError, TContext>(
        (input, expectedScope) => execute(input, expectedScope),
        { ...mutationOptions, mutationKey: ["POST", "/auth/login"] },
        loginEndpoint,
    );
}

const meEndpoint = spellbook("/v1/users/me").GET();

export type MeRequest = {} & RequestMeta;

const meCall = (input: MeRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(meEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /v1/users/me. */
export function meQueryOptions<TSelected = Awaited<ReturnType<typeof meCall>>>(
    input: MeRequest = {} as MeRequest,
    options?: ScopeQueryOptions<Awaited<ReturnType<typeof meCall>>, DefaultError, TSelected>,
) {
    return createScopeQueryOptions({
        operation: meEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/v1/users/me"] as const,
        queryFn: ({ signal }, expectedScope) => meCall({ ...input, signal }, expectedScope),
        options,
    });
}

const organisationListSidersEndpoint = spellbook("/tasks/{id}/siders").GET();

export type OrganisationListSidersRequest<
    TParams = z.infer<typeof organisationListSidersEndpoint.p>,
    TQuery = z.infer<typeof organisationListSidersEndpoint.q>,
> = {
    params: TParams;
    query: TQuery;
} & RequestMeta;

const organisationListSidersCall = (
    input: OrganisationListSidersRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(organisationListSidersEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /tasks/{id}/siders. */
export function organisationListSidersQueryOptions<
    TSelected = Awaited<ReturnType<typeof organisationListSidersCall>>,
>(
    input: OrganisationListSidersRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof organisationListSidersCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: organisationListSidersEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/tasks/{id}/siders", input.params, input.query] as const,
        queryFn: ({ signal }, expectedScope) =>
            organisationListSidersCall({ ...input, signal }, expectedScope),
        options,
    });
}

const organisationUploadHandlerEndpoint = spellbook("/me/organisations/picture/{type}").POST();

export type OrganisationUploadHandlerRequest<
    TParams = z.infer<typeof organisationUploadHandlerEndpoint.p>,
> = {
    params: TParams;
} & RequestMeta;

const organisationUploadHandlerCall = (
    input: OrganisationUploadHandlerRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(organisationUploadHandlerEndpoint, undefined, { expectedScope });
    return call(input);
};
type OrganisationUploadHandlerResponse = Awaited<ReturnType<typeof organisationUploadHandlerCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type OrganisationUploadHandlerMutationCallOptions<
    TParams = z.infer<typeof organisationUploadHandlerEndpoint.p>,
    TRes = OrganisationUploadHandlerResponse,
> = {
    schemas?: {
        params?: (schema: typeof organisationUploadHandlerEndpoint.p) => z.ZodType<TParams>;
        res?: (schema: typeof organisationUploadHandlerEndpoint.res) => z.ZodType<TRes>;
    };
};
/** Returns a TanStack mutation for POST /me/organisations/picture/{type} without idempotency retry behavior. */
export type OrganisationUploadHandlerMutationOptions<
    TParams = z.infer<typeof organisationUploadHandlerEndpoint.p>,
    TRes = OrganisationUploadHandlerResponse,
    TContext = unknown,
> = Omit<
    UseMutationOptions<TRes, DefaultError, OrganisationUploadHandlerRequest<TParams>, TContext>,
    "mutationFn"
> & {
    call?: OrganisationUploadHandlerMutationCallOptions<TParams, TRes>;
};

export function useOrganisationUploadHandlerMutation<
    TParams = z.infer<typeof organisationUploadHandlerEndpoint.p>,
    TRes = OrganisationUploadHandlerResponse,
    TContext = unknown,
>(options?: OrganisationUploadHandlerMutationOptions<TParams, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: OrganisationUploadHandlerRequest<TParams>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, undefined, TRes>(
            {
                ...organisationUploadHandlerEndpoint,
                p: (callOptions?.schemas?.params?.(organisationUploadHandlerEndpoint.p) ??
                    organisationUploadHandlerEndpoint.p) as z.ZodType<TParams>,
                res: (callOptions?.schemas?.res?.(organisationUploadHandlerEndpoint.res) ??
                    organisationUploadHandlerEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
            },
        );
        return call(input as unknown as Parameters<typeof call>[0]) as Promise<TRes>;
    };

    return useScopeAwareMutation<
        TRes,
        OrganisationUploadHandlerRequest<TParams>,
        DefaultError,
        TContext
    >(
        (input, expectedScope) => execute(input, expectedScope),
        { ...mutationOptions, mutationKey: ["POST", "/me/organisations/picture/{type}"] },
        organisationUploadHandlerEndpoint,
    );
}

const organisationsGetOrganisationInvoicesEndpoint = spellbook(
    "/v1/organisations/invoices/download",
).GET();

export type OrganisationsGetOrganisationInvoicesRequest<
    TQuery = z.infer<typeof organisationsGetOrganisationInvoicesEndpoint.q>,
> = {
    query: TQuery;
} & RequestMeta;

const organisationsGetOrganisationInvoicesCall = (
    input: OrganisationsGetOrganisationInvoicesRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(organisationsGetOrganisationInvoicesEndpoint, undefined, {
        expectedScope,
    });
    return call(input);
};
/** Returns TanStack Query options for GET /v1/organisations/invoices/download. */
export function organisationsGetOrganisationInvoicesQueryOptions<
    TSelected = Awaited<ReturnType<typeof organisationsGetOrganisationInvoicesCall>>,
>(
    input: OrganisationsGetOrganisationInvoicesRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof organisationsGetOrganisationInvoicesCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: organisationsGetOrganisationInvoicesEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/v1/organisations/invoices/download", input.query] as const,
        queryFn: ({ signal }, expectedScope) =>
            organisationsGetOrganisationInvoicesCall({ ...input, signal }, expectedScope),
        options,
    });
}

const patchEndpoint = spellbook("/tasks/{id}").PATCH();

export type PatchRequest<
    TParams = z.infer<typeof patchEndpoint.p>,
    TReq = z.infer<typeof patchEndpoint.req>,
> = {
    params: TParams;
    req: TReq;
} & RequestMeta;

const patchCall = (input: PatchRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(patchEndpoint, undefined, { expectedScope });
    return call(input);
};
type PatchResponse = Awaited<ReturnType<typeof patchCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type PatchMutationCallOptions<
    TParams = z.infer<typeof patchEndpoint.p>,
    TReq = z.infer<typeof patchEndpoint.req>,
    TRes = PatchResponse,
> = {
    schemas?: {
        params?: (schema: typeof patchEndpoint.p) => z.ZodType<TParams>;
        req?: (schema: typeof patchEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof patchEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for PATCH /tasks/{id}.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type PatchMutationOptions<
    TParams = z.infer<typeof patchEndpoint.p>,
    TReq = z.infer<typeof patchEndpoint.req>,
    TRes = PatchResponse,
    TContext = unknown,
> = IdempotentMutationOptions<TRes, PatchRequest<TParams, TReq>, DefaultError, TContext> & {
    call?: PatchMutationCallOptions<TParams, TReq, TRes>;
};

export function usePatchMutation<
    TParams = z.infer<typeof patchEndpoint.p>,
    TReq = z.infer<typeof patchEndpoint.req>,
    TRes = PatchResponse,
    TContext = unknown,
>(options?: PatchMutationOptions<TParams, TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (input: PatchRequest<TParams, TReq>, expectedScope: ScopeContextBinding) => {
        const call = createCall<TParams, undefined, TReq, TRes>(
            {
                ...patchEndpoint,
                p: (callOptions?.schemas?.params?.(patchEndpoint.p) ??
                    patchEndpoint.p) as z.ZodType<TParams>,
                req: (callOptions?.schemas?.req?.(patchEndpoint.req) ??
                    patchEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(patchEndpoint.res) ??
                    patchEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<TRes, PatchRequest<TParams, TReq>, DefaultError, TContext>(
        (input, idempotencyKey, expectedScope) =>
            execute({ ...input, idempotencyKey } as PatchRequest<TParams, TReq>, expectedScope),
        {
            mutationKey: ["PATCH", "/tasks/{id}"],
            ...mutationOptions,
        },
        patchEndpoint,
    );
}

const patchHourTypesEndpoint = spellbook("/organisations/{organisationId}/hours-types").PATCH();

export type PatchHourTypesRequest<
    TParams = z.infer<typeof patchHourTypesEndpoint.p>,
    TReq = z.infer<typeof patchHourTypesEndpoint.req>,
> = {
    params: TParams;
    req: TReq;
} & RequestMeta;

const patchHourTypesCall = (input: PatchHourTypesRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(patchHourTypesEndpoint, undefined, { expectedScope });
    return call(input);
};
type PatchHourTypesResponse = Awaited<ReturnType<typeof patchHourTypesCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type PatchHourTypesMutationCallOptions<
    TParams = z.infer<typeof patchHourTypesEndpoint.p>,
    TReq = z.infer<typeof patchHourTypesEndpoint.req>,
> = {
    schemas?: {
        params?: (schema: typeof patchHourTypesEndpoint.p) => z.ZodType<TParams>;
        req?: (schema: typeof patchHourTypesEndpoint.req) => z.ZodType<TReq>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for PATCH /organisations/{organisationId}/hours-types.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type PatchHourTypesMutationOptions<
    TParams = z.infer<typeof patchHourTypesEndpoint.p>,
    TReq = z.infer<typeof patchHourTypesEndpoint.req>,
    TContext = unknown,
> = IdempotentMutationOptions<
    PatchHourTypesResponse,
    PatchHourTypesRequest<TParams, TReq>,
    DefaultError,
    TContext
> & {
    call?: PatchHourTypesMutationCallOptions<TParams, TReq>;
};

export function usePatchHourTypesMutation<
    TParams = z.infer<typeof patchHourTypesEndpoint.p>,
    TReq = z.infer<typeof patchHourTypesEndpoint.req>,
    TContext = unknown,
>(options?: PatchHourTypesMutationOptions<TParams, TReq, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: PatchHourTypesRequest<TParams, TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, TReq, undefined>(
            {
                ...patchHourTypesEndpoint,
                p: (callOptions?.schemas?.params?.(patchHourTypesEndpoint.p) ??
                    patchHourTypesEndpoint.p) as z.ZodType<TParams>,
                req: (callOptions?.schemas?.req?.(patchHourTypesEndpoint.req) ??
                    patchHourTypesEndpoint.req) as z.ZodType<TReq>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<PatchHourTypesResponse>;
    };

    return useIdempotentMutation<
        PatchHourTypesResponse,
        PatchHourTypesRequest<TParams, TReq>,
        DefaultError,
        TContext
    >(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as PatchHourTypesRequest<TParams, TReq>,
                expectedScope,
            ),
        {
            mutationKey: ["PATCH", "/organisations/{organisationId}/hours-types"],
            ...mutationOptions,
        },
        patchHourTypesEndpoint,
    );
}

const patchIndemnitiesEndpoint = spellbook("/organisations/{organisationId}/indemnities").PATCH();

export type PatchIndemnitiesRequest<
    TParams = z.infer<typeof patchIndemnitiesEndpoint.p>,
    TReq = z.infer<typeof patchIndemnitiesEndpoint.req>,
> = {
    params: TParams;
    req: TReq;
} & RequestMeta;

const patchIndemnitiesCall = (
    input: PatchIndemnitiesRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(patchIndemnitiesEndpoint, undefined, { expectedScope });
    return call(input);
};
type PatchIndemnitiesResponse = Awaited<ReturnType<typeof patchIndemnitiesCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type PatchIndemnitiesMutationCallOptions<
    TParams = z.infer<typeof patchIndemnitiesEndpoint.p>,
    TReq = z.infer<typeof patchIndemnitiesEndpoint.req>,
    TRes = PatchIndemnitiesResponse,
> = {
    schemas?: {
        params?: (schema: typeof patchIndemnitiesEndpoint.p) => z.ZodType<TParams>;
        req?: (schema: typeof patchIndemnitiesEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof patchIndemnitiesEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for PATCH /organisations/{organisationId}/indemnities.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type PatchIndemnitiesMutationOptions<
    TParams = z.infer<typeof patchIndemnitiesEndpoint.p>,
    TReq = z.infer<typeof patchIndemnitiesEndpoint.req>,
    TRes = PatchIndemnitiesResponse,
    TContext = unknown,
> = IdempotentMutationOptions<
    TRes,
    PatchIndemnitiesRequest<TParams, TReq>,
    DefaultError,
    TContext
> & {
    call?: PatchIndemnitiesMutationCallOptions<TParams, TReq, TRes>;
};

export function usePatchIndemnitiesMutation<
    TParams = z.infer<typeof patchIndemnitiesEndpoint.p>,
    TReq = z.infer<typeof patchIndemnitiesEndpoint.req>,
    TRes = PatchIndemnitiesResponse,
    TContext = unknown,
>(options?: PatchIndemnitiesMutationOptions<TParams, TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: PatchIndemnitiesRequest<TParams, TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, TReq, TRes>(
            {
                ...patchIndemnitiesEndpoint,
                p: (callOptions?.schemas?.params?.(patchIndemnitiesEndpoint.p) ??
                    patchIndemnitiesEndpoint.p) as z.ZodType<TParams>,
                req: (callOptions?.schemas?.req?.(patchIndemnitiesEndpoint.req) ??
                    patchIndemnitiesEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(patchIndemnitiesEndpoint.res) ??
                    patchIndemnitiesEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<
        TRes,
        PatchIndemnitiesRequest<TParams, TReq>,
        DefaultError,
        TContext
    >(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as PatchIndemnitiesRequest<TParams, TReq>,
                expectedScope,
            ),
        {
            mutationKey: ["PATCH", "/organisations/{organisationId}/indemnities"],
            ...mutationOptions,
        },
        patchIndemnitiesEndpoint,
    );
}

const patchJobDescriptionEndpoint = spellbook("/job-descriptions/{id}").PATCH();

export type PatchJobDescriptionRequest<
    TParams = z.infer<typeof patchJobDescriptionEndpoint.p>,
    TReq = z.infer<typeof patchJobDescriptionEndpoint.req>,
> = {
    params: TParams;
    req: TReq;
} & RequestMeta;

const patchJobDescriptionCall = (
    input: PatchJobDescriptionRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(patchJobDescriptionEndpoint, undefined, { expectedScope });
    return call(input);
};
type PatchJobDescriptionResponse = Awaited<ReturnType<typeof patchJobDescriptionCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type PatchJobDescriptionMutationCallOptions<
    TParams = z.infer<typeof patchJobDescriptionEndpoint.p>,
    TReq = z.infer<typeof patchJobDescriptionEndpoint.req>,
    TRes = PatchJobDescriptionResponse,
> = {
    schemas?: {
        params?: (schema: typeof patchJobDescriptionEndpoint.p) => z.ZodType<TParams>;
        req?: (schema: typeof patchJobDescriptionEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof patchJobDescriptionEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for PATCH /job-descriptions/{id}.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type PatchJobDescriptionMutationOptions<
    TParams = z.infer<typeof patchJobDescriptionEndpoint.p>,
    TReq = z.infer<typeof patchJobDescriptionEndpoint.req>,
    TRes = PatchJobDescriptionResponse,
    TContext = unknown,
> = IdempotentMutationOptions<
    TRes,
    PatchJobDescriptionRequest<TParams, TReq>,
    DefaultError,
    TContext
> & {
    call?: PatchJobDescriptionMutationCallOptions<TParams, TReq, TRes>;
};

export function usePatchJobDescriptionMutation<
    TParams = z.infer<typeof patchJobDescriptionEndpoint.p>,
    TReq = z.infer<typeof patchJobDescriptionEndpoint.req>,
    TRes = PatchJobDescriptionResponse,
    TContext = unknown,
>(options?: PatchJobDescriptionMutationOptions<TParams, TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: PatchJobDescriptionRequest<TParams, TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, TReq, TRes>(
            {
                ...patchJobDescriptionEndpoint,
                p: (callOptions?.schemas?.params?.(patchJobDescriptionEndpoint.p) ??
                    patchJobDescriptionEndpoint.p) as z.ZodType<TParams>,
                req: (callOptions?.schemas?.req?.(patchJobDescriptionEndpoint.req) ??
                    patchJobDescriptionEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(patchJobDescriptionEndpoint.res) ??
                    patchJobDescriptionEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<
        TRes,
        PatchJobDescriptionRequest<TParams, TReq>,
        DefaultError,
        TContext
    >(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as PatchJobDescriptionRequest<TParams, TReq>,
                expectedScope,
            ),
        {
            mutationKey: ["PATCH", "/job-descriptions/{id}"],
            ...mutationOptions,
        },
        patchJobDescriptionEndpoint,
    );
}

const patchLegalInformationsEndpoint = spellbook(
    "/organisations/{organisationId}/legal-informations",
).PATCH();

export type PatchLegalInformationsRequest<
    TParams = z.infer<typeof patchLegalInformationsEndpoint.p>,
    TReq = z.infer<typeof patchLegalInformationsEndpoint.req>,
> = {
    params: TParams;
    req: TReq;
} & RequestMeta;

const patchLegalInformationsCall = (
    input: PatchLegalInformationsRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(patchLegalInformationsEndpoint, undefined, { expectedScope });
    return call(input);
};
type PatchLegalInformationsResponse = Awaited<ReturnType<typeof patchLegalInformationsCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type PatchLegalInformationsMutationCallOptions<
    TParams = z.infer<typeof patchLegalInformationsEndpoint.p>,
    TReq = z.infer<typeof patchLegalInformationsEndpoint.req>,
> = {
    schemas?: {
        params?: (schema: typeof patchLegalInformationsEndpoint.p) => z.ZodType<TParams>;
        req?: (schema: typeof patchLegalInformationsEndpoint.req) => z.ZodType<TReq>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for PATCH /organisations/{organisationId}/legal-informations.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type PatchLegalInformationsMutationOptions<
    TParams = z.infer<typeof patchLegalInformationsEndpoint.p>,
    TReq = z.infer<typeof patchLegalInformationsEndpoint.req>,
    TContext = unknown,
> = IdempotentMutationOptions<
    PatchLegalInformationsResponse,
    PatchLegalInformationsRequest<TParams, TReq>,
    DefaultError,
    TContext
> & {
    call?: PatchLegalInformationsMutationCallOptions<TParams, TReq>;
};

export function usePatchLegalInformationsMutation<
    TParams = z.infer<typeof patchLegalInformationsEndpoint.p>,
    TReq = z.infer<typeof patchLegalInformationsEndpoint.req>,
    TContext = unknown,
>(options?: PatchLegalInformationsMutationOptions<TParams, TReq, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: PatchLegalInformationsRequest<TParams, TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, TReq, undefined>(
            {
                ...patchLegalInformationsEndpoint,
                p: (callOptions?.schemas?.params?.(patchLegalInformationsEndpoint.p) ??
                    patchLegalInformationsEndpoint.p) as z.ZodType<TParams>,
                req: (callOptions?.schemas?.req?.(patchLegalInformationsEndpoint.req) ??
                    patchLegalInformationsEndpoint.req) as z.ZodType<TReq>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<PatchLegalInformationsResponse>;
    };

    return useIdempotentMutation<
        PatchLegalInformationsResponse,
        PatchLegalInformationsRequest<TParams, TReq>,
        DefaultError,
        TContext
    >(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as PatchLegalInformationsRequest<TParams, TReq>,
                expectedScope,
            ),
        {
            mutationKey: ["PATCH", "/organisations/{organisationId}/legal-informations"],
            ...mutationOptions,
        },
        patchLegalInformationsEndpoint,
    );
}

const patchLocationEndpoint = spellbook(
    "/organisations/{organisationId}/locations/{locationId}",
).PATCH();

export type PatchLocationRequest<
    TParams = z.infer<typeof patchLocationEndpoint.p>,
    TReq = z.infer<typeof patchLocationEndpoint.req>,
> = {
    params: TParams;
    req: TReq;
} & RequestMeta;

const patchLocationCall = (input: PatchLocationRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(patchLocationEndpoint, undefined, { expectedScope });
    return call(input);
};
type PatchLocationResponse = Awaited<ReturnType<typeof patchLocationCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type PatchLocationMutationCallOptions<
    TParams = z.infer<typeof patchLocationEndpoint.p>,
    TReq = z.infer<typeof patchLocationEndpoint.req>,
    TRes = PatchLocationResponse,
> = {
    schemas?: {
        params?: (schema: typeof patchLocationEndpoint.p) => z.ZodType<TParams>;
        req?: (schema: typeof patchLocationEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof patchLocationEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for PATCH /organisations/{organisationId}/locations/{locationId}.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type PatchLocationMutationOptions<
    TParams = z.infer<typeof patchLocationEndpoint.p>,
    TReq = z.infer<typeof patchLocationEndpoint.req>,
    TRes = PatchLocationResponse,
    TContext = unknown,
> = IdempotentMutationOptions<TRes, PatchLocationRequest<TParams, TReq>, DefaultError, TContext> & {
    call?: PatchLocationMutationCallOptions<TParams, TReq, TRes>;
};

export function usePatchLocationMutation<
    TParams = z.infer<typeof patchLocationEndpoint.p>,
    TReq = z.infer<typeof patchLocationEndpoint.req>,
    TRes = PatchLocationResponse,
    TContext = unknown,
>(options?: PatchLocationMutationOptions<TParams, TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: PatchLocationRequest<TParams, TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, TReq, TRes>(
            {
                ...patchLocationEndpoint,
                p: (callOptions?.schemas?.params?.(patchLocationEndpoint.p) ??
                    patchLocationEndpoint.p) as z.ZodType<TParams>,
                req: (callOptions?.schemas?.req?.(patchLocationEndpoint.req) ??
                    patchLocationEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(patchLocationEndpoint.res) ??
                    patchLocationEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<TRes, PatchLocationRequest<TParams, TReq>, DefaultError, TContext>(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as PatchLocationRequest<TParams, TReq>,
                expectedScope,
            ),
        {
            mutationKey: ["PATCH", "/organisations/{organisationId}/locations/{locationId}"],
            ...mutationOptions,
        },
        patchLocationEndpoint,
    );
}

const patchOrganisationProfileEndpoint = spellbook("/me/organisations/profile").PATCH();

export type PatchOrganisationProfileRequest<
    TReq = z.infer<typeof patchOrganisationProfileEndpoint.req>,
> = {
    req: TReq;
} & RequestMeta;

const patchOrganisationProfileCall = (
    input: PatchOrganisationProfileRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(patchOrganisationProfileEndpoint, undefined, { expectedScope });
    return call(input);
};
type PatchOrganisationProfileResponse = Awaited<ReturnType<typeof patchOrganisationProfileCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type PatchOrganisationProfileMutationCallOptions<
    TReq = z.infer<typeof patchOrganisationProfileEndpoint.req>,
    TRes = PatchOrganisationProfileResponse,
> = {
    schemas?: {
        req?: (schema: typeof patchOrganisationProfileEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof patchOrganisationProfileEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for PATCH /me/organisations/profile.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type PatchOrganisationProfileMutationOptions<
    TReq = z.infer<typeof patchOrganisationProfileEndpoint.req>,
    TRes = PatchOrganisationProfileResponse,
    TContext = unknown,
> = IdempotentMutationOptions<
    TRes,
    PatchOrganisationProfileRequest<TReq>,
    DefaultError,
    TContext
> & {
    call?: PatchOrganisationProfileMutationCallOptions<TReq, TRes>;
};

export function usePatchOrganisationProfileMutation<
    TReq = z.infer<typeof patchOrganisationProfileEndpoint.req>,
    TRes = PatchOrganisationProfileResponse,
    TContext = unknown,
>(options?: PatchOrganisationProfileMutationOptions<TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: PatchOrganisationProfileRequest<TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<undefined, undefined, TReq, TRes>(
            {
                ...patchOrganisationProfileEndpoint,
                req: (callOptions?.schemas?.req?.(patchOrganisationProfileEndpoint.req) ??
                    patchOrganisationProfileEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(patchOrganisationProfileEndpoint.res) ??
                    patchOrganisationProfileEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<
        TRes,
        PatchOrganisationProfileRequest<TReq>,
        DefaultError,
        TContext
    >(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as PatchOrganisationProfileRequest<TReq>,
                expectedScope,
            ),
        {
            mutationKey: ["PATCH", "/me/organisations/profile"],
            ...mutationOptions,
        },
        patchOrganisationProfileEndpoint,
    );
}

const patchPaymentInformationsEndpoint = spellbook(
    "/organisations/{organisationId}/payment-informations",
).PATCH();

export type PatchPaymentInformationsRequest<
    TParams = z.infer<typeof patchPaymentInformationsEndpoint.p>,
    TReq = z.infer<typeof patchPaymentInformationsEndpoint.req>,
> = {
    params: TParams;
    req: TReq;
} & RequestMeta;

const patchPaymentInformationsCall = (
    input: PatchPaymentInformationsRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(patchPaymentInformationsEndpoint, undefined, { expectedScope });
    return call(input);
};
type PatchPaymentInformationsResponse = Awaited<ReturnType<typeof patchPaymentInformationsCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type PatchPaymentInformationsMutationCallOptions<
    TParams = z.infer<typeof patchPaymentInformationsEndpoint.p>,
    TReq = z.infer<typeof patchPaymentInformationsEndpoint.req>,
> = {
    schemas?: {
        params?: (schema: typeof patchPaymentInformationsEndpoint.p) => z.ZodType<TParams>;
        req?: (schema: typeof patchPaymentInformationsEndpoint.req) => z.ZodType<TReq>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for PATCH /organisations/{organisationId}/payment-informations.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type PatchPaymentInformationsMutationOptions<
    TParams = z.infer<typeof patchPaymentInformationsEndpoint.p>,
    TReq = z.infer<typeof patchPaymentInformationsEndpoint.req>,
    TContext = unknown,
> = IdempotentMutationOptions<
    PatchPaymentInformationsResponse,
    PatchPaymentInformationsRequest<TParams, TReq>,
    DefaultError,
    TContext
> & {
    call?: PatchPaymentInformationsMutationCallOptions<TParams, TReq>;
};

export function usePatchPaymentInformationsMutation<
    TParams = z.infer<typeof patchPaymentInformationsEndpoint.p>,
    TReq = z.infer<typeof patchPaymentInformationsEndpoint.req>,
    TContext = unknown,
>(options?: PatchPaymentInformationsMutationOptions<TParams, TReq, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: PatchPaymentInformationsRequest<TParams, TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, TReq, undefined>(
            {
                ...patchPaymentInformationsEndpoint,
                p: (callOptions?.schemas?.params?.(patchPaymentInformationsEndpoint.p) ??
                    patchPaymentInformationsEndpoint.p) as z.ZodType<TParams>,
                req: (callOptions?.schemas?.req?.(patchPaymentInformationsEndpoint.req) ??
                    patchPaymentInformationsEndpoint.req) as z.ZodType<TReq>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<PatchPaymentInformationsResponse>;
    };

    return useIdempotentMutation<
        PatchPaymentInformationsResponse,
        PatchPaymentInformationsRequest<TParams, TReq>,
        DefaultError,
        TContext
    >(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as PatchPaymentInformationsRequest<TParams, TReq>,
                expectedScope,
            ),
        {
            mutationKey: ["PATCH", "/organisations/{organisationId}/payment-informations"],
            ...mutationOptions,
        },
        patchPaymentInformationsEndpoint,
    );
}

const patchPlanningEndpoint = spellbook("/tasks/{id}/planning").PATCH();

export type PatchPlanningRequest<
    TParams = z.infer<typeof patchPlanningEndpoint.p>,
    TReq = z.infer<typeof patchPlanningEndpoint.req>,
> = {
    params: TParams;
    req: TReq;
} & RequestMeta;

const patchPlanningCall = (input: PatchPlanningRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(patchPlanningEndpoint, undefined, { expectedScope });
    return call(input);
};
type PatchPlanningResponse = Awaited<ReturnType<typeof patchPlanningCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type PatchPlanningMutationCallOptions<
    TParams = z.infer<typeof patchPlanningEndpoint.p>,
    TReq = z.infer<typeof patchPlanningEndpoint.req>,
    TRes = PatchPlanningResponse,
> = {
    schemas?: {
        params?: (schema: typeof patchPlanningEndpoint.p) => z.ZodType<TParams>;
        req?: (schema: typeof patchPlanningEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof patchPlanningEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for PATCH /tasks/{id}/planning.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type PatchPlanningMutationOptions<
    TParams = z.infer<typeof patchPlanningEndpoint.p>,
    TReq = z.infer<typeof patchPlanningEndpoint.req>,
    TRes = PatchPlanningResponse,
    TContext = unknown,
> = IdempotentMutationOptions<TRes, PatchPlanningRequest<TParams, TReq>, DefaultError, TContext> & {
    call?: PatchPlanningMutationCallOptions<TParams, TReq, TRes>;
};

export function usePatchPlanningMutation<
    TParams = z.infer<typeof patchPlanningEndpoint.p>,
    TReq = z.infer<typeof patchPlanningEndpoint.req>,
    TRes = PatchPlanningResponse,
    TContext = unknown,
>(options?: PatchPlanningMutationOptions<TParams, TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: PatchPlanningRequest<TParams, TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, TReq, TRes>(
            {
                ...patchPlanningEndpoint,
                p: (callOptions?.schemas?.params?.(patchPlanningEndpoint.p) ??
                    patchPlanningEndpoint.p) as z.ZodType<TParams>,
                req: (callOptions?.schemas?.req?.(patchPlanningEndpoint.req) ??
                    patchPlanningEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(patchPlanningEndpoint.res) ??
                    patchPlanningEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<TRes, PatchPlanningRequest<TParams, TReq>, DefaultError, TContext>(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as PatchPlanningRequest<TParams, TReq>,
                expectedScope,
            ),
        {
            mutationKey: ["PATCH", "/tasks/{id}/planning"],
            ...mutationOptions,
        },
        patchPlanningEndpoint,
    );
}

const patchRemunerationPolicySettingsEndpoint = spellbook(
    "/organisations/{organisationId}/remuneration-policy-settings",
).PATCH();

export type PatchRemunerationPolicySettingsRequest<
    TParams = z.infer<typeof patchRemunerationPolicySettingsEndpoint.p>,
    TReq = z.infer<typeof patchRemunerationPolicySettingsEndpoint.req>,
> = {
    params: TParams;
    req: TReq;
} & RequestMeta;

const patchRemunerationPolicySettingsCall = (
    input: PatchRemunerationPolicySettingsRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(patchRemunerationPolicySettingsEndpoint, undefined, { expectedScope });
    return call(input);
};
type PatchRemunerationPolicySettingsResponse = Awaited<
    ReturnType<typeof patchRemunerationPolicySettingsCall>
>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type PatchRemunerationPolicySettingsMutationCallOptions<
    TParams = z.infer<typeof patchRemunerationPolicySettingsEndpoint.p>,
    TReq = z.infer<typeof patchRemunerationPolicySettingsEndpoint.req>,
    TRes = PatchRemunerationPolicySettingsResponse,
> = {
    schemas?: {
        params?: (schema: typeof patchRemunerationPolicySettingsEndpoint.p) => z.ZodType<TParams>;
        req?: (schema: typeof patchRemunerationPolicySettingsEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof patchRemunerationPolicySettingsEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for PATCH /organisations/{organisationId}/remuneration-policy-settings.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type PatchRemunerationPolicySettingsMutationOptions<
    TParams = z.infer<typeof patchRemunerationPolicySettingsEndpoint.p>,
    TReq = z.infer<typeof patchRemunerationPolicySettingsEndpoint.req>,
    TRes = PatchRemunerationPolicySettingsResponse,
    TContext = unknown,
> = IdempotentMutationOptions<
    TRes,
    PatchRemunerationPolicySettingsRequest<TParams, TReq>,
    DefaultError,
    TContext
> & {
    call?: PatchRemunerationPolicySettingsMutationCallOptions<TParams, TReq, TRes>;
};

export function usePatchRemunerationPolicySettingsMutation<
    TParams = z.infer<typeof patchRemunerationPolicySettingsEndpoint.p>,
    TReq = z.infer<typeof patchRemunerationPolicySettingsEndpoint.req>,
    TRes = PatchRemunerationPolicySettingsResponse,
    TContext = unknown,
>(options?: PatchRemunerationPolicySettingsMutationOptions<TParams, TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: PatchRemunerationPolicySettingsRequest<TParams, TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, TReq, TRes>(
            {
                ...patchRemunerationPolicySettingsEndpoint,
                p: (callOptions?.schemas?.params?.(patchRemunerationPolicySettingsEndpoint.p) ??
                    patchRemunerationPolicySettingsEndpoint.p) as z.ZodType<TParams>,
                req: (callOptions?.schemas?.req?.(patchRemunerationPolicySettingsEndpoint.req) ??
                    patchRemunerationPolicySettingsEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(patchRemunerationPolicySettingsEndpoint.res) ??
                    patchRemunerationPolicySettingsEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<
        TRes,
        PatchRemunerationPolicySettingsRequest<TParams, TReq>,
        DefaultError,
        TContext
    >(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as PatchRemunerationPolicySettingsRequest<
                    TParams,
                    TReq
                >,
                expectedScope,
            ),
        {
            mutationKey: ["PATCH", "/organisations/{organisationId}/remuneration-policy-settings"],
            ...mutationOptions,
        },
        patchRemunerationPolicySettingsEndpoint,
    );
}

const patchShiftEndpoint = spellbook("/shifts/{id}").PATCH();

export type PatchShiftRequest<
    TParams = z.infer<typeof patchShiftEndpoint.p>,
    TQuery = z.infer<typeof patchShiftEndpoint.q>,
    TReq = z.infer<typeof patchShiftEndpoint.req>,
> = {
    params: TParams;
    query: TQuery;
    req: TReq;
} & RequestMeta;

const patchShiftCall = (input: PatchShiftRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(patchShiftEndpoint, undefined, { expectedScope });
    return call(input);
};
type PatchShiftResponse = Awaited<ReturnType<typeof patchShiftCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type PatchShiftMutationCallOptions<
    TParams = z.infer<typeof patchShiftEndpoint.p>,
    TQuery = z.infer<typeof patchShiftEndpoint.q>,
    TReq = z.infer<typeof patchShiftEndpoint.req>,
    TRes = PatchShiftResponse,
> = {
    schemas?: {
        params?: (schema: typeof patchShiftEndpoint.p) => z.ZodType<TParams>;
        query?: (schema: typeof patchShiftEndpoint.q) => z.ZodType<TQuery>;
        req?: (schema: typeof patchShiftEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof patchShiftEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for PATCH /shifts/{id}.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type PatchShiftMutationOptions<
    TParams = z.infer<typeof patchShiftEndpoint.p>,
    TQuery = z.infer<typeof patchShiftEndpoint.q>,
    TReq = z.infer<typeof patchShiftEndpoint.req>,
    TRes = PatchShiftResponse,
    TContext = unknown,
> = IdempotentMutationOptions<
    TRes,
    PatchShiftRequest<TParams, TQuery, TReq>,
    DefaultError,
    TContext
> & {
    call?: PatchShiftMutationCallOptions<TParams, TQuery, TReq, TRes>;
};

export function usePatchShiftMutation<
    TParams = z.infer<typeof patchShiftEndpoint.p>,
    TQuery = z.infer<typeof patchShiftEndpoint.q>,
    TReq = z.infer<typeof patchShiftEndpoint.req>,
    TRes = PatchShiftResponse,
    TContext = unknown,
>(options?: PatchShiftMutationOptions<TParams, TQuery, TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: PatchShiftRequest<TParams, TQuery, TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, TQuery, TReq, TRes>(
            {
                ...patchShiftEndpoint,
                p: (callOptions?.schemas?.params?.(patchShiftEndpoint.p) ??
                    patchShiftEndpoint.p) as z.ZodType<TParams>,
                q: (callOptions?.schemas?.query?.(patchShiftEndpoint.q) ??
                    patchShiftEndpoint.q) as z.ZodType<TQuery>,
                req: (callOptions?.schemas?.req?.(patchShiftEndpoint.req) ??
                    patchShiftEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(patchShiftEndpoint.res) ??
                    patchShiftEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<
        TRes,
        PatchShiftRequest<TParams, TQuery, TReq>,
        DefaultError,
        TContext
    >(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as PatchShiftRequest<TParams, TQuery, TReq>,
                expectedScope,
            ),
        {
            mutationKey: ["PATCH", "/shifts/{id}"],
            ...mutationOptions,
        },
        patchShiftEndpoint,
    );
}

const patchShiftsEndpoint = spellbook("/tasks/{id}/shifts").PATCH();

export type PatchShiftsRequest<
    TParams = z.infer<typeof patchShiftsEndpoint.p>,
    TReq = z.infer<typeof patchShiftsEndpoint.req>,
> = {
    params: TParams;
    req: TReq;
} & RequestMeta;

const patchShiftsCall = (input: PatchShiftsRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(patchShiftsEndpoint, undefined, { expectedScope });
    return call(input);
};
type PatchShiftsResponse = Awaited<ReturnType<typeof patchShiftsCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type PatchShiftsMutationCallOptions<
    TParams = z.infer<typeof patchShiftsEndpoint.p>,
    TReq = z.infer<typeof patchShiftsEndpoint.req>,
    TRes = PatchShiftsResponse,
> = {
    schemas?: {
        params?: (schema: typeof patchShiftsEndpoint.p) => z.ZodType<TParams>;
        req?: (schema: typeof patchShiftsEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof patchShiftsEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for PATCH /tasks/{id}/shifts.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type PatchShiftsMutationOptions<
    TParams = z.infer<typeof patchShiftsEndpoint.p>,
    TReq = z.infer<typeof patchShiftsEndpoint.req>,
    TRes = PatchShiftsResponse,
    TContext = unknown,
> = IdempotentMutationOptions<TRes, PatchShiftsRequest<TParams, TReq>, DefaultError, TContext> & {
    call?: PatchShiftsMutationCallOptions<TParams, TReq, TRes>;
};

export function usePatchShiftsMutation<
    TParams = z.infer<typeof patchShiftsEndpoint.p>,
    TReq = z.infer<typeof patchShiftsEndpoint.req>,
    TRes = PatchShiftsResponse,
    TContext = unknown,
>(options?: PatchShiftsMutationOptions<TParams, TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: PatchShiftsRequest<TParams, TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, TReq, TRes>(
            {
                ...patchShiftsEndpoint,
                p: (callOptions?.schemas?.params?.(patchShiftsEndpoint.p) ??
                    patchShiftsEndpoint.p) as z.ZodType<TParams>,
                req: (callOptions?.schemas?.req?.(patchShiftsEndpoint.req) ??
                    patchShiftsEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(patchShiftsEndpoint.res) ??
                    patchShiftsEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<TRes, PatchShiftsRequest<TParams, TReq>, DefaultError, TContext>(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as PatchShiftsRequest<TParams, TReq>,
                expectedScope,
            ),
        {
            mutationKey: ["PATCH", "/tasks/{id}/shifts"],
            ...mutationOptions,
        },
        patchShiftsEndpoint,
    );
}

const patchUserEndpoint = spellbook("/me/organisations/users/{id}").PATCH();

export type PatchUserRequest<
    TParams = z.infer<typeof patchUserEndpoint.p>,
    TReq = z.infer<typeof patchUserEndpoint.req>,
> = {
    params: TParams;
    req: TReq;
} & RequestMeta;

const patchUserCall = (input: PatchUserRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(patchUserEndpoint, undefined, { expectedScope });
    return call(input);
};
type PatchUserResponse = Awaited<ReturnType<typeof patchUserCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type PatchUserMutationCallOptions<
    TParams = z.infer<typeof patchUserEndpoint.p>,
    TReq = z.infer<typeof patchUserEndpoint.req>,
    TRes = PatchUserResponse,
> = {
    schemas?: {
        params?: (schema: typeof patchUserEndpoint.p) => z.ZodType<TParams>;
        req?: (schema: typeof patchUserEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof patchUserEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for PATCH /me/organisations/users/{id}.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type PatchUserMutationOptions<
    TParams = z.infer<typeof patchUserEndpoint.p>,
    TReq = z.infer<typeof patchUserEndpoint.req>,
    TRes = PatchUserResponse,
    TContext = unknown,
> = IdempotentMutationOptions<TRes, PatchUserRequest<TParams, TReq>, DefaultError, TContext> & {
    call?: PatchUserMutationCallOptions<TParams, TReq, TRes>;
};

export function usePatchUserMutation<
    TParams = z.infer<typeof patchUserEndpoint.p>,
    TReq = z.infer<typeof patchUserEndpoint.req>,
    TRes = PatchUserResponse,
    TContext = unknown,
>(options?: PatchUserMutationOptions<TParams, TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: PatchUserRequest<TParams, TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, TReq, TRes>(
            {
                ...patchUserEndpoint,
                p: (callOptions?.schemas?.params?.(patchUserEndpoint.p) ??
                    patchUserEndpoint.p) as z.ZodType<TParams>,
                req: (callOptions?.schemas?.req?.(patchUserEndpoint.req) ??
                    patchUserEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(patchUserEndpoint.res) ??
                    patchUserEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<TRes, PatchUserRequest<TParams, TReq>, DefaultError, TContext>(
        (input, idempotencyKey, expectedScope) =>
            execute({ ...input, idempotencyKey } as PatchUserRequest<TParams, TReq>, expectedScope),
        {
            mutationKey: ["PATCH", "/me/organisations/users/{id}"],
            ...mutationOptions,
        },
        patchUserEndpoint,
    );
}

const patchUserProfileEndpoint = spellbook("/me/profile").PATCH();

export type PatchUserProfileRequest<TReq = z.infer<typeof patchUserProfileEndpoint.req>> = {
    req: TReq;
} & RequestMeta;

const patchUserProfileCall = (
    input: PatchUserProfileRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(patchUserProfileEndpoint, undefined, { expectedScope });
    return call(input);
};
type PatchUserProfileResponse = Awaited<ReturnType<typeof patchUserProfileCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type PatchUserProfileMutationCallOptions<
    TReq = z.infer<typeof patchUserProfileEndpoint.req>,
    TRes = PatchUserProfileResponse,
> = {
    schemas?: {
        req?: (schema: typeof patchUserProfileEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof patchUserProfileEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for PATCH /me/profile.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type PatchUserProfileMutationOptions<
    TReq = z.infer<typeof patchUserProfileEndpoint.req>,
    TRes = PatchUserProfileResponse,
    TContext = unknown,
> = IdempotentMutationOptions<TRes, PatchUserProfileRequest<TReq>, DefaultError, TContext> & {
    call?: PatchUserProfileMutationCallOptions<TReq, TRes>;
};

export function usePatchUserProfileMutation<
    TReq = z.infer<typeof patchUserProfileEndpoint.req>,
    TRes = PatchUserProfileResponse,
    TContext = unknown,
>(options?: PatchUserProfileMutationOptions<TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (input: PatchUserProfileRequest<TReq>, expectedScope: ScopeContextBinding) => {
        const call = createCall<undefined, undefined, TReq, TRes>(
            {
                ...patchUserProfileEndpoint,
                req: (callOptions?.schemas?.req?.(patchUserProfileEndpoint.req) ??
                    patchUserProfileEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(patchUserProfileEndpoint.res) ??
                    patchUserProfileEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<TRes, PatchUserProfileRequest<TReq>, DefaultError, TContext>(
        (input, idempotencyKey, expectedScope) =>
            execute({ ...input, idempotencyKey } as PatchUserProfileRequest<TReq>, expectedScope),
        {
            mutationKey: ["PATCH", "/me/profile"],
            ...mutationOptions,
        },
        patchUserProfileEndpoint,
    );
}

const postEndpoint = spellbook("/organisations/{organisationId}/departments").POST();

export type PostRequest<
    TParams = z.infer<typeof postEndpoint.p>,
    TReq = z.infer<typeof postEndpoint.req>,
> = {
    params: TParams;
    req: TReq;
} & RequestMeta;

const postCall = (input: PostRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(postEndpoint, undefined, { expectedScope });
    return call(input);
};
type PostResponse = Awaited<ReturnType<typeof postCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type PostMutationCallOptions<
    TParams = z.infer<typeof postEndpoint.p>,
    TReq = z.infer<typeof postEndpoint.req>,
    TRes = PostResponse,
> = {
    schemas?: {
        params?: (schema: typeof postEndpoint.p) => z.ZodType<TParams>;
        req?: (schema: typeof postEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof postEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for POST /organisations/{organisationId}/departments.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type PostMutationOptions<
    TParams = z.infer<typeof postEndpoint.p>,
    TReq = z.infer<typeof postEndpoint.req>,
    TRes = PostResponse,
    TContext = unknown,
> = IdempotentMutationOptions<TRes, PostRequest<TParams, TReq>, DefaultError, TContext> & {
    call?: PostMutationCallOptions<TParams, TReq, TRes>;
};

export function usePostMutation<
    TParams = z.infer<typeof postEndpoint.p>,
    TReq = z.infer<typeof postEndpoint.req>,
    TRes = PostResponse,
    TContext = unknown,
>(options?: PostMutationOptions<TParams, TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (input: PostRequest<TParams, TReq>, expectedScope: ScopeContextBinding) => {
        const call = createCall<TParams, undefined, TReq, TRes>(
            {
                ...postEndpoint,
                p: (callOptions?.schemas?.params?.(postEndpoint.p) ??
                    postEndpoint.p) as z.ZodType<TParams>,
                req: (callOptions?.schemas?.req?.(postEndpoint.req) ??
                    postEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(postEndpoint.res) ??
                    postEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<TRes, PostRequest<TParams, TReq>, DefaultError, TContext>(
        (input, idempotencyKey, expectedScope) =>
            execute({ ...input, idempotencyKey } as PostRequest<TParams, TReq>, expectedScope),
        {
            mutationKey: ["POST", "/organisations/{organisationId}/departments"],
            ...mutationOptions,
        },
        postEndpoint,
    );
}

const postAttendanceDisputeEndpoint = spellbook("/attendances/{id}/dispute").POST();

export type PostAttendanceDisputeRequest<
    TParams = z.infer<typeof postAttendanceDisputeEndpoint.p>,
    TReq = z.infer<typeof postAttendanceDisputeEndpoint.req>,
> = {
    params: TParams;
    req: TReq;
} & RequestMeta;

const postAttendanceDisputeCall = (
    input: PostAttendanceDisputeRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(postAttendanceDisputeEndpoint, undefined, { expectedScope });
    return call(input);
};
type PostAttendanceDisputeResponse = Awaited<ReturnType<typeof postAttendanceDisputeCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type PostAttendanceDisputeMutationCallOptions<
    TParams = z.infer<typeof postAttendanceDisputeEndpoint.p>,
    TReq = z.infer<typeof postAttendanceDisputeEndpoint.req>,
    TRes = PostAttendanceDisputeResponse,
> = {
    schemas?: {
        params?: (schema: typeof postAttendanceDisputeEndpoint.p) => z.ZodType<TParams>;
        req?: (schema: typeof postAttendanceDisputeEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof postAttendanceDisputeEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for POST /attendances/{id}/dispute.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type PostAttendanceDisputeMutationOptions<
    TParams = z.infer<typeof postAttendanceDisputeEndpoint.p>,
    TReq = z.infer<typeof postAttendanceDisputeEndpoint.req>,
    TRes = PostAttendanceDisputeResponse,
    TContext = unknown,
> = IdempotentMutationOptions<
    TRes,
    PostAttendanceDisputeRequest<TParams, TReq>,
    DefaultError,
    TContext
> & {
    call?: PostAttendanceDisputeMutationCallOptions<TParams, TReq, TRes>;
};

export function usePostAttendanceDisputeMutation<
    TParams = z.infer<typeof postAttendanceDisputeEndpoint.p>,
    TReq = z.infer<typeof postAttendanceDisputeEndpoint.req>,
    TRes = PostAttendanceDisputeResponse,
    TContext = unknown,
>(options?: PostAttendanceDisputeMutationOptions<TParams, TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: PostAttendanceDisputeRequest<TParams, TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, TReq, TRes>(
            {
                ...postAttendanceDisputeEndpoint,
                p: (callOptions?.schemas?.params?.(postAttendanceDisputeEndpoint.p) ??
                    postAttendanceDisputeEndpoint.p) as z.ZodType<TParams>,
                req: (callOptions?.schemas?.req?.(postAttendanceDisputeEndpoint.req) ??
                    postAttendanceDisputeEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(postAttendanceDisputeEndpoint.res) ??
                    postAttendanceDisputeEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<
        TRes,
        PostAttendanceDisputeRequest<TParams, TReq>,
        DefaultError,
        TContext
    >(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as PostAttendanceDisputeRequest<TParams, TReq>,
                expectedScope,
            ),
        {
            mutationKey: ["POST", "/attendances/{id}/dispute"],
            ...mutationOptions,
        },
        postAttendanceDisputeEndpoint,
    );
}

const postAttendanceReviewEndpoint = spellbook("/v1/attendances/{attendanceID}/review").POST();

export type PostAttendanceReviewRequest<
    TParams = z.infer<typeof postAttendanceReviewEndpoint.p>,
    TReq = z.infer<typeof postAttendanceReviewEndpoint.req>,
> = {
    params: TParams;
    req: TReq;
} & RequestMeta;

const postAttendanceReviewCall = (
    input: PostAttendanceReviewRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(postAttendanceReviewEndpoint, undefined, { expectedScope });
    return call(input);
};
type PostAttendanceReviewResponse = Awaited<ReturnType<typeof postAttendanceReviewCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type PostAttendanceReviewMutationCallOptions<
    TParams = z.infer<typeof postAttendanceReviewEndpoint.p>,
    TReq = z.infer<typeof postAttendanceReviewEndpoint.req>,
    TRes = PostAttendanceReviewResponse,
> = {
    schemas?: {
        params?: (schema: typeof postAttendanceReviewEndpoint.p) => z.ZodType<TParams>;
        req?: (schema: typeof postAttendanceReviewEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof postAttendanceReviewEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for POST /v1/attendances/{attendanceID}/review.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type PostAttendanceReviewMutationOptions<
    TParams = z.infer<typeof postAttendanceReviewEndpoint.p>,
    TReq = z.infer<typeof postAttendanceReviewEndpoint.req>,
    TRes = PostAttendanceReviewResponse,
    TContext = unknown,
> = IdempotentMutationOptions<
    TRes,
    PostAttendanceReviewRequest<TParams, TReq>,
    DefaultError,
    TContext
> & {
    call?: PostAttendanceReviewMutationCallOptions<TParams, TReq, TRes>;
};

export function usePostAttendanceReviewMutation<
    TParams = z.infer<typeof postAttendanceReviewEndpoint.p>,
    TReq = z.infer<typeof postAttendanceReviewEndpoint.req>,
    TRes = PostAttendanceReviewResponse,
    TContext = unknown,
>(options?: PostAttendanceReviewMutationOptions<TParams, TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: PostAttendanceReviewRequest<TParams, TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, TReq, TRes>(
            {
                ...postAttendanceReviewEndpoint,
                p: (callOptions?.schemas?.params?.(postAttendanceReviewEndpoint.p) ??
                    postAttendanceReviewEndpoint.p) as z.ZodType<TParams>,
                req: (callOptions?.schemas?.req?.(postAttendanceReviewEndpoint.req) ??
                    postAttendanceReviewEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(postAttendanceReviewEndpoint.res) ??
                    postAttendanceReviewEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<
        TRes,
        PostAttendanceReviewRequest<TParams, TReq>,
        DefaultError,
        TContext
    >(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as PostAttendanceReviewRequest<TParams, TReq>,
                expectedScope,
            ),
        {
            mutationKey: ["POST", "/v1/attendances/{attendanceID}/review"],
            ...mutationOptions,
        },
        postAttendanceReviewEndpoint,
    );
}

const postAttendancesEndpoint = spellbook("/attendances").POST();

export type PostAttendancesRequest<TReq = z.infer<typeof postAttendancesEndpoint.req>> = {
    req: TReq;
} & RequestMeta;

const postAttendancesCall = (
    input: PostAttendancesRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(postAttendancesEndpoint, undefined, { expectedScope });
    return call(input);
};
type PostAttendancesResponse = Awaited<ReturnType<typeof postAttendancesCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type PostAttendancesMutationCallOptions<TReq = z.infer<typeof postAttendancesEndpoint.req>> =
    {
        schemas?: {
            req?: (schema: typeof postAttendancesEndpoint.req) => z.ZodType<TReq>;
        };
        getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
    };
/**
 * Returns an idempotent TanStack mutation for POST /attendances.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type PostAttendancesMutationOptions<
    TReq = z.infer<typeof postAttendancesEndpoint.req>,
    TContext = unknown,
> = IdempotentMutationOptions<
    PostAttendancesResponse,
    PostAttendancesRequest<TReq>,
    DefaultError,
    TContext
> & {
    call?: PostAttendancesMutationCallOptions<TReq>;
};

export function usePostAttendancesMutation<
    TReq = z.infer<typeof postAttendancesEndpoint.req>,
    TContext = unknown,
>(options?: PostAttendancesMutationOptions<TReq, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (input: PostAttendancesRequest<TReq>, expectedScope: ScopeContextBinding) => {
        const call = createCall<undefined, undefined, TReq, undefined>(
            {
                ...postAttendancesEndpoint,
                req: (callOptions?.schemas?.req?.(postAttendancesEndpoint.req) ??
                    postAttendancesEndpoint.req) as z.ZodType<TReq>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<PostAttendancesResponse>;
    };

    return useIdempotentMutation<
        PostAttendancesResponse,
        PostAttendancesRequest<TReq>,
        DefaultError,
        TContext
    >(
        (input, idempotencyKey, expectedScope) =>
            execute({ ...input, idempotencyKey } as PostAttendancesRequest<TReq>, expectedScope),
        {
            mutationKey: ["POST", "/attendances"],
            ...mutationOptions,
        },
        postAttendancesEndpoint,
    );
}

const postFeedbackEndpoint = spellbook(
    "/v1/feedback/organisation/{organisationId}/sider/{siderId}",
).POST();

export type PostFeedbackRequest<
    TParams = z.infer<typeof postFeedbackEndpoint.p>,
    TReq = z.infer<typeof postFeedbackEndpoint.req>,
> = {
    params: TParams;
    req: TReq;
} & RequestMeta;

const postFeedbackCall = (input: PostFeedbackRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(postFeedbackEndpoint, undefined, { expectedScope });
    return call(input);
};
type PostFeedbackResponse = Awaited<ReturnType<typeof postFeedbackCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type PostFeedbackMutationCallOptions<
    TParams = z.infer<typeof postFeedbackEndpoint.p>,
    TReq = z.infer<typeof postFeedbackEndpoint.req>,
    TRes = PostFeedbackResponse,
> = {
    schemas?: {
        params?: (schema: typeof postFeedbackEndpoint.p) => z.ZodType<TParams>;
        req?: (schema: typeof postFeedbackEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof postFeedbackEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for POST /v1/feedback/organisation/{organisationId}/sider/{siderId}.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type PostFeedbackMutationOptions<
    TParams = z.infer<typeof postFeedbackEndpoint.p>,
    TReq = z.infer<typeof postFeedbackEndpoint.req>,
    TRes = PostFeedbackResponse,
    TContext = unknown,
> = IdempotentMutationOptions<TRes, PostFeedbackRequest<TParams, TReq>, DefaultError, TContext> & {
    call?: PostFeedbackMutationCallOptions<TParams, TReq, TRes>;
};

export function usePostFeedbackMutation<
    TParams = z.infer<typeof postFeedbackEndpoint.p>,
    TReq = z.infer<typeof postFeedbackEndpoint.req>,
    TRes = PostFeedbackResponse,
    TContext = unknown,
>(options?: PostFeedbackMutationOptions<TParams, TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: PostFeedbackRequest<TParams, TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, TReq, TRes>(
            {
                ...postFeedbackEndpoint,
                p: (callOptions?.schemas?.params?.(postFeedbackEndpoint.p) ??
                    postFeedbackEndpoint.p) as z.ZodType<TParams>,
                req: (callOptions?.schemas?.req?.(postFeedbackEndpoint.req) ??
                    postFeedbackEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(postFeedbackEndpoint.res) ??
                    postFeedbackEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<TRes, PostFeedbackRequest<TParams, TReq>, DefaultError, TContext>(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as PostFeedbackRequest<TParams, TReq>,
                expectedScope,
            ),
        {
            mutationKey: ["POST", "/v1/feedback/organisation/{organisationId}/sider/{siderId}"],
            ...mutationOptions,
        },
        postFeedbackEndpoint,
    );
}

const postFlexiblePlanningEndpoint = spellbook("/tasks/{id}/flexible-planning").POST();

export type PostFlexiblePlanningRequest<
    TParams = z.infer<typeof postFlexiblePlanningEndpoint.p>,
    TReq = z.infer<typeof postFlexiblePlanningEndpoint.req>,
> = {
    params: TParams;
    req: TReq;
} & RequestMeta;

const postFlexiblePlanningCall = (
    input: PostFlexiblePlanningRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(postFlexiblePlanningEndpoint, undefined, { expectedScope });
    return call(input);
};
type PostFlexiblePlanningResponse = Awaited<ReturnType<typeof postFlexiblePlanningCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type PostFlexiblePlanningMutationCallOptions<
    TParams = z.infer<typeof postFlexiblePlanningEndpoint.p>,
    TReq = z.infer<typeof postFlexiblePlanningEndpoint.req>,
> = {
    schemas?: {
        params?: (schema: typeof postFlexiblePlanningEndpoint.p) => z.ZodType<TParams>;
        req?: (schema: typeof postFlexiblePlanningEndpoint.req) => z.ZodType<TReq>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for POST /tasks/{id}/flexible-planning.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type PostFlexiblePlanningMutationOptions<
    TParams = z.infer<typeof postFlexiblePlanningEndpoint.p>,
    TReq = z.infer<typeof postFlexiblePlanningEndpoint.req>,
    TContext = unknown,
> = IdempotentMutationOptions<
    PostFlexiblePlanningResponse,
    PostFlexiblePlanningRequest<TParams, TReq>,
    DefaultError,
    TContext
> & {
    call?: PostFlexiblePlanningMutationCallOptions<TParams, TReq>;
};

export function usePostFlexiblePlanningMutation<
    TParams = z.infer<typeof postFlexiblePlanningEndpoint.p>,
    TReq = z.infer<typeof postFlexiblePlanningEndpoint.req>,
    TContext = unknown,
>(options?: PostFlexiblePlanningMutationOptions<TParams, TReq, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: PostFlexiblePlanningRequest<TParams, TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, TReq, undefined>(
            {
                ...postFlexiblePlanningEndpoint,
                p: (callOptions?.schemas?.params?.(postFlexiblePlanningEndpoint.p) ??
                    postFlexiblePlanningEndpoint.p) as z.ZodType<TParams>,
                req: (callOptions?.schemas?.req?.(postFlexiblePlanningEndpoint.req) ??
                    postFlexiblePlanningEndpoint.req) as z.ZodType<TReq>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<PostFlexiblePlanningResponse>;
    };

    return useIdempotentMutation<
        PostFlexiblePlanningResponse,
        PostFlexiblePlanningRequest<TParams, TReq>,
        DefaultError,
        TContext
    >(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as PostFlexiblePlanningRequest<TParams, TReq>,
                expectedScope,
            ),
        {
            mutationKey: ["POST", "/tasks/{id}/flexible-planning"],
            ...mutationOptions,
        },
        postFlexiblePlanningEndpoint,
    );
}

const postFromJobDescriptionEndpoint = spellbook("/job-descriptions/{id}/tasks").POST();

export type PostFromJobDescriptionRequest<
    TParams = z.infer<typeof postFromJobDescriptionEndpoint.p>,
    TReq = z.infer<typeof postFromJobDescriptionEndpoint.req>,
> = {
    params: TParams;
    req: TReq;
} & RequestMeta;

const postFromJobDescriptionCall = (
    input: PostFromJobDescriptionRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(postFromJobDescriptionEndpoint, undefined, { expectedScope });
    return call(input);
};
type PostFromJobDescriptionResponse = Awaited<ReturnType<typeof postFromJobDescriptionCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type PostFromJobDescriptionMutationCallOptions<
    TParams = z.infer<typeof postFromJobDescriptionEndpoint.p>,
    TReq = z.infer<typeof postFromJobDescriptionEndpoint.req>,
    TRes = PostFromJobDescriptionResponse,
> = {
    schemas?: {
        params?: (schema: typeof postFromJobDescriptionEndpoint.p) => z.ZodType<TParams>;
        req?: (schema: typeof postFromJobDescriptionEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof postFromJobDescriptionEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for POST /job-descriptions/{id}/tasks.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type PostFromJobDescriptionMutationOptions<
    TParams = z.infer<typeof postFromJobDescriptionEndpoint.p>,
    TReq = z.infer<typeof postFromJobDescriptionEndpoint.req>,
    TRes = PostFromJobDescriptionResponse,
    TContext = unknown,
> = IdempotentMutationOptions<
    TRes,
    PostFromJobDescriptionRequest<TParams, TReq>,
    DefaultError,
    TContext
> & {
    call?: PostFromJobDescriptionMutationCallOptions<TParams, TReq, TRes>;
};

export function usePostFromJobDescriptionMutation<
    TParams = z.infer<typeof postFromJobDescriptionEndpoint.p>,
    TReq = z.infer<typeof postFromJobDescriptionEndpoint.req>,
    TRes = PostFromJobDescriptionResponse,
    TContext = unknown,
>(options?: PostFromJobDescriptionMutationOptions<TParams, TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: PostFromJobDescriptionRequest<TParams, TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, TReq, TRes>(
            {
                ...postFromJobDescriptionEndpoint,
                p: (callOptions?.schemas?.params?.(postFromJobDescriptionEndpoint.p) ??
                    postFromJobDescriptionEndpoint.p) as z.ZodType<TParams>,
                req: (callOptions?.schemas?.req?.(postFromJobDescriptionEndpoint.req) ??
                    postFromJobDescriptionEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(postFromJobDescriptionEndpoint.res) ??
                    postFromJobDescriptionEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<
        TRes,
        PostFromJobDescriptionRequest<TParams, TReq>,
        DefaultError,
        TContext
    >(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as PostFromJobDescriptionRequest<TParams, TReq>,
                expectedScope,
            ),
        {
            mutationKey: ["POST", "/job-descriptions/{id}/tasks"],
            ...mutationOptions,
        },
        postFromJobDescriptionEndpoint,
    );
}

const postGuestFeedbackEndpoint = spellbook("/guest/feedbacks").POST();

export type PostGuestFeedbackRequest<
    TQuery = z.infer<typeof postGuestFeedbackEndpoint.q>,
    TReq = z.infer<typeof postGuestFeedbackEndpoint.req>,
> = {
    query: TQuery;
    req: TReq;
} & RequestMeta;

const postGuestFeedbackCall = (
    input: PostGuestFeedbackRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(postGuestFeedbackEndpoint, undefined, { expectedScope });
    return call(input);
};
type PostGuestFeedbackResponse = Awaited<ReturnType<typeof postGuestFeedbackCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type PostGuestFeedbackMutationCallOptions<
    TQuery = z.infer<typeof postGuestFeedbackEndpoint.q>,
    TReq = z.infer<typeof postGuestFeedbackEndpoint.req>,
    TRes = PostGuestFeedbackResponse,
> = {
    schemas?: {
        query?: (schema: typeof postGuestFeedbackEndpoint.q) => z.ZodType<TQuery>;
        req?: (schema: typeof postGuestFeedbackEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof postGuestFeedbackEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/** Returns a TanStack mutation for POST /guest/feedbacks without idempotency retry behavior. */
export type PostGuestFeedbackMutationOptions<
    TQuery = z.infer<typeof postGuestFeedbackEndpoint.q>,
    TReq = z.infer<typeof postGuestFeedbackEndpoint.req>,
    TRes = PostGuestFeedbackResponse,
    TContext = unknown,
> = Omit<
    UseMutationOptions<TRes, DefaultError, PostGuestFeedbackRequest<TQuery, TReq>, TContext>,
    "mutationFn"
> & {
    call?: PostGuestFeedbackMutationCallOptions<TQuery, TReq, TRes>;
};

export function usePostGuestFeedbackMutation<
    TQuery = z.infer<typeof postGuestFeedbackEndpoint.q>,
    TReq = z.infer<typeof postGuestFeedbackEndpoint.req>,
    TRes = PostGuestFeedbackResponse,
    TContext = unknown,
>(options?: PostGuestFeedbackMutationOptions<TQuery, TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: PostGuestFeedbackRequest<TQuery, TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<undefined, TQuery, TReq, TRes>(
            {
                ...postGuestFeedbackEndpoint,
                q: (callOptions?.schemas?.query?.(postGuestFeedbackEndpoint.q) ??
                    postGuestFeedbackEndpoint.q) as z.ZodType<TQuery>,
                req: (callOptions?.schemas?.req?.(postGuestFeedbackEndpoint.req) ??
                    postGuestFeedbackEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(postGuestFeedbackEndpoint.res) ??
                    postGuestFeedbackEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call(input as unknown as Parameters<typeof call>[0]) as Promise<TRes>;
    };

    return useScopeAwareMutation<
        TRes,
        PostGuestFeedbackRequest<TQuery, TReq>,
        DefaultError,
        TContext
    >(
        (input, expectedScope) => execute(input, expectedScope),
        { ...mutationOptions, mutationKey: ["POST", "/guest/feedbacks"] },
        postGuestFeedbackEndpoint,
    );
}

const postJobDescriptionEndpoint = spellbook("/job-descriptions").POST();

export type PostJobDescriptionRequest<TReq = z.infer<typeof postJobDescriptionEndpoint.req>> = {
    req: TReq;
} & RequestMeta;

const postJobDescriptionCall = (
    input: PostJobDescriptionRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(postJobDescriptionEndpoint, undefined, { expectedScope });
    return call(input);
};
type PostJobDescriptionResponse = Awaited<ReturnType<typeof postJobDescriptionCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type PostJobDescriptionMutationCallOptions<
    TReq = z.infer<typeof postJobDescriptionEndpoint.req>,
    TRes = PostJobDescriptionResponse,
> = {
    schemas?: {
        req?: (schema: typeof postJobDescriptionEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof postJobDescriptionEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for POST /job-descriptions.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type PostJobDescriptionMutationOptions<
    TReq = z.infer<typeof postJobDescriptionEndpoint.req>,
    TRes = PostJobDescriptionResponse,
    TContext = unknown,
> = IdempotentMutationOptions<TRes, PostJobDescriptionRequest<TReq>, DefaultError, TContext> & {
    call?: PostJobDescriptionMutationCallOptions<TReq, TRes>;
};

export function usePostJobDescriptionMutation<
    TReq = z.infer<typeof postJobDescriptionEndpoint.req>,
    TRes = PostJobDescriptionResponse,
    TContext = unknown,
>(options?: PostJobDescriptionMutationOptions<TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: PostJobDescriptionRequest<TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<undefined, undefined, TReq, TRes>(
            {
                ...postJobDescriptionEndpoint,
                req: (callOptions?.schemas?.req?.(postJobDescriptionEndpoint.req) ??
                    postJobDescriptionEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(postJobDescriptionEndpoint.res) ??
                    postJobDescriptionEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<TRes, PostJobDescriptionRequest<TReq>, DefaultError, TContext>(
        (input, idempotencyKey, expectedScope) =>
            execute({ ...input, idempotencyKey } as PostJobDescriptionRequest<TReq>, expectedScope),
        {
            mutationKey: ["POST", "/job-descriptions"],
            ...mutationOptions,
        },
        postJobDescriptionEndpoint,
    );
}

const postLocationEndpoint = spellbook("/organisations/{organisationId}/locations").POST();

export type PostLocationRequest<
    TParams = z.infer<typeof postLocationEndpoint.p>,
    TReq = z.infer<typeof postLocationEndpoint.req>,
> = {
    params: TParams;
    req: TReq;
} & RequestMeta;

const postLocationCall = (input: PostLocationRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(postLocationEndpoint, undefined, { expectedScope });
    return call(input);
};
type PostLocationResponse = Awaited<ReturnType<typeof postLocationCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type PostLocationMutationCallOptions<
    TParams = z.infer<typeof postLocationEndpoint.p>,
    TReq = z.infer<typeof postLocationEndpoint.req>,
    TRes = PostLocationResponse,
> = {
    schemas?: {
        params?: (schema: typeof postLocationEndpoint.p) => z.ZodType<TParams>;
        req?: (schema: typeof postLocationEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof postLocationEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for POST /organisations/{organisationId}/locations.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type PostLocationMutationOptions<
    TParams = z.infer<typeof postLocationEndpoint.p>,
    TReq = z.infer<typeof postLocationEndpoint.req>,
    TRes = PostLocationResponse,
    TContext = unknown,
> = IdempotentMutationOptions<TRes, PostLocationRequest<TParams, TReq>, DefaultError, TContext> & {
    call?: PostLocationMutationCallOptions<TParams, TReq, TRes>;
};

export function usePostLocationMutation<
    TParams = z.infer<typeof postLocationEndpoint.p>,
    TReq = z.infer<typeof postLocationEndpoint.req>,
    TRes = PostLocationResponse,
    TContext = unknown,
>(options?: PostLocationMutationOptions<TParams, TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: PostLocationRequest<TParams, TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, TReq, TRes>(
            {
                ...postLocationEndpoint,
                p: (callOptions?.schemas?.params?.(postLocationEndpoint.p) ??
                    postLocationEndpoint.p) as z.ZodType<TParams>,
                req: (callOptions?.schemas?.req?.(postLocationEndpoint.req) ??
                    postLocationEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(postLocationEndpoint.res) ??
                    postLocationEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<TRes, PostLocationRequest<TParams, TReq>, DefaultError, TContext>(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as PostLocationRequest<TParams, TReq>,
                expectedScope,
            ),
        {
            mutationKey: ["POST", "/organisations/{organisationId}/locations"],
            ...mutationOptions,
        },
        postLocationEndpoint,
    );
}

const postMotiveEndpoint = spellbook("/motives").POST();

export type PostMotiveRequest<TReq = z.infer<typeof postMotiveEndpoint.req>> = {
    req: TReq;
} & RequestMeta;

const postMotiveCall = (input: PostMotiveRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(postMotiveEndpoint, undefined, { expectedScope });
    return call(input);
};
type PostMotiveResponse = Awaited<ReturnType<typeof postMotiveCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type PostMotiveMutationCallOptions<
    TReq = z.infer<typeof postMotiveEndpoint.req>,
    TRes = PostMotiveResponse,
> = {
    schemas?: {
        req?: (schema: typeof postMotiveEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof postMotiveEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for POST /motives.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type PostMotiveMutationOptions<
    TReq = z.infer<typeof postMotiveEndpoint.req>,
    TRes = PostMotiveResponse,
    TContext = unknown,
> = IdempotentMutationOptions<TRes, PostMotiveRequest<TReq>, DefaultError, TContext> & {
    call?: PostMotiveMutationCallOptions<TReq, TRes>;
};

export function usePostMotiveMutation<
    TReq = z.infer<typeof postMotiveEndpoint.req>,
    TRes = PostMotiveResponse,
    TContext = unknown,
>(options?: PostMotiveMutationOptions<TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (input: PostMotiveRequest<TReq>, expectedScope: ScopeContextBinding) => {
        const call = createCall<undefined, undefined, TReq, TRes>(
            {
                ...postMotiveEndpoint,
                req: (callOptions?.schemas?.req?.(postMotiveEndpoint.req) ??
                    postMotiveEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(postMotiveEndpoint.res) ??
                    postMotiveEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<TRes, PostMotiveRequest<TReq>, DefaultError, TContext>(
        (input, idempotencyKey, expectedScope) =>
            execute({ ...input, idempotencyKey } as PostMotiveRequest<TReq>, expectedScope),
        {
            mutationKey: ["POST", "/motives"],
            ...mutationOptions,
        },
        postMotiveEndpoint,
    );
}

const postRelationshipEndpoint = spellbook("/me/organisation/siders").POST();

export type PostRelationshipRequest<TReq = z.infer<typeof postRelationshipEndpoint.req>> = {
    req: TReq;
} & RequestMeta;

const postRelationshipCall = (
    input: PostRelationshipRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(postRelationshipEndpoint, undefined, { expectedScope });
    return call(input);
};
type PostRelationshipResponse = Awaited<ReturnType<typeof postRelationshipCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type PostRelationshipMutationCallOptions<
    TReq = z.infer<typeof postRelationshipEndpoint.req>,
> = {
    schemas?: {
        req?: (schema: typeof postRelationshipEndpoint.req) => z.ZodType<TReq>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for POST /me/organisation/siders.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type PostRelationshipMutationOptions<
    TReq = z.infer<typeof postRelationshipEndpoint.req>,
    TContext = unknown,
> = IdempotentMutationOptions<
    PostRelationshipResponse,
    PostRelationshipRequest<TReq>,
    DefaultError,
    TContext
> & {
    call?: PostRelationshipMutationCallOptions<TReq>;
};

export function usePostRelationshipMutation<
    TReq = z.infer<typeof postRelationshipEndpoint.req>,
    TContext = unknown,
>(options?: PostRelationshipMutationOptions<TReq, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (input: PostRelationshipRequest<TReq>, expectedScope: ScopeContextBinding) => {
        const call = createCall<undefined, undefined, TReq, undefined>(
            {
                ...postRelationshipEndpoint,
                req: (callOptions?.schemas?.req?.(postRelationshipEndpoint.req) ??
                    postRelationshipEndpoint.req) as z.ZodType<TReq>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<PostRelationshipResponse>;
    };

    return useIdempotentMutation<
        PostRelationshipResponse,
        PostRelationshipRequest<TReq>,
        DefaultError,
        TContext
    >(
        (input, idempotencyKey, expectedScope) =>
            execute({ ...input, idempotencyKey } as PostRelationshipRequest<TReq>, expectedScope),
        {
            mutationKey: ["POST", "/me/organisation/siders"],
            ...mutationOptions,
        },
        postRelationshipEndpoint,
    );
}

const postShiftsEndpoint = spellbook("/tasks/{id}/shifts").POST();

export type PostShiftsRequest<
    TParams = z.infer<typeof postShiftsEndpoint.p>,
    TReq = z.infer<typeof postShiftsEndpoint.req>,
> = {
    params: TParams;
    req: TReq;
} & RequestMeta;

const postShiftsCall = (input: PostShiftsRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(postShiftsEndpoint, undefined, { expectedScope });
    return call(input);
};
type PostShiftsResponse = Awaited<ReturnType<typeof postShiftsCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type PostShiftsMutationCallOptions<
    TParams = z.infer<typeof postShiftsEndpoint.p>,
    TReq = z.infer<typeof postShiftsEndpoint.req>,
    TRes = PostShiftsResponse,
> = {
    schemas?: {
        params?: (schema: typeof postShiftsEndpoint.p) => z.ZodType<TParams>;
        req?: (schema: typeof postShiftsEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof postShiftsEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for POST /tasks/{id}/shifts.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type PostShiftsMutationOptions<
    TParams = z.infer<typeof postShiftsEndpoint.p>,
    TReq = z.infer<typeof postShiftsEndpoint.req>,
    TRes = PostShiftsResponse,
    TContext = unknown,
> = IdempotentMutationOptions<TRes, PostShiftsRequest<TParams, TReq>, DefaultError, TContext> & {
    call?: PostShiftsMutationCallOptions<TParams, TReq, TRes>;
};

export function usePostShiftsMutation<
    TParams = z.infer<typeof postShiftsEndpoint.p>,
    TReq = z.infer<typeof postShiftsEndpoint.req>,
    TRes = PostShiftsResponse,
    TContext = unknown,
>(options?: PostShiftsMutationOptions<TParams, TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: PostShiftsRequest<TParams, TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, TReq, TRes>(
            {
                ...postShiftsEndpoint,
                p: (callOptions?.schemas?.params?.(postShiftsEndpoint.p) ??
                    postShiftsEndpoint.p) as z.ZodType<TParams>,
                req: (callOptions?.schemas?.req?.(postShiftsEndpoint.req) ??
                    postShiftsEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(postShiftsEndpoint.res) ??
                    postShiftsEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<TRes, PostShiftsRequest<TParams, TReq>, DefaultError, TContext>(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as PostShiftsRequest<TParams, TReq>,
                expectedScope,
            ),
        {
            mutationKey: ["POST", "/tasks/{id}/shifts"],
            ...mutationOptions,
        },
        postShiftsEndpoint,
    );
}

const putAttendanceDisputeEndpoint = spellbook(
    "/v1/attendances/{attendanceID}/dispute/{disputeID}",
).PUT();

export type PutAttendanceDisputeRequest<
    TParams = z.infer<typeof putAttendanceDisputeEndpoint.p>,
    TReq = z.infer<typeof putAttendanceDisputeEndpoint.req>,
> = {
    params: TParams;
    req: TReq;
} & RequestMeta;

const putAttendanceDisputeCall = (
    input: PutAttendanceDisputeRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(putAttendanceDisputeEndpoint, undefined, { expectedScope });
    return call(input);
};
type PutAttendanceDisputeResponse = Awaited<ReturnType<typeof putAttendanceDisputeCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type PutAttendanceDisputeMutationCallOptions<
    TParams = z.infer<typeof putAttendanceDisputeEndpoint.p>,
    TReq = z.infer<typeof putAttendanceDisputeEndpoint.req>,
    TRes = PutAttendanceDisputeResponse,
> = {
    schemas?: {
        params?: (schema: typeof putAttendanceDisputeEndpoint.p) => z.ZodType<TParams>;
        req?: (schema: typeof putAttendanceDisputeEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof putAttendanceDisputeEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for PUT /v1/attendances/{attendanceID}/dispute/{disputeID}.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type PutAttendanceDisputeMutationOptions<
    TParams = z.infer<typeof putAttendanceDisputeEndpoint.p>,
    TReq = z.infer<typeof putAttendanceDisputeEndpoint.req>,
    TRes = PutAttendanceDisputeResponse,
    TContext = unknown,
> = IdempotentMutationOptions<
    TRes,
    PutAttendanceDisputeRequest<TParams, TReq>,
    DefaultError,
    TContext
> & {
    call?: PutAttendanceDisputeMutationCallOptions<TParams, TReq, TRes>;
};

export function usePutAttendanceDisputeMutation<
    TParams = z.infer<typeof putAttendanceDisputeEndpoint.p>,
    TReq = z.infer<typeof putAttendanceDisputeEndpoint.req>,
    TRes = PutAttendanceDisputeResponse,
    TContext = unknown,
>(options?: PutAttendanceDisputeMutationOptions<TParams, TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: PutAttendanceDisputeRequest<TParams, TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, TReq, TRes>(
            {
                ...putAttendanceDisputeEndpoint,
                p: (callOptions?.schemas?.params?.(putAttendanceDisputeEndpoint.p) ??
                    putAttendanceDisputeEndpoint.p) as z.ZodType<TParams>,
                req: (callOptions?.schemas?.req?.(putAttendanceDisputeEndpoint.req) ??
                    putAttendanceDisputeEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(putAttendanceDisputeEndpoint.res) ??
                    putAttendanceDisputeEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<
        TRes,
        PutAttendanceDisputeRequest<TParams, TReq>,
        DefaultError,
        TContext
    >(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as PutAttendanceDisputeRequest<TParams, TReq>,
                expectedScope,
            ),
        {
            mutationKey: ["PUT", "/v1/attendances/{attendanceID}/dispute/{disputeID}"],
            ...mutationOptions,
        },
        putAttendanceDisputeEndpoint,
    );
}

const putInvoiceOrderFormMentionEndpoint = spellbook("/invoice/order-form-mention").PUT();

export type PutInvoiceOrderFormMentionRequest<
    TReq = z.infer<typeof putInvoiceOrderFormMentionEndpoint.req>,
> = {
    req: TReq;
} & RequestMeta;

const putInvoiceOrderFormMentionCall = (
    input: PutInvoiceOrderFormMentionRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(putInvoiceOrderFormMentionEndpoint, undefined, { expectedScope });
    return call(input);
};
type PutInvoiceOrderFormMentionResponse = Awaited<
    ReturnType<typeof putInvoiceOrderFormMentionCall>
>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type PutInvoiceOrderFormMentionMutationCallOptions<
    TReq = z.infer<typeof putInvoiceOrderFormMentionEndpoint.req>,
    TRes = PutInvoiceOrderFormMentionResponse,
> = {
    schemas?: {
        req?: (schema: typeof putInvoiceOrderFormMentionEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof putInvoiceOrderFormMentionEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/** Returns a TanStack mutation for PUT /invoice/order-form-mention without idempotency retry behavior. */
export type PutInvoiceOrderFormMentionMutationOptions<
    TReq = z.infer<typeof putInvoiceOrderFormMentionEndpoint.req>,
    TRes = PutInvoiceOrderFormMentionResponse,
    TContext = unknown,
> = Omit<
    UseMutationOptions<TRes, DefaultError, PutInvoiceOrderFormMentionRequest<TReq>, TContext>,
    "mutationFn"
> & {
    call?: PutInvoiceOrderFormMentionMutationCallOptions<TReq, TRes>;
};

export function usePutInvoiceOrderFormMentionMutation<
    TReq = z.infer<typeof putInvoiceOrderFormMentionEndpoint.req>,
    TRes = PutInvoiceOrderFormMentionResponse,
    TContext = unknown,
>(options?: PutInvoiceOrderFormMentionMutationOptions<TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: PutInvoiceOrderFormMentionRequest<TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<undefined, undefined, TReq, TRes>(
            {
                ...putInvoiceOrderFormMentionEndpoint,
                req: (callOptions?.schemas?.req?.(putInvoiceOrderFormMentionEndpoint.req) ??
                    putInvoiceOrderFormMentionEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(putInvoiceOrderFormMentionEndpoint.res) ??
                    putInvoiceOrderFormMentionEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call(input as unknown as Parameters<typeof call>[0]) as Promise<TRes>;
    };

    return useScopeAwareMutation<
        TRes,
        PutInvoiceOrderFormMentionRequest<TReq>,
        DefaultError,
        TContext
    >(
        (input, expectedScope) => execute(input, expectedScope),
        { ...mutationOptions, mutationKey: ["PUT", "/invoice/order-form-mention"] },
        putInvoiceOrderFormMentionEndpoint,
    );
}

const reInviteUserEndpoint = spellbook("/me/organisations/users/{id}/invite").POST();

export type ReInviteUserRequest<TParams = z.infer<typeof reInviteUserEndpoint.p>> = {
    params: TParams;
} & RequestMeta;

const reInviteUserCall = (input: ReInviteUserRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(reInviteUserEndpoint, undefined, { expectedScope });
    return call(input);
};
type ReInviteUserResponse = Awaited<ReturnType<typeof reInviteUserCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type ReInviteUserMutationCallOptions<TParams = z.infer<typeof reInviteUserEndpoint.p>> = {
    schemas?: {
        params?: (schema: typeof reInviteUserEndpoint.p) => z.ZodType<TParams>;
    };
};
/**
 * Returns an idempotent TanStack mutation for POST /me/organisations/users/{id}/invite.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type ReInviteUserMutationOptions<
    TParams = z.infer<typeof reInviteUserEndpoint.p>,
    TContext = unknown,
> = IdempotentMutationOptions<
    ReInviteUserResponse,
    ReInviteUserRequest<TParams>,
    DefaultError,
    TContext
> & {
    call?: ReInviteUserMutationCallOptions<TParams>;
};

export function useReInviteUserMutation<
    TParams = z.infer<typeof reInviteUserEndpoint.p>,
    TContext = unknown,
>(options?: ReInviteUserMutationOptions<TParams, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (input: ReInviteUserRequest<TParams>, expectedScope: ScopeContextBinding) => {
        const call = createCall<TParams, undefined, undefined, undefined>(
            {
                ...reInviteUserEndpoint,
                p: (callOptions?.schemas?.params?.(reInviteUserEndpoint.p) ??
                    reInviteUserEndpoint.p) as z.ZodType<TParams>,
            },
            undefined,
            {
                expectedScope,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<ReInviteUserResponse>;
    };

    return useIdempotentMutation<
        ReInviteUserResponse,
        ReInviteUserRequest<TParams>,
        DefaultError,
        TContext
    >(
        (input, idempotencyKey, expectedScope) =>
            execute({ ...input, idempotencyKey } as ReInviteUserRequest<TParams>, expectedScope),
        {
            mutationKey: ["POST", "/me/organisations/users/{id}/invite"],
            ...mutationOptions,
        },
        reInviteUserEndpoint,
    );
}

const regenerateDocumentsEndpoint = spellbook(
    "/organisations/{organisationId}/documents/regenerate",
).PUT();

export type RegenerateDocumentsRequest<
    TParams = z.infer<typeof regenerateDocumentsEndpoint.p>,
    TReq = z.infer<typeof regenerateDocumentsEndpoint.req>,
> = {
    params: TParams;
    req: TReq;
} & RequestMeta;

const regenerateDocumentsCall = (
    input: RegenerateDocumentsRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(regenerateDocumentsEndpoint, undefined, { expectedScope });
    return call(input);
};
type RegenerateDocumentsResponse = Awaited<ReturnType<typeof regenerateDocumentsCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type RegenerateDocumentsMutationCallOptions<
    TParams = z.infer<typeof regenerateDocumentsEndpoint.p>,
    TReq = z.infer<typeof regenerateDocumentsEndpoint.req>,
> = {
    schemas?: {
        params?: (schema: typeof regenerateDocumentsEndpoint.p) => z.ZodType<TParams>;
        req?: (schema: typeof regenerateDocumentsEndpoint.req) => z.ZodType<TReq>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for PUT /organisations/{organisationId}/documents/regenerate.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type RegenerateDocumentsMutationOptions<
    TParams = z.infer<typeof regenerateDocumentsEndpoint.p>,
    TReq = z.infer<typeof regenerateDocumentsEndpoint.req>,
    TContext = unknown,
> = IdempotentMutationOptions<
    RegenerateDocumentsResponse,
    RegenerateDocumentsRequest<TParams, TReq>,
    DefaultError,
    TContext
> & {
    call?: RegenerateDocumentsMutationCallOptions<TParams, TReq>;
};

export function useRegenerateDocumentsMutation<
    TParams = z.infer<typeof regenerateDocumentsEndpoint.p>,
    TReq = z.infer<typeof regenerateDocumentsEndpoint.req>,
    TContext = unknown,
>(options?: RegenerateDocumentsMutationOptions<TParams, TReq, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: RegenerateDocumentsRequest<TParams, TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, TReq, undefined>(
            {
                ...regenerateDocumentsEndpoint,
                p: (callOptions?.schemas?.params?.(regenerateDocumentsEndpoint.p) ??
                    regenerateDocumentsEndpoint.p) as z.ZodType<TParams>,
                req: (callOptions?.schemas?.req?.(regenerateDocumentsEndpoint.req) ??
                    regenerateDocumentsEndpoint.req) as z.ZodType<TReq>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<RegenerateDocumentsResponse>;
    };

    return useIdempotentMutation<
        RegenerateDocumentsResponse,
        RegenerateDocumentsRequest<TParams, TReq>,
        DefaultError,
        TContext
    >(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as RegenerateDocumentsRequest<TParams, TReq>,
                expectedScope,
            ),
        {
            mutationKey: ["PUT", "/organisations/{organisationId}/documents/regenerate"],
            ...mutationOptions,
        },
        regenerateDocumentsEndpoint,
    );
}

const requestPermanentContractEndpoint = spellbook("/permanent-contracts/requests").POST();

export type RequestPermanentContractRequest<
    TReq = z.infer<typeof requestPermanentContractEndpoint.req>,
> = {
    req: TReq;
} & RequestMeta;

const requestPermanentContractCall = (
    input: RequestPermanentContractRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(requestPermanentContractEndpoint, undefined, { expectedScope });
    return call(input);
};
type RequestPermanentContractResponse = Awaited<ReturnType<typeof requestPermanentContractCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type RequestPermanentContractMutationCallOptions<
    TReq = z.infer<typeof requestPermanentContractEndpoint.req>,
> = {
    schemas?: {
        req?: (schema: typeof requestPermanentContractEndpoint.req) => z.ZodType<TReq>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for POST /permanent-contracts/requests.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type RequestPermanentContractMutationOptions<
    TReq = z.infer<typeof requestPermanentContractEndpoint.req>,
    TContext = unknown,
> = IdempotentMutationOptions<
    RequestPermanentContractResponse,
    RequestPermanentContractRequest<TReq>,
    DefaultError,
    TContext
> & {
    call?: RequestPermanentContractMutationCallOptions<TReq>;
};

export function useRequestPermanentContractMutation<
    TReq = z.infer<typeof requestPermanentContractEndpoint.req>,
    TContext = unknown,
>(options?: RequestPermanentContractMutationOptions<TReq, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: RequestPermanentContractRequest<TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<undefined, undefined, TReq, undefined>(
            {
                ...requestPermanentContractEndpoint,
                req: (callOptions?.schemas?.req?.(requestPermanentContractEndpoint.req) ??
                    requestPermanentContractEndpoint.req) as z.ZodType<TReq>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<RequestPermanentContractResponse>;
    };

    return useIdempotentMutation<
        RequestPermanentContractResponse,
        RequestPermanentContractRequest<TReq>,
        DefaultError,
        TContext
    >(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as RequestPermanentContractRequest<TReq>,
                expectedScope,
            ),
        {
            mutationKey: ["POST", "/permanent-contracts/requests"],
            ...mutationOptions,
        },
        requestPermanentContractEndpoint,
    );
}

const resetPaymentMethodEndpoint = spellbook(
    "/organisations/{organisationId}/payment-method",
).DELETE();

export type ResetPaymentMethodRequest<TParams = z.infer<typeof resetPaymentMethodEndpoint.p>> = {
    params: TParams;
} & RequestMeta;

const resetPaymentMethodCall = (
    input: ResetPaymentMethodRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(resetPaymentMethodEndpoint, undefined, { expectedScope });
    return call(input);
};
type ResetPaymentMethodResponse = Awaited<ReturnType<typeof resetPaymentMethodCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type ResetPaymentMethodMutationCallOptions<
    TParams = z.infer<typeof resetPaymentMethodEndpoint.p>,
> = {
    schemas?: {
        params?: (schema: typeof resetPaymentMethodEndpoint.p) => z.ZodType<TParams>;
    };
};
/**
 * Returns an idempotent TanStack mutation for DELETE /organisations/{organisationId}/payment-method.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type ResetPaymentMethodMutationOptions<
    TParams = z.infer<typeof resetPaymentMethodEndpoint.p>,
    TContext = unknown,
> = IdempotentMutationOptions<
    ResetPaymentMethodResponse,
    ResetPaymentMethodRequest<TParams>,
    DefaultError,
    TContext
> & {
    call?: ResetPaymentMethodMutationCallOptions<TParams>;
};

export function useResetPaymentMethodMutation<
    TParams = z.infer<typeof resetPaymentMethodEndpoint.p>,
    TContext = unknown,
>(options?: ResetPaymentMethodMutationOptions<TParams, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: ResetPaymentMethodRequest<TParams>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, undefined, undefined>(
            {
                ...resetPaymentMethodEndpoint,
                p: (callOptions?.schemas?.params?.(resetPaymentMethodEndpoint.p) ??
                    resetPaymentMethodEndpoint.p) as z.ZodType<TParams>,
            },
            undefined,
            {
                expectedScope,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<ResetPaymentMethodResponse>;
    };

    return useIdempotentMutation<
        ResetPaymentMethodResponse,
        ResetPaymentMethodRequest<TParams>,
        DefaultError,
        TContext
    >(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as ResetPaymentMethodRequest<TParams>,
                expectedScope,
            ),
        {
            mutationKey: ["DELETE", "/organisations/{organisationId}/payment-method"],
            ...mutationOptions,
        },
        resetPaymentMethodEndpoint,
    );
}

const runConfirmAutomationsEndpoint = spellbook("/tasks/{id}/confirm-automations").POST();

export type RunConfirmAutomationsRequest<
    TParams = z.infer<typeof runConfirmAutomationsEndpoint.p>,
> = {
    params: TParams;
} & RequestMeta;

const runConfirmAutomationsCall = (
    input: RunConfirmAutomationsRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(runConfirmAutomationsEndpoint, undefined, { expectedScope });
    return call(input);
};
type RunConfirmAutomationsResponse = Awaited<ReturnType<typeof runConfirmAutomationsCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type RunConfirmAutomationsMutationCallOptions<
    TParams = z.infer<typeof runConfirmAutomationsEndpoint.p>,
> = {
    schemas?: {
        params?: (schema: typeof runConfirmAutomationsEndpoint.p) => z.ZodType<TParams>;
    };
};
/**
 * Returns an idempotent TanStack mutation for POST /tasks/{id}/confirm-automations.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type RunConfirmAutomationsMutationOptions<
    TParams = z.infer<typeof runConfirmAutomationsEndpoint.p>,
    TContext = unknown,
> = IdempotentMutationOptions<
    RunConfirmAutomationsResponse,
    RunConfirmAutomationsRequest<TParams>,
    DefaultError,
    TContext
> & {
    call?: RunConfirmAutomationsMutationCallOptions<TParams>;
};

export function useRunConfirmAutomationsMutation<
    TParams = z.infer<typeof runConfirmAutomationsEndpoint.p>,
    TContext = unknown,
>(options?: RunConfirmAutomationsMutationOptions<TParams, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: RunConfirmAutomationsRequest<TParams>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, undefined, undefined>(
            {
                ...runConfirmAutomationsEndpoint,
                p: (callOptions?.schemas?.params?.(runConfirmAutomationsEndpoint.p) ??
                    runConfirmAutomationsEndpoint.p) as z.ZodType<TParams>,
            },
            undefined,
            {
                expectedScope,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<RunConfirmAutomationsResponse>;
    };

    return useIdempotentMutation<
        RunConfirmAutomationsResponse,
        RunConfirmAutomationsRequest<TParams>,
        DefaultError,
        TContext
    >(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as RunConfirmAutomationsRequest<TParams>,
                expectedScope,
            ),
        {
            mutationKey: ["POST", "/tasks/{id}/confirm-automations"],
            ...mutationOptions,
        },
        runConfirmAutomationsEndpoint,
    );
}

const searchJobTitlesEndpoint = spellbook("/search/job-titles").GET();

export type SearchJobTitlesRequest<TQuery = z.infer<typeof searchJobTitlesEndpoint.q>> = {
    query: TQuery;
} & RequestMeta;

const searchJobTitlesCall = (
    input: SearchJobTitlesRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(searchJobTitlesEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /search/job-titles. */
export function searchJobTitlesQueryOptions<
    TSelected = Awaited<ReturnType<typeof searchJobTitlesCall>>,
>(
    input: SearchJobTitlesRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof searchJobTitlesCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: searchJobTitlesEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/search/job-titles", input.query] as const,
        queryFn: ({ signal }, expectedScope) =>
            searchJobTitlesCall({ ...input, signal }, expectedScope),
        options,
    });
}

const setFixedWeeklyDurationEndpoint = spellbook("/contracts/{contractId}/weeklyduration").PUT();

export type SetFixedWeeklyDurationRequest<
    TParams = z.infer<typeof setFixedWeeklyDurationEndpoint.p>,
    TReq = z.infer<typeof setFixedWeeklyDurationEndpoint.req>,
> = {
    params: TParams;
    req: TReq;
} & RequestMeta;

const setFixedWeeklyDurationCall = (
    input: SetFixedWeeklyDurationRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(setFixedWeeklyDurationEndpoint, undefined, { expectedScope });
    return call(input);
};
type SetFixedWeeklyDurationResponse = Awaited<ReturnType<typeof setFixedWeeklyDurationCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type SetFixedWeeklyDurationMutationCallOptions<
    TParams = z.infer<typeof setFixedWeeklyDurationEndpoint.p>,
    TReq = z.infer<typeof setFixedWeeklyDurationEndpoint.req>,
> = {
    schemas?: {
        params?: (schema: typeof setFixedWeeklyDurationEndpoint.p) => z.ZodType<TParams>;
        req?: (schema: typeof setFixedWeeklyDurationEndpoint.req) => z.ZodType<TReq>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/** Returns a TanStack mutation for PUT /contracts/{contractId}/weeklyduration without idempotency retry behavior. */
export type SetFixedWeeklyDurationMutationOptions<
    TParams = z.infer<typeof setFixedWeeklyDurationEndpoint.p>,
    TReq = z.infer<typeof setFixedWeeklyDurationEndpoint.req>,
    TContext = unknown,
> = Omit<
    UseMutationOptions<
        SetFixedWeeklyDurationResponse,
        DefaultError,
        SetFixedWeeklyDurationRequest<TParams, TReq>,
        TContext
    >,
    "mutationFn"
> & {
    call?: SetFixedWeeklyDurationMutationCallOptions<TParams, TReq>;
};

export function useSetFixedWeeklyDurationMutation<
    TParams = z.infer<typeof setFixedWeeklyDurationEndpoint.p>,
    TReq = z.infer<typeof setFixedWeeklyDurationEndpoint.req>,
    TContext = unknown,
>(options?: SetFixedWeeklyDurationMutationOptions<TParams, TReq, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: SetFixedWeeklyDurationRequest<TParams, TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, TReq, undefined>(
            {
                ...setFixedWeeklyDurationEndpoint,
                p: (callOptions?.schemas?.params?.(setFixedWeeklyDurationEndpoint.p) ??
                    setFixedWeeklyDurationEndpoint.p) as z.ZodType<TParams>,
                req: (callOptions?.schemas?.req?.(setFixedWeeklyDurationEndpoint.req) ??
                    setFixedWeeklyDurationEndpoint.req) as z.ZodType<TReq>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call(
            input as unknown as Parameters<typeof call>[0],
        ) as Promise<SetFixedWeeklyDurationResponse>;
    };

    return useScopeAwareMutation<
        SetFixedWeeklyDurationResponse,
        SetFixedWeeklyDurationRequest<TParams, TReq>,
        DefaultError,
        TContext
    >(
        (input, expectedScope) => execute(input, expectedScope),
        { ...mutationOptions, mutationKey: ["PUT", "/contracts/{contractId}/weeklyduration"] },
        setFixedWeeklyDurationEndpoint,
    );
}

const siderListSidersEndpoint = spellbook("/siders/search").GET();

export type SiderListSidersRequest<TQuery = z.infer<typeof siderListSidersEndpoint.q>> = {
    query: TQuery;
} & RequestMeta;

const siderListSidersCall = (
    input: SiderListSidersRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(siderListSidersEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /siders/search. */
export function siderListSidersQueryOptions<
    TSelected = Awaited<ReturnType<typeof siderListSidersCall>>,
>(
    input: SiderListSidersRequest,
    options?: ScopeQueryOptions<
        Awaited<ReturnType<typeof siderListSidersCall>>,
        DefaultError,
        TSelected
    >,
) {
    return createScopeQueryOptions({
        operation: siderListSidersEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/siders/search", input.query] as const,
        queryFn: ({ signal }, expectedScope) =>
            siderListSidersCall({ ...input, signal }, expectedScope),
        options,
    });
}

const sides3proxyUploadHandlerEndpoint = spellbook("/upload/{type}/{id}").POST();

export type Sides3proxyUploadHandlerRequest<
    TParams = z.infer<typeof sides3proxyUploadHandlerEndpoint.p>,
> = {
    params: TParams;
} & RequestMeta;

const sides3proxyUploadHandlerCall = (
    input: Sides3proxyUploadHandlerRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(sides3proxyUploadHandlerEndpoint, undefined, { expectedScope });
    return call(input);
};
type Sides3proxyUploadHandlerResponse = Awaited<ReturnType<typeof sides3proxyUploadHandlerCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type Sides3proxyUploadHandlerMutationCallOptions<
    TParams = z.infer<typeof sides3proxyUploadHandlerEndpoint.p>,
> = {
    schemas?: {
        params?: (schema: typeof sides3proxyUploadHandlerEndpoint.p) => z.ZodType<TParams>;
    };
};
/** Returns a TanStack mutation for POST /upload/{type}/{id} without idempotency retry behavior. */
export type Sides3proxyUploadHandlerMutationOptions<
    TParams = z.infer<typeof sides3proxyUploadHandlerEndpoint.p>,
    TContext = unknown,
> = Omit<
    UseMutationOptions<
        Sides3proxyUploadHandlerResponse,
        DefaultError,
        Sides3proxyUploadHandlerRequest<TParams>,
        TContext
    >,
    "mutationFn"
> & {
    call?: Sides3proxyUploadHandlerMutationCallOptions<TParams>;
};

export function useSides3proxyUploadHandlerMutation<
    TParams = z.infer<typeof sides3proxyUploadHandlerEndpoint.p>,
    TContext = unknown,
>(options?: Sides3proxyUploadHandlerMutationOptions<TParams, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: Sides3proxyUploadHandlerRequest<TParams>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, undefined, undefined>(
            {
                ...sides3proxyUploadHandlerEndpoint,
                p: (callOptions?.schemas?.params?.(sides3proxyUploadHandlerEndpoint.p) ??
                    sides3proxyUploadHandlerEndpoint.p) as z.ZodType<TParams>,
            },
            undefined,
            {
                expectedScope,
            },
        );
        return call(
            input as unknown as Parameters<typeof call>[0],
        ) as Promise<Sides3proxyUploadHandlerResponse>;
    };

    return useScopeAwareMutation<
        Sides3proxyUploadHandlerResponse,
        Sides3proxyUploadHandlerRequest<TParams>,
        DefaultError,
        TContext
    >(
        (input, expectedScope) => execute(input, expectedScope),
        { ...mutationOptions, mutationKey: ["POST", "/upload/{type}/{id}"] },
        sides3proxyUploadHandlerEndpoint,
    );
}

const signDocumentEndpoint = spellbook(
    "/organisations/{organisationId}/document/{documentId}/sign",
).PUT();

export type SignDocumentRequest<TParams = z.infer<typeof signDocumentEndpoint.p>> = {
    params: TParams;
} & RequestMeta;

const signDocumentCall = (input: SignDocumentRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(signDocumentEndpoint, undefined, { expectedScope });
    return call(input);
};
type SignDocumentResponse = Awaited<ReturnType<typeof signDocumentCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type SignDocumentMutationCallOptions<TParams = z.infer<typeof signDocumentEndpoint.p>> = {
    schemas?: {
        params?: (schema: typeof signDocumentEndpoint.p) => z.ZodType<TParams>;
    };
};
/**
 * Returns an idempotent TanStack mutation for PUT /organisations/{organisationId}/document/{documentId}/sign.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type SignDocumentMutationOptions<
    TParams = z.infer<typeof signDocumentEndpoint.p>,
    TContext = unknown,
> = IdempotentMutationOptions<
    SignDocumentResponse,
    SignDocumentRequest<TParams>,
    DefaultError,
    TContext
> & {
    call?: SignDocumentMutationCallOptions<TParams>;
};

export function useSignDocumentMutation<
    TParams = z.infer<typeof signDocumentEndpoint.p>,
    TContext = unknown,
>(options?: SignDocumentMutationOptions<TParams, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (input: SignDocumentRequest<TParams>, expectedScope: ScopeContextBinding) => {
        const call = createCall<TParams, undefined, undefined, undefined>(
            {
                ...signDocumentEndpoint,
                p: (callOptions?.schemas?.params?.(signDocumentEndpoint.p) ??
                    signDocumentEndpoint.p) as z.ZodType<TParams>,
            },
            undefined,
            {
                expectedScope,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<SignDocumentResponse>;
    };

    return useIdempotentMutation<
        SignDocumentResponse,
        SignDocumentRequest<TParams>,
        DefaultError,
        TContext
    >(
        (input, idempotencyKey, expectedScope) =>
            execute({ ...input, idempotencyKey } as SignDocumentRequest<TParams>, expectedScope),
        {
            mutationKey: ["PUT", "/organisations/{organisationId}/document/{documentId}/sign"],
            ...mutationOptions,
        },
        signDocumentEndpoint,
    );
}

const signUpEndpoint = spellbook("/auth/signup").POST();

export type SignUpRequest<TReq = z.infer<typeof signUpEndpoint.req>> = {
    req: TReq;
} & RequestMeta;

const signUpCall = (input: SignUpRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(signUpEndpoint, undefined, { expectedScope });
    return call(input);
};
type SignUpResponse = Awaited<ReturnType<typeof signUpCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type SignUpMutationCallOptions<
    TReq = z.infer<typeof signUpEndpoint.req>,
    TRes = SignUpResponse,
> = {
    schemas?: {
        req?: (schema: typeof signUpEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof signUpEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/** Returns a TanStack mutation for POST /auth/signup without idempotency retry behavior. */
export type SignUpMutationOptions<
    TReq = z.infer<typeof signUpEndpoint.req>,
    TRes = SignUpResponse,
    TContext = unknown,
> = Omit<UseMutationOptions<TRes, DefaultError, SignUpRequest<TReq>, TContext>, "mutationFn"> & {
    call?: SignUpMutationCallOptions<TReq, TRes>;
};

export function useSignUpMutation<
    TReq = z.infer<typeof signUpEndpoint.req>,
    TRes = SignUpResponse,
    TContext = unknown,
>(options?: SignUpMutationOptions<TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (input: SignUpRequest<TReq>, expectedScope: ScopeContextBinding) => {
        const call = createCall<undefined, undefined, TReq, TRes>(
            {
                ...signUpEndpoint,
                req: (callOptions?.schemas?.req?.(signUpEndpoint.req) ??
                    signUpEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(signUpEndpoint.res) ??
                    signUpEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call(input as unknown as Parameters<typeof call>[0]) as Promise<TRes>;
    };

    return useScopeAwareMutation<TRes, SignUpRequest<TReq>, DefaultError, TContext>(
        (input, expectedScope) => execute(input, expectedScope),
        { ...mutationOptions, mutationKey: ["POST", "/auth/signup"] },
        signUpEndpoint,
    );
}

const taskGetEndpoint = spellbook("/tasks/{id}").GET();

export type TaskGetRequest<TParams = z.infer<typeof taskGetEndpoint.p>> = {
    params: TParams;
} & RequestMeta;

const taskGetCall = (input: TaskGetRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(taskGetEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /tasks/{id}. */
export function taskGetQueryOptions<TSelected = Awaited<ReturnType<typeof taskGetCall>>>(
    input: TaskGetRequest,
    options?: ScopeQueryOptions<Awaited<ReturnType<typeof taskGetCall>>, DefaultError, TSelected>,
) {
    return createScopeQueryOptions({
        operation: taskGetEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/tasks/{id}", input.params] as const,
        queryFn: ({ signal }, expectedScope) => taskGetCall({ ...input, signal }, expectedScope),
        options,
    });
}

const taskListEndpoint = spellbook("/me/tasks").GET();

export type TaskListRequest<TQuery = z.infer<typeof taskListEndpoint.q>> = {
    query: TQuery;
} & RequestMeta;

const taskListCall = (input: TaskListRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(taskListEndpoint, undefined, { expectedScope });
    return call(input);
};
/** Returns TanStack Query options for GET /me/tasks. */
export function taskListQueryOptions<TSelected = Awaited<ReturnType<typeof taskListCall>>>(
    input: TaskListRequest,
    options?: ScopeQueryOptions<Awaited<ReturnType<typeof taskListCall>>, DefaultError, TSelected>,
) {
    return createScopeQueryOptions({
        operation: taskListEndpoint,
        requestContext: input.requestContext,
        queryKey: ["GET", "/me/tasks", input.query] as const,
        queryFn: ({ signal }, expectedScope) => taskListCall({ ...input, signal }, expectedScope),
        options,
    });
}

const updateFeedbackEndpoint = spellbook("/v1/feedback/{feedbackId}").PUT();

export type UpdateFeedbackRequest<
    TParams = z.infer<typeof updateFeedbackEndpoint.p>,
    TReq = z.infer<typeof updateFeedbackEndpoint.req>,
> = {
    params: TParams;
    req: TReq;
} & RequestMeta;

const updateFeedbackCall = (input: UpdateFeedbackRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(updateFeedbackEndpoint, undefined, { expectedScope });
    return call(input);
};
type UpdateFeedbackResponse = Awaited<ReturnType<typeof updateFeedbackCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type UpdateFeedbackMutationCallOptions<
    TParams = z.infer<typeof updateFeedbackEndpoint.p>,
    TReq = z.infer<typeof updateFeedbackEndpoint.req>,
    TRes = UpdateFeedbackResponse,
> = {
    schemas?: {
        params?: (schema: typeof updateFeedbackEndpoint.p) => z.ZodType<TParams>;
        req?: (schema: typeof updateFeedbackEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof updateFeedbackEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for PUT /v1/feedback/{feedbackId}.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type UpdateFeedbackMutationOptions<
    TParams = z.infer<typeof updateFeedbackEndpoint.p>,
    TReq = z.infer<typeof updateFeedbackEndpoint.req>,
    TRes = UpdateFeedbackResponse,
    TContext = unknown,
> = IdempotentMutationOptions<
    TRes,
    UpdateFeedbackRequest<TParams, TReq>,
    DefaultError,
    TContext
> & {
    call?: UpdateFeedbackMutationCallOptions<TParams, TReq, TRes>;
};

export function useUpdateFeedbackMutation<
    TParams = z.infer<typeof updateFeedbackEndpoint.p>,
    TReq = z.infer<typeof updateFeedbackEndpoint.req>,
    TRes = UpdateFeedbackResponse,
    TContext = unknown,
>(options?: UpdateFeedbackMutationOptions<TParams, TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: UpdateFeedbackRequest<TParams, TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<TParams, undefined, TReq, TRes>(
            {
                ...updateFeedbackEndpoint,
                p: (callOptions?.schemas?.params?.(updateFeedbackEndpoint.p) ??
                    updateFeedbackEndpoint.p) as z.ZodType<TParams>,
                req: (callOptions?.schemas?.req?.(updateFeedbackEndpoint.req) ??
                    updateFeedbackEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(updateFeedbackEndpoint.res) ??
                    updateFeedbackEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<
        TRes,
        UpdateFeedbackRequest<TParams, TReq>,
        DefaultError,
        TContext
    >(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as UpdateFeedbackRequest<TParams, TReq>,
                expectedScope,
            ),
        {
            mutationKey: ["PUT", "/v1/feedback/{feedbackId}"],
            ...mutationOptions,
        },
        updateFeedbackEndpoint,
    );
}

const updateUserEndpoint = spellbook("/v1/users/me").PUT();

export type UpdateUserRequest<TReq = z.infer<typeof updateUserEndpoint.req>> = {
    req: TReq;
} & RequestMeta;

const updateUserCall = (input: UpdateUserRequest, expectedScope?: ScopeContextBinding) => {
    const call = createCall(updateUserEndpoint, undefined, { expectedScope });
    return call(input);
};
type UpdateUserResponse = Awaited<ReturnType<typeof updateUserCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type UpdateUserMutationCallOptions<TReq = z.infer<typeof updateUserEndpoint.req>> = {
    schemas?: {
        req?: (schema: typeof updateUserEndpoint.req) => z.ZodType<TReq>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for PUT /v1/users/me.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type UpdateUserMutationOptions<
    TReq = z.infer<typeof updateUserEndpoint.req>,
    TContext = unknown,
> = IdempotentMutationOptions<
    UpdateUserResponse,
    UpdateUserRequest<TReq>,
    DefaultError,
    TContext
> & {
    call?: UpdateUserMutationCallOptions<TReq>;
};

export function useUpdateUserMutation<
    TReq = z.infer<typeof updateUserEndpoint.req>,
    TContext = unknown,
>(options?: UpdateUserMutationOptions<TReq, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (input: UpdateUserRequest<TReq>, expectedScope: ScopeContextBinding) => {
        const call = createCall<undefined, undefined, TReq, undefined>(
            {
                ...updateUserEndpoint,
                req: (callOptions?.schemas?.req?.(updateUserEndpoint.req) ??
                    updateUserEndpoint.req) as z.ZodType<TReq>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<UpdateUserResponse>;
    };

    return useIdempotentMutation<
        UpdateUserResponse,
        UpdateUserRequest<TReq>,
        DefaultError,
        TContext
    >(
        (input, idempotencyKey, expectedScope) =>
            execute({ ...input, idempotencyKey } as UpdateUserRequest<TReq>, expectedScope),
        {
            mutationKey: ["PUT", "/v1/users/me"],
            ...mutationOptions,
        },
        updateUserEndpoint,
    );
}

const validateAttendancesEndpoint = spellbook("/v1/attendances/validate").POST();

export type ValidateAttendancesRequest<TReq = z.infer<typeof validateAttendancesEndpoint.req>> = {
    req: TReq;
} & RequestMeta;

const validateAttendancesCall = (
    input: ValidateAttendancesRequest,
    expectedScope?: ScopeContextBinding,
) => {
    const call = createCall(validateAttendancesEndpoint, undefined, { expectedScope });
    return call(input);
};
type ValidateAttendancesResponse = Awaited<ReturnType<typeof validateAttendancesCall>>;
/** Call-level validation and diagnostic customizations for the generated mutation. */
export type ValidateAttendancesMutationCallOptions<
    TReq = z.infer<typeof validateAttendancesEndpoint.req>,
    TRes = ValidateAttendancesResponse,
> = {
    schemas?: {
        req?: (schema: typeof validateAttendancesEndpoint.req) => z.ZodType<TReq>;
        res?: (schema: typeof validateAttendancesEndpoint.res) => z.ZodType<TRes>;
    };
    getRequestErrorExtra?: (req: TReq) => Record<string, unknown>;
};
/**
 * Returns an idempotent TanStack mutation for POST /v1/attendances/validate.
 * Equivalent calls after a failure automatically reuse their idempotency key.
 * Use `restart(...)` only when the same input represents a new operation.
 */
export type ValidateAttendancesMutationOptions<
    TReq = z.infer<typeof validateAttendancesEndpoint.req>,
    TRes = ValidateAttendancesResponse,
    TContext = unknown,
> = IdempotentMutationOptions<TRes, ValidateAttendancesRequest<TReq>, DefaultError, TContext> & {
    call?: ValidateAttendancesMutationCallOptions<TReq, TRes>;
};

export function useValidateAttendancesMutation<
    TReq = z.infer<typeof validateAttendancesEndpoint.req>,
    TRes = ValidateAttendancesResponse,
    TContext = unknown,
>(options?: ValidateAttendancesMutationOptions<TReq, TRes, TContext>) {
    const { call: callOptions, ...mutationOptions } = options ?? {};
    const execute = (
        input: ValidateAttendancesRequest<TReq>,
        expectedScope: ScopeContextBinding,
    ) => {
        const call = createCall<undefined, undefined, TReq, TRes>(
            {
                ...validateAttendancesEndpoint,
                req: (callOptions?.schemas?.req?.(validateAttendancesEndpoint.req) ??
                    validateAttendancesEndpoint.req) as z.ZodType<TReq>,
                res: (callOptions?.schemas?.res?.(validateAttendancesEndpoint.res) ??
                    validateAttendancesEndpoint.res) as z.ZodType<TRes>,
            },
            undefined,
            {
                expectedScope,
                getRequestErrorExtra: callOptions?.getRequestErrorExtra,
            },
        );
        return call({ ...input, idempotencyKey: input.idempotencyKey } as unknown as Parameters<
            typeof call
        >[0]) as Promise<TRes>;
    };

    return useIdempotentMutation<TRes, ValidateAttendancesRequest<TReq>, DefaultError, TContext>(
        (input, idempotencyKey, expectedScope) =>
            execute(
                { ...input, idempotencyKey } as ValidateAttendancesRequest<TReq>,
                expectedScope,
            ),
        {
            mutationKey: ["POST", "/v1/attendances/validate"],
            ...mutationOptions,
        },
        validateAttendancesEndpoint,
    );
}
